import type {
  EngineAdapter,
  Layer,
  LayerKind,
  ViewerCamera,
  ViewerHostContext,
} from '@geosoft/viewer-sdk';
import * as THREE from 'three';
import {
  declareCapabilities,
  UnsupportedFeatureError,
  type EngineCapabilities,
  type LayerRenderDiagnostics,
  type RenderPathKind,
  type LayerAlignmentStatus,
} from '@/engines/contract';
import { isNullIslandLonLat } from '@/app/cameraPlaceholder';
import { TERRAIN_ELEVATION_MESH } from '@/lib/featureFlags';
import { selectTerrainElevationFloor } from './terrainElevationFloor';
import { computeOrbitClamp } from './controlsTuning';
import { computeTerrainObliquePose, TERRAIN_OBLIQUE_PITCH_DEG } from './terrainObliqueCamera';
import type { OutlinePassManager } from './outlinePass';
import { createOutlinePassManager, createPerformanceMonitor } from './outlinePass';
import {
  buildTerrainColorDrapeDescriptor,
  createDefaultOsmBasemapLayer,
  createGiro3DEntity,
  createGiro3DMap,
  describeGiro3DLayer,
  loadGiro3DModules,
  type Giro3DInstance,
  type Giro3DLayerEntityDescriptor,
  type Giro3DMap,
  type Giro3DModules,
} from './giro3dRuntime';
import { DEFAULT_BASEMAP_LAYER_ID } from '@/app/defaultBasemap';
import {
  applyCameraPresetTarget,
  computeCameraPresetTarget,
  type CameraPreset,
  type Giro3DCameraTarget,
  type ModelBounds,
} from './cameraPresets';
import {
  computeClipPlane,
  computeClippingBoxPlanes,
  type ClippingBoxSpec,
  type ClipPlaneVector,
  type SectionPlaneSpec,
} from './clipController';
import { readFeatureIdFromObject } from './componentPicker';
import {
  applyHillshade,
  removeHillshade,
  setContourLines,
  setVerticalExaggeration,
} from './terrainLighting';
import { applyColorMapMode, ColorMapMode, rampToTitilerColormap, type ColorRampPreset } from './terrainColorMap';
import { align, type Alignment, type AnchorLocation, type ScaleMode, type SourceBounds } from '@/crs/crsAligner';
import { resolveLayerStyle, getUniformColor } from './layerStyle';
import { patchPlateauCoarseMaterial, patchPlateauTileScene, applyPlateauForensicAuditDefaults } from './plateauTileMaterials';
import { maybeRegisterPlateauLodFade } from './plateauTilesFade';
import { applyPlateauRendererToneMapping } from './plateauRendererPolicy';
import { createEnvironmentController, type EnvironmentController, isEnvironmentLayer } from '@/modules/weather';
import {
  NativeGiroPmtilesVectorLayer,
  type NativePmtilesVectorMetrics,
  type NativeVectorStylePatch,
} from './nativePmtilesVectorLayer';

const GIRO3D_SUPPORTED: LayerKind[] = ['raster', 'vector', 'terrain', '3dtiles', 'bim', 'pointcloud', 'environment'];
const GIRO3D_ARTIFACT_TOO_LARGE_CODE = 'CV_E_ARTIFACT_TOO_LARGE';
const GIRO3D_MAX_GLB_BYTES = 500 * 1024 * 1024;
const GIRO3D_MAX_INITIAL_TILE_COUNT = 64;
/** Skip face-fill highlight when isolation returns an entire tile (mis-pick). */
const MAX_SELECTION_HIGHLIGHT_FACES = 12_000;
const GIRO3D_MAX_INITIAL_TILE_BYTES = 1_200 * 1024 * 1024;
const GIRO3D_TILE_LOAD_CONCURRENCY = 2;

const GIRO3D_CAPABILITIES: EngineCapabilities = declareCapabilities({
  engineId: 'giro3d',
  supportedLayerKinds: GIRO3D_SUPPORTED,
  supportsTime: false,
  supportsTerrain: true,
  supports3DTiles: '1.1',
  supportsInterleaving: [],
  defenceVariantSafe: true,
  pluginHostKinds: ['measurement', 'annotation', 'export', 'profile', 'viewshed'],
});

interface ConverterTileManifest {
  tile_count: number;
  total_mb: number;
  total_estimated_triangles: number;
  total_objects: number;
  oversized_tiles: number;
  tiles: Array<{
    filename: string;
    uri: string;
    mb: number;
    estimated_triangles: number;
    object_count: number;
    lods?: Array<{
      lod: number;
      uri: string;
      mb: number;
      byte_size: number;
    }>;
  }>;
}

/** Minimal 3d-tiles-renderer stats surface for Q8 progress reporting. */
interface TilesProgressSource {
  readonly stats?: {
    readonly loaded?: number;
    readonly queued?: number;
    readonly downloading?: number;
    readonly parsing?: number;
    readonly inCacheSinceLoad?: number;
  };
  readonly lruCache?: {
    readonly cachedBytes?: number;
    readonly maxBytesSize?: number;
  };
  readonly isLoading?: boolean;
  addEventListener(type: string, listener: (...args: any[]) => void): void;
}

interface GiroLayerRecord {
  readonly layer: Layer;
  readonly addedAt: string;
  readonly descriptor: Giro3DLayerEntityDescriptor;
  readonly entity?: unknown;
  /**
   * Wave-4 hardening fields. Populated for `copc-pointcloud` records so
   * the adapter can group, fit, and surface health without re-reading the
   * scene-package extensions on every UI poll.
   */
  readonly sourceGroupId?: string | null;
  readonly coordinateMode?: CopcCoordinateMode;
  readonly recommendedPointBudget?: number | null;
  readonly health?: SceneItemHealth;
  readonly hint?: string;
  readonly nativeVector?: NativeGiroPmtilesVectorLayer;
  // RFC-112 CRS alignment result for UI display
  readonly alignment?: Alignment;
}

/** Per-SceneItem health surfaced to the engine adapter consumer. */
export type SceneItemHealth = 'ready' | 'unsupported' | 'failed' | 'pending';

/** Q8 — tile-load progress snapshot for the toolbar chip. */
export type TileLoadPhase = 'initial' | 'streaming' | 'done';

export interface TileLoadProgress {
  readonly loaded: number;
  readonly total: number;
  readonly phase: TileLoadPhase;
}

export type CopcCoordinateMode = 'georeferenced' | 'local-engineering' | 'unknown-local';

export type PointCloudCoordinateModeSummary =
  | 'none'
  | 'georeferenced'
  | 'local-engineering'
  | 'mixed';

/**
 * Typed error surfaced when the lazy COPC import path is unavailable
 * (build does not bundle `COPCSource`) or the `COPCSource` constructor
 * throws. The adapter never rethrows this — it records the layer as
 * `unsupported` and emits a `viewer:error` event so the chrome can
 * render the existing Banner/Chip primitives without crashing.
 */
export class CopcUnsupportedError extends Error {
  readonly code = 'CV_E_COPC_UNSUPPORTED';
  readonly health: SceneItemHealth = 'unsupported';
  readonly layerId: string;
  readonly hint: string;
  constructor(layerId: string, hint: string, options?: { cause?: unknown }) {
    super(`COPC unsupported for layer "${layerId}": ${hint}`);
    this.name = 'CopcUnsupportedError';
    this.layerId = layerId;
    this.hint = hint;
    if (options && 'cause' in options) {
      (this as { cause?: unknown }).cause = options.cause;
    }
  }
}

export interface CopcSceneItemSnapshot {
  readonly layerId: string;
  readonly sourceGroupId: string | null;
  readonly coordinateMode: CopcCoordinateMode;
  readonly recommendedPointBudget: number | null;
  readonly health: SceneItemHealth;
  readonly hint?: string;
}

export interface CopcSourceGroup {
  /** `null` when the SceneItem did not declare a `sourceGroupId`. */
  readonly sourceGroupId: string | null;
  readonly layerIds: readonly string[];
  readonly coordinateMode: CopcCoordinateMode | 'mixed';
  readonly health: SceneItemHealth;
}

interface TilesetTileNode {
  readonly content?: { readonly uri?: string };
  readonly children?: TilesetTileNode[];
  readonly refine?: string;
}

export type RenderMode = 'default' | 'greyscale' | 'xray' | 'wireframe';
export type PointCloudColorMode = 'auto' | 'rgb' | 'elevation' | 'intensity' | 'classification';
export type PointCloudIntensityBackground = 'dark' | 'light';
/**
 * Point splat shape — matches potree-core `PointShape` enum.
 * `square` is the fastest and what a native Potree viewer defaults to;
 * `circle` is the most-Potree-like inspection look; `paraboloid` gives
 * a faux-spherical highlight that approximates "splats".
 */
export type PointCloudShape = 'square' | 'circle' | 'paraboloid';

export interface PointCloudQualitySettings {
  pointBudget: number | null;
  pointSize: number;
  subdivisionThreshold: number;
  occlusion: boolean;
  colorMode: PointCloudColorMode;
  shape: PointCloudShape;
  intensityGamma: number;
  intensityContrast: number;
  intensityBrightness: number;
  intensityBackground: PointCloudIntensityBackground;
}

export interface PointCloudQualitySnapshot extends PointCloudQualitySettings {
  layerCount: number;
  displayedPointCount: number | null;
  pointCount: number | null;
  supportedColorModes: PointCloudColorMode[];
}

const DEFAULT_POINT_CLOUD_QUALITY: PointCloudQualitySettings = {
  // Potree v2 octrees can stream high-density point budgets efficiently.
  // 50M gives real inspection detail for PEARL02-class datasets without
  // asking the browser to load the full cloud.
  pointBudget: 50_000_000,
  pointSize: 1,
  subdivisionThreshold: 0.5,
  occlusion: false,
  colorMode: 'auto',
  shape: 'square',
  // Slightly brighter defaults for intensity-mode inspection.
  intensityGamma: 0.9,
  intensityContrast: 0.15,
  intensityBrightness: 0.12,
  intensityBackground: 'dark',
};

/**
 * Per-entity snapshot of the Potree material/octree properties we have
 * already applied. Used to skip redundant work AND, critically, to skip
 * `updateShaderSource()` unless a property that actually affects the
 * compiled shader (pointColorType / pointSizeType / shape / useEDL)
 * has changed. Calling `updateShaderSource()` every frame triggers a
 * full WebGL program recompile and stalls the renderer.
 */
interface PotreeAppliedState {
  pointBudget?: number;
  pointSize?: number;
  minNodePixelSize?: number;
  pointColorType?: number;
  pointSizeType?: number;
  shape?: number;
  useEDL?: boolean;
  newFormat?: boolean;
}

/**
 * Giro3D adapter — unified 3D engine for BIM, point clouds, 3D Tiles,
 * and 2D overlays. For non-georeferenced BIM models, the adapter skips
 * the world basemap and renders a clean 3D scene with orbit controls.
 */
export class Giro3DAdapter implements EngineAdapter {
  id = 'giro3d' as const;
  readonly capabilities: EngineCapabilities = GIRO3D_CAPABILITIES;

  private host: ViewerHostContext | null = null;
  private layers = new Map<string, GiroLayerRecord>();
  private camera: ViewerCamera | null = null;
  private modules: Giro3DModules | null = null;
  private instance: Giro3DInstance | null = null;
  private map: Giro3DMap | null = null;
  /**
   * G20 — hypsometric ColorLayer drapes paired with terrain `ElevationLayer`s
   * (flag ON only), keyed by the owning terrain layer id. A true-3D terrain
   * with no colour drape shows only the OSM basemap streets; this companion
   * ColorLayer paints the relief with the DEM's own `terrain` colormap. Tracked
   * separately so `removeLayer` tears the drape down with its parent terrain.
   */
  private terrainColorDrapes = new Map<string, unknown>();
  /**
   * G20 — last colour-ramp preset applied to each terrain layer's hypsometric
   * drape (`layerId` → preset). Lets the ramp selector skip a rebuild when the
   * ramp is unchanged (e.g. on every opacity-slider tick) and lets a re-mount
   * restore the operator's chosen colormap. Defaults to `hypsometric`.
   */
  private terrainColorRamp = new Map<string, ColorRampPreset>();
  /**
   * G20 — last visibility applied to each map-layer (`layerId` → visible). The
   * paired hypsometric drape is a separate Map layer, so we persist visibility
   * here to mirror it onto the drape and to honour it across drape re-mounts /
   * colormap rebuilds.
   */
  private layerVisible = new Map<string, boolean>();
  private controls: any = null;
  private hasBasemap = false;
  private dblClickHandler: ((e: MouseEvent) => void) | null = null;
  private wheelZoomHandler: ((e: WheelEvent) => void) | null = null;
  private knownBounds: { center: [number, number, number]; size: [number, number, number]; diagonal: number } | null = null;
  private modelSphere: THREE.Sphere | null = null;
  /**
   * View stability bookkeeping (see `cacheModelSphere`):
   * - `firstBoundsAt` is set when bounds first refine; we tolerate strict
   *   re-syncs for ~8 s afterwards, then loosen the threshold.
   * - `controlsUserInteracted` flips on the first OrbitControls `start`
   *   event and remains true. After that we stop snapping the orbit
   *   target on small drift.
   */
  private firstBoundsAt: number | null = null;
  private controlsUserInteracted = false;
  private cameraChangeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private frameCount = 0;
  private lastPotreeUpdateAt = 0;
  private patchedMeshes = new WeakSet<THREE.Mesh>();
  /** M8 — track overlay entities to prevent Q1 material patching from applying */
  private overlayEntities = new WeakSet<object>();
  // Stop the per-second scene material sweep once nothing new has
  // arrived for two consecutive cycles. The sweep exists so direct-
  // load GLB tiles get patched as they arrive, but past that it just
  // re-traverses the live point cloud octree once a second — that is
  // what caused the 1Hz canvas blink under combined point cloud + 3D
  // model scenes.
  private scenePatchSweepEnabled = false;
  private scenePatchIdleSweeps = 0;

  private selectionHighlights: THREE.Mesh[] = [];
  private outlinePassManager: OutlinePassManager | null = null;
  private performanceMonitor = createPerformanceMonitor(60);
  private selectedObjects = new Set<THREE.Object3D>();
  private selectedFeatureIds = new Set<string>();
  private lastPickedFeatureId: string | null = null;
  private batchIndexByGmlId = new Map<string, string>();
  private gmlIdByBatchIndex = new Map<string, string>();
  private attrsByGmlId = new Map<string, Record<string, unknown>>();
  /** The glTF node name of the last pick — used by the geometry-binding pick
   *  resolver to look up the corresponding TreeNode in the object-index. */
  private lastPickedObjectName: string | null = null;
  private lastPickedFeatureSelection: import('@/components/three-d/ThreeDModelToolCard').ThreeDFeatureSelection | null = null;
  private lastPickedBatchTableProperties: Record<string, unknown> | null = null;
  private hiddenModelNodeIds = new Set<string>();
  private originalMaterials = new WeakMap<THREE.Mesh, THREE.Material | THREE.Material[]>();
  private renderMode: RenderMode = 'default';
  /** Operator LoD override for 3D Tiles layers (see setTilesetLodMode). */
  private tilesetLodMode: 'auto' | 'high' | 'low' = 'auto';
  private pointCloudQuality: PointCloudQualitySettings = { ...DEFAULT_POINT_CLOUD_QUALITY };
  private pointCloudClassificationFilter: ReadonlySet<number> = new Set<number>();
  /**
   * Tracks the last-applied Potree material/octree state per entity so
   * we never re-apply identical values inside the render loop (which
   * would otherwise force `material.updateShaderSource()` every frame).
   */
  private potreeAppliedState = new WeakMap<object, PotreeAppliedState>();
  /**
   * Set of `sourceGroupId` keys (or `layer.id` fallback when a SceneItem
   * does not declare a group) that the adapter has already auto-fit on
   * a previous `addLayer`. Prevents multi-COPC groups from teleporting
   * the camera on every member.
   */
  private fittedCopcGroups = new Set<string>();
  /**
   * 3D Tiles auto-focus guard. Only the FIRST 3D Tiles entity added to the
   * scene re-frames the camera. Subsequent additions (e.g. when 17 PLATEAU
   * Tier-2 layers stream in after Tier-1) leave the camera where it is so
   * the user does not get teleported away from a layer they can already see.
   * Reset by `removeAllLayers` / scene rebuild.
   */
  private hasFocusedFirstTileset = false;
  /** True once shared Giro3D tile LRU/queues have been sized for multi-layer PLATEAU scenes. */
  private sharedTileCacheConfigured = false;
  /** Per-layer opacity (0..1). Re-applied to newly streamed 3D Tiles meshes. */
  private layerOpacity = new Map<string, number>();
  /** Latest requested visibility, including requests that arrive while addLayer is still loading. */
  private desiredLayerVisibility = new Map<string, boolean>();
  /** In-flight addLayer calls by layer id; prevents duplicate concurrent scene objects. */
  private layerAddInFlight = new Map<string, Promise<void>>();
  /** Shared ENU anchor for all EPSG:4978 tilesets in one scene (prevents layer Z drift). */
  private projectEcefAnchor: AnchorLocation | null = null;
  /**
   * Wave-1 A7 — set of layer ids whose `addLayer` call threw or whose
   * entity construction returned nothing. Drives the "failed" red-dot
   * health state on the SceneTreePanel layer card.
   */
  private failedLayerIds = new Set<string>();
  /** Q8 — aggregated tile-load progress for operator quality signals. */
  private tileLoadProgress: TileLoadProgress = { loaded: 0, total: 0, phase: 'done' };
  private tileLoadProgressListeners = new Set<(progress: TileLoadProgress) => void>();
  private tileLoadInitialComplete = false;
  /** Per-layer tile stats summed for the load chip (avoids last-writer drops). */
  private tileProgressByLayer = new Map<
    string,
    { loaded: number; total: number; inFlight: number }
  >();
  private activeSectionPlane: SectionPlaneSpec | null = null;
  private activeClippingBox: ClippingBoxSpec | null = null;
  private keyLight: THREE.DirectionalLight | null = null;
  // RFC-112: Default scale mode for CRS alignment
  private defaultScaleMode: ScaleMode = 'visual';
  /**
   * INIT-2026-Q3-SCENE-HIERARCHY V-CRS-A — standard render frame. When the
   * converter supplies a `displayCrs` (+ proj4), the Giro3D Instance runs in
   * that region-appropriate projected CRS instead of hard-wired Web Mercator,
   * and ECEF blocks are reprojected per-block at unit scale (MSL pass-through).
   * Falls back to EPSG:3857 for legacy/raster gTwins so nothing regresses.
   */
  private renderFrameCrs: string | null = null;
  private renderFrameProj4: string | null = null;
  /** The CRS code the live Instance was actually created in. */
  private activeFrameCrs = 'EPSG:3857';
  /** True once a 3D-tiles / bim / point-cloud entity has been added; gates the
   * "recreate Instance in displayCrs before first geometry" path. */
  private hasAddedSceneGeometry = false;
  /** Re-entrancy guard around the unmount()+mount() Instance rebuild. */
  private rebuildingInstance = false;
  // V-CRS-A: render 3D Tiles in the converter-supplied displayCrs when present
  // (for example UTM/Japan Plane) instead of flattening through Web Mercator.
  // Legacy layers without displayCrs still fall back to EPSG:3857.
  private displayCrsRenderEnabled = true;
  private fillLight: THREE.DirectionalLight | null = null;
  private rimLight: THREE.DirectionalLight | null = null;
  private hemiLight: THREE.HemisphereLight | null = null;
  private plateauForensicPolicyApplied = false;
  /** True once PLATEAU sky+fog atmosphere has been applied for a 3D-tiles scene. */
  private atmosphereApplied = false;
  /** Optional converter QC manifests are not emitted for every 3D tiles output. */
  private missingConverterTileManifestUrls = new Set<string>();
  /** Per-child displayCrs correction matrices keyed by external tileset directory. */
  private perBlockAlignmentByDir = new Map<string, number[]>();
  /** In-flight per-child alignment (b3dm may stream before tileset.json returns). */
  private perBlockAlignmentPending = new Map<string, Promise<number[] | null>>();
  /** Environment controller managing weather/atmosphere effects. */
  private envController: EnvironmentController | null = null;

  constructor(private readonly loadModules: () => Promise<Giro3DModules> = loadGiro3DModules) {}

  supports(kind: LayerKind): boolean {
    return GIRO3D_SUPPORTED.includes(kind);
  }

  async mount(host: ViewerHostContext): Promise<void> {
    this.host = host;
    console.log('[Giro3DAdapter] mount: loading modules...');
    this.modules = await this.loadModules();
    console.log('[Giro3DAdapter] mount: creating Instance with log-depth buffer...');
    // React dev StrictMode can start a second mount before the first Giro3D
    // instance has fully disposed. Giro3D requires an empty target; stale
    // viewport/canvas children can otherwise leave an opaque dead canvas over
    // the live map.
    const target = host.container as HTMLDivElement;
    // V-CRS-A: when we intentionally rebuild the Instance for a new displayCrs,
    // we must clear any leftover Giro3D viewport from the previous frame so
    // the new Instance does not see "Target element has children".
    if (target.childElementCount > 0) {
      console.warn(
        `[Giro3DAdapter] mount: clearing ${target.childElementCount} stale child element(s) before creating Instance`,
      );
      target.replaceChildren();
    }
    // V-CRS-A: pick the render frame. When a displayCrs is configured we
    // register it with Giro3D/proj4 and create the Instance in that projected
    // CRS; otherwise we keep the legacy Web-Mercator frame (no regression).
    const { coordinateSystem, frameCrs } = this.resolveCoordinateSystem();
    this.activeFrameCrs = frameCrs;
    console.log(
      `[Giro3DAdapter] Instance CRS = ${frameCrs}` +
        (frameCrs === 'EPSG:3857'
          ? ' (default Web Mercator)'
          : ' (dataset displayCrs render frame)'),
    );
    this.instance = new this.modules.Instance({
      target,
      crs: coordinateSystem,
      // `backgroundColor: null` is the documented Giro3D contract for a
      // transparent canvas — internally C3DEngine sets clearAlpha=0 and
      // clearColor=0. Without this the Giro3D canvas paints opaque dark
      // (0x030508) on top of MapLibre and hides it.
      backgroundColor: null,
      // CRITICAL: logarithmic depth buffer is the only way to maintain depth
      // precision across the wide near/far ranges typical of large 3D models.
      // Without this, zoom-in/zoom-out causes parts of the model to disappear
      // due to Z-fighting and depth-test failures. Three.js requires this at
      // renderer construction time — we cannot toggle it after the fact.
      renderer: {
        antialias: true,
        alpha: true,
        logarithmicDepthBuffer: true,
        premultipliedAlpha: false,
        powerPreference: 'high-performance',
      },
    } as any);

    const inst = this.instance as any;

    // Expose THREE globally — picker/toolbar/etc. need it on first interaction.
    (globalThis as any).__THREE__ = THREE;

    // Force Giro3D's viewport stack above any sibling canvas (MapLibre)
    // sharing the same host container, so the transparent 3D layer is the
    // overlay and the 2D map is the underlay.
    try {
      const viewport = inst.viewport as HTMLElement | undefined;
      if (viewport) {
        viewport.style.position = 'absolute';
        viewport.style.inset = '0';
        viewport.style.zIndex = '10';
        viewport.style.pointerEvents = 'auto';
        viewport.style.background = 'transparent';
      }
    } catch { /* non-fatal */ }

    // Verify log-depth buffer is actually enabled, and force the WebGL
    // canvas to render with a transparent clear. We log the actual values
    // after the override so any later re-paint that resets them is easy
    // to spot in the console.
    try {
      const renderer = inst.renderer ?? inst.mainLoop?.gfxEngine?.renderer;
      const engine = inst._engine ?? inst.mainLoop?.gfxEngine;
      const logDepth = renderer?.capabilities?.logarithmicDepthBuffer ?? false;
      const ctxAttrs = renderer?.getContext?.()?.getContextAttributes?.();
      // Force the engine-tracked clear and the renderer state, then mark the
      // wrapped renderer.domElement as transparent so MapLibre below shows.
      if (engine) {
        engine.clearColor = 0;
        engine.clearAlpha = 0;
      }
      // Keep the Giro3D canvas transparent. MapLibre can sit underneath as
      // a 2D safety net while Giro3D basemap/model tiles stream in; an opaque
      // dark clear hides useful feedback and looks like a dead viewer.
      renderer?.setClearColor?.(0x000000, 0);
      if (inst.scene) inst.scene.background = null;

      // Initialize outline pass for selection visualization
      if (renderer && inst.scene && inst.view?.camera) {
        try {
          this.outlinePassManager = createOutlinePassManager({
            scene: inst.scene,
            camera: inst.view.camera,
            renderer: renderer,
            edgeColor: 0x00ccff,
            edgeThickness: 2,
            pulseFrequency: 0.5,
            minEdgeStrength: 1.0,
            maxEdgeStrength: 3.0,
          });
          console.log('[Giro3DAdapter] Outline pass initialized for selection visualization');
        } catch (err) {
          console.warn('[Giro3DAdapter] Failed to initialize outline pass:', err);
        }
      }
      const canvas = renderer?.domElement as HTMLCanvasElement | undefined;
      if (canvas) {
        canvas.style.background = 'transparent';
      }
      console.log(
        `[Giro3DAdapter] Renderer log-depth buffer: ${logDepth ? 'ENABLED' : 'DISABLED'}, ` +
        `ctx alpha: ${ctxAttrs?.alpha}, premultiplied: ${ctxAttrs?.premultipliedAlpha}, ` +
        `clearColor=${engine?.clearColor}, clearAlpha=${engine?.clearAlpha}`,
      );
      if (!logDepth) {
        console.warn(
          '[Giro3DAdapter] Log-depth buffer NOT enabled — clipping at extreme zoom levels expected.',
        );
      }
    } catch { /* non-fatal */ }

    console.log('[Giro3DAdapter] mount: Instance created, setting up OrbitControls...');

    // CRITICAL: Giro3D's View has DEFAULT_MIN_NEAR_PLANE = 2 and
    // DEFAULT_MAX_FAR_PLANE = 2,000,000,000. Its `view.update()` loop
    // CLAMPS the camera's near/far to these bounds every frame, overwriting
    // whatever we set directly on camera.near/camera.far. We must lower
    // minNearPlane so close-up zoom doesn't clip the model surface.
    try {
      const view = inst.view;
      if (view) {
        view.minNearPlane = 0.001;
        view.maxFarPlane = 1e10;
        console.log(
          `[Giro3DAdapter] View clamps configured: minNearPlane=${view.minNearPlane}, maxFarPlane=${view.maxFarPlane}`,
        );
      }
    } catch (err) {
      console.warn('[Giro3DAdapter] view clamp setup failed:', err);
    }

    // OrbitControls — tight, responsive feel for industrial model review.
    try {
      const { OrbitControls } = await import(
        'three/examples/jsm/controls/OrbitControls.js'
      );
      const cam = inst.view?.camera;
      const domEl = inst.domElement;
      if (cam && domEl) {
        this.controls = new OrbitControls(cam, domEl);
        this.controls.enableDamping = true;
        // Lower damping = snappier response, less drift after release.
        // Inspection-grade clouds need predictable stops, not "ice-skating".
        this.controls.dampingFactor = 0.12;
        this.controls.minDistance = 0.0001;
        this.controls.maxDistance = Infinity;
        // Use OrbitControls' built-in dolly-to-target wheel zoom. It already
        // handles asymptotic approach + zoomToCursor correctly. We just slow
        // it down significantly so close-range inspection doesn't fly past
        // surfaces in one click.
        this.controls.enableZoom = true;
        this.controls.zoomSpeed = 0.45;
        this.controls.zoomToCursor = true;
        this.controls.rotateSpeed = 0.85;
        this.controls.panSpeed = 0.55;
        this.controls.screenSpacePanning = true;
        this.controls.mouseButtons = {
          LEFT: THREE.MOUSE.ROTATE,
          MIDDLE: THREE.MOUSE.DOLLY,
          RIGHT: THREE.MOUSE.PAN,
        };

        try {
          inst.view?.setControls?.(this.controls);
        } catch { /* non-fatal */ }

        this.installWheelModifierAdjuster(domEl);
        // Flip `controlsUserInteracted` on the first `start` event so
        // cacheModelSphere stops snapping the orbit target on small drift
        // once the operator has touched the camera (RFC-114 stability).
        this.controls.addEventListener?.('start', () => {
          if (!this.controlsUserInteracted) {
            this.controlsUserInteracted = true;
          }
        });
        this.controls.addEventListener?.('end', () => {
          this.emitCameraChangeDebounced();
        });
        console.log('[Giro3DAdapter] OrbitControls ready (dampingFactor=0.12, zoomSpeed=0.45, zoomToCursor)');
      }
    } catch (err) {
      console.warn('[Giro3DAdapter] OrbitControls setup failed:', err);
    }

    // Render loop: update OrbitControls each frame so damping works smoothly.
    // Also patch tile materials every ~60 frames (about once per second) to
    // catch tiles that load after the bounds monitor stops.
    if (this.controls) {
      const controls = this.controls;
      let idleFrames = 0;
      const animate = () => {
        if (!this.controls) return;
        const moving = Boolean(controls.update?.());
        this.updatePotreePointClouds(performance.now(), moving);

        const cam = inst.view?.camera;
        if (moving) {
          inst.notifyChange?.(cam);
          idleFrames = 0;
        } else if (this.frameCount % 6 === 0) {
          inst.notifyChange?.(cam, { needsRedraw: false });
        }
        this.frameCount++;

        if (this.scenePatchSweepEnabled && this.frameCount % 60 === 0) {
          const scene = inst.scene;
          const patched = scene ? this.applyTileMaterialFixes(scene) : 0;
          if (patched === 0) {
            this.scenePatchIdleSweeps += 1;
            if (this.scenePatchIdleSweeps >= 2) {
              this.scenePatchSweepEnabled = false;
            }
          } else {
            this.scenePatchIdleSweeps = 0;
          }
        }

        if (moving) {
          idleFrames = 0;
          requestAnimationFrame(animate);
        } else {
          idleFrames++;
          // Keep heartbeat for first 5 min (18000 frames at 60fps) while
          // tiles may still be streaming; then drop to on-demand only.
          if (idleFrames < 18000) {
            requestAnimationFrame(animate);
          }
        }
      };
      requestAnimationFrame(animate);

      // Restart the loop when Giro3D signals a change (user interaction,
      // tile loaded, etc.) and the loop has gone idle.
      if (typeof inst.addEventListener === 'function') {
        inst.addEventListener('after-update', () => {
          if (idleFrames >= 18000) {
            idleFrames = 0;
            requestAnimationFrame(animate);
          }
        });
      }
    }

    // Keep a small before-render hook for selection outline animation only.
    // Do NOT dynamically rewrite camera near/far every frame for 3D Tiles:
    // multi-layer PLATEAU scenes report different transient model spheres
    // while tiles stream, causing far/near to oscillate and visually slice
    // buildings. Log-depth is enabled, so we use permissive stable planes.
    try {
      const camera = inst.view?.camera;
      if (camera && typeof inst.addEventListener === 'function') {
        let lastFrameTime = performance.now();
        inst.addEventListener('before-render', () => {
          // Update outline pass animation
          const currentTime = performance.now();
          const deltaTime = (currentTime - lastFrameTime) / 1000; // Convert to seconds
          lastFrameTime = currentTime;
          
          if (this.outlinePassManager) {
            this.outlinePassManager.update(deltaTime);
            this.performanceMonitor.recordFrame();
          }
        });
        console.log('[Giro3DAdapter] before-render hook registered for outline pass');
      }
    } catch (err) {
      console.warn('[Giro3DAdapter] before-render hook setup failed:', err);
    }

    // Double-click: select object, highlight it, zoom to it, set as rotation center
    try {
      const instForDbl = inst;
      const controlsForDbl = this.controls;
      const cameraForDbl = inst.view?.camera;
      this.dblClickHandler = async (event: MouseEvent) => {
        try {
          if (!cameraForDbl || !controlsForDbl) return;

          const domEl = instForDbl.domElement;
          if (!domEl) return;

          const rect = domEl.getBoundingClientRect();
          const mouse = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
          );
          const raycaster = new THREE.Raycaster();
          raycaster.setFromCamera(mouse, cameraForDbl);

          const meshes: any[] = [];
          const points: any[] = [];
          instForDbl.scene?.traverse((child: any) => {
            if (!child.visible) return;
            if (child.isMesh && child.geometry) meshes.push(child);
            // Include point clouds (potree-core PointCloudOctreeNodes are
            // THREE.Points with geometry). They were missing from picking,
            // which is why double-clicking on the platform did nothing.
            else if (child.isPoints && child.geometry) points.push(child);
          });

          // Threshold for point picking: scale with camera distance so a
          // double-click hits the nearest visible point, not the geometry-
          // exact location. Without this, point raycasting almost always
          // misses because points are infinitely thin geometric primitives.
          const camDistToTarget = cameraForDbl.position.distanceTo(controlsForDbl.target);
          const pointThreshold = Math.max(camDistToTarget * 0.01, 0.05);
          raycaster.params.Points = { threshold: pointThreshold };

          const meshHits = meshes.length ? raycaster.intersectObjects(meshes, false) : [];
          const pointHits = points.length ? raycaster.intersectObjects(points, false) : [];

          // Combine and pick the closest hit across both types.
          const allHits = [...meshHits, ...pointHits].sort((a, b) => a.distance - b.distance);
          if (!allHits.length) {
            this.clearSelection();
            return;
          }

          const hits = allHits;
          const hitPoint = hits[0].point;
          const hitObject = hits[0].object as THREE.Object3D;
          const hitFaceIndex = (hits[0] as any).faceIndex ?? null;

          // Point-cloud double-click: fly the camera to the picked point and
          // reset the orbit pivot there. After this, zoom + rotation work
          // around the detail instead of the original cloud centre.
          if ((hitObject as any).isPoints) {
            const startTarget = controlsForDbl.target.clone();
            const startPos = cameraForDbl.position.clone();
            const newTarget = hitPoint.clone();
            // Place camera along the existing view direction at a small
            // standoff so the picked feature fills a sensible portion of
            // the screen.
            const standoff = Math.max(camDistToTarget * 0.08, 1.5);
            const dirFromTarget = startPos.clone().sub(startTarget).normalize();
            const newPos = newTarget.clone().addScaledVector(dirFromTarget, standoff);

            const duration = 450;
            const startTime = performance.now();
            const animatePointFly = () => {
              const elapsed = performance.now() - startTime;
              const t = Math.min(1, elapsed / duration);
              const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
              cameraForDbl.position.lerpVectors(startPos, newPos, ease);
              controlsForDbl.target.lerpVectors(startTarget, newTarget, ease);
              cameraForDbl.updateProjectionMatrix();
              controlsForDbl.update();
              instForDbl.notifyChange?.();
              if (t < 1) requestAnimationFrame(animatePointFly);
            };
            requestAnimationFrame(animatePointFly);
            console.log(
              `[Giro3DAdapter] Fly to point: (${hitPoint.x.toFixed(2)}, ` +
              `${hitPoint.y.toFixed(2)}, ${hitPoint.z.toFixed(2)}) standoff=${standoff.toFixed(2)}`,
            );
            return;
          }

          const hitMesh = (hitObject as THREE.Mesh).isMesh
            ? (hitObject as THREE.Mesh)
            : null;

          // Resolve component faces + feature ID before calling selectObject so
          // the wireframe box wraps only the clicked building, not the whole tile.
          let resolvedFeatureId: string | null = null;
          let componentFaces: Set<number> | null = null;
          if (hitFaceIndex !== null && hitMesh?.geometry) {
            try {
              // Debug: log all geometry attribute names so we can see what
              // batch-ID attribute name the Giro3D tile loader actually uses.
              const attrNames = Object.keys(hitMesh.geometry.attributes);
              console.log(`[Giro3DAdapter] Geometry attributes: [${attrNames.join(', ')}]`);

              const { isolateComponent, readFeatureIdFromObject } = await import('./componentPicker');
              const featureId = readFeatureIdFromObject(hitMesh, hitFaceIndex);
              if (featureId) {
                this.lastPickedFeatureId = featureId;
                resolvedFeatureId = featureId;
                console.log(`[Giro3DAdapter] Picked feature: ${featureId}`);
              }
              componentFaces = isolateComponent(hitMesh.geometry, hitFaceIndex);
              if (componentFaces) {
                console.log(`[Giro3DAdapter] Component: ${componentFaces.size} faces`);
              }
            } catch (err) {
              console.warn('[Giro3DAdapter] componentPicker error:', err);
            }
          }

          if (hitMesh) {
            const addToSelection = event.shiftKey;
            this.selectObject(hitMesh, addToSelection, componentFaces);
          }
          this.lastPickedFeatureSelection = hitMesh
            ? this.buildPickedFeatureSelection(
                hitMesh,
                resolvedFeatureId ?? this.lastPickedFeatureId,
                hitFaceIndex,
              )
            : null;

          // Extract full batch table properties for property panel
          this.lastPickedBatchTableProperties = hitMesh
            ? this.extractBatchTableProperties(hitMesh, resolvedFeatureId ?? this.lastPickedFeatureId, hitFaceIndex)
            : null;

          // Capture the glTF node name for the geometry-binding pick resolver.
          this.lastPickedObjectName = hitObject?.name ?? null;
          console.log(
            `[Giro3DAdapter] Selected: ${hitObject?.name || 'unnamed'} at ` +
            `(${hitPoint.x.toFixed(1)}, ${hitPoint.y.toFixed(1)}, ${hitPoint.z.toFixed(1)})`,
          );

          // INIT-2026-Q3-SCENE-HIERARCHY Block 2, Task E — emit a minimal
          // 'select' host event on dblclick so the chrome's geometry-binding
          // pick path (TwinShell host 'select' handler) resolves the picked
          // glTF node name → TreeNode → tree selection (scroll + expand).
          //
          // GUARDED: only emit when the hit object carries a name (i.e. a named
          // hierarchy mesh). Terrain / basemap / unnamed picks emit nothing, so
          // existing behaviour for non-hierarchy geometry is unchanged. The
          // emitted `layerId` is the glTF node name (a sentinel, mirroring the
          // MapLibre adapter's '__map__'); the host handler resolves the real
          // node via `getLastPickedObjectName()`, so an unmatched layerId is a
          // harmless no-op in the fallback path.
          if (this.lastPickedObjectName) {
            try {
              this.host?.emit({
                type: 'select',
                selection: {
                  layerId: this.lastPickedObjectName,
                  featureId:
                    resolvedFeatureId ?? this.lastPickedFeatureId ?? undefined,
                },
              });
            } catch (err) {
              console.warn('[Giro3DAdapter] dblclick select emit failed:', err);
            }
          }

          // Compute zoom target: fit the selected object's bounding box
          hitMesh?.geometry?.computeBoundingSphere?.();
          const objSphere = hitMesh?.geometry?.boundingSphere;
          const targetRadius = objSphere?.radius ?? 1;

          // Transform sphere center to world space
          const worldCenter = new THREE.Vector3();
          if (objSphere) {
            worldCenter.copy(objSphere.center);
            hitMesh.localToWorld(worldCenter);
          } else {
            worldCenter.copy(hitPoint);
          }

          const dir = new THREE.Vector3().subVectors(cameraForDbl.position, worldCenter).normalize();
          const zoomDist = Math.max(targetRadius * 2.5, 0.5);
          const endPos = worldCenter.clone().add(dir.multiplyScalar(zoomDist));
          const endTarget = worldCenter.clone();

          const startTarget = controlsForDbl.target.clone();
          const startPos = cameraForDbl.position.clone();

          const duration = 500;
          const startTime = performance.now();
          const animateZoom = () => {
            const elapsed = performance.now() - startTime;
            const t = Math.min(1, elapsed / duration);
            const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

            cameraForDbl.position.lerpVectors(startPos, endPos, ease);
            controlsForDbl.target.lerpVectors(startTarget, endTarget, ease);
            cameraForDbl.updateProjectionMatrix();
            controlsForDbl.update();
            instForDbl.notifyChange?.();

            if (t < 1) requestAnimationFrame(animateZoom);
          };
          requestAnimationFrame(animateZoom);
        } catch { /* ignore errors */ }
      };
      inst.domElement?.addEventListener('dblclick', this.dblClickHandler);

      // Add keyboard handler for Escape to clear selection
      document.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.clearSelection();
        }
      });
    } catch (err) {
      console.warn('[Giro3DAdapter] dblclick handler setup failed:', err);
    }

    // Professional 3-point lighting + renderer quality config
    try {
      const scene = inst.scene;
      const renderer = inst.renderer ?? inst.mainLoop?.gfxEngine?.renderer;
      if (scene) {
        this.applyPointCloudSceneBackground(this.pointCloudQuality);

        // Key light — warm, strong, primary shadow-casting direction
        this.keyLight = new THREE.DirectionalLight(0xfff5e6, 2.2);
        this.keyLight.position.set(-60, 120, 200);
        this.keyLight.updateMatrixWorld();
        scene.add(this.keyLight);

        // Fill light — cooler, softer, from opposite side
        this.fillLight = new THREE.DirectionalLight(0xd4e5ff, 1.0);
        this.fillLight.position.set(80, -60, 100);
        this.fillLight.updateMatrixWorld();
        scene.add(this.fillLight);

        // Rim/back light — creates edge definition on silhouettes
        this.rimLight = new THREE.DirectionalLight(0xffffff, 0.6);
        this.rimLight.position.set(0, -100, -150);
        this.rimLight.updateMatrixWorld();
        scene.add(this.rimLight);

        // Hemisphere light — sky/ground ambient that prevents fully black shadows
        this.hemiLight = new THREE.HemisphereLight(0x8899bb, 0x333344, 1.0);
        this.hemiLight.position.set(0, 0, 200);
        this.hemiLight.updateMatrixWorld();
        scene.add(this.hemiLight);
      }

      // Renderer quality settings — tone mapping reconciled when layers mount
      // (PLATEAU PBR city tiles need ACES; point clouds need NoToneMapping).
      if (renderer) {
        renderer.toneMapping = THREE.NoToneMapping;
        renderer.toneMappingExposure = 1.0;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        if (renderer.shadowMap) renderer.shadowMap.enabled = false;
        console.log('[Giro3DAdapter] Renderer: sRGB output; tone mapping follows mounted layer kinds');
      }
    } catch (err) {
      console.warn('[Giro3DAdapter] Lighting/renderer setup failed:', err);
    }

    // Note: the OSM basemap is NOT auto-mounted here anymore. It is injected
    // into the catalog as a regular `kind:'raster'` Layer in
    // `TwinRoute → maybeInjectDefaultBasemap`, so it appears as a real layer
    // card in the SceneTreePanel (toggleable like any other layer). The Map
    // entity is created lazily on the first map-layer add inside `addLayer`.

    // Create environment controller after lighting setup
    const envInst = this.instance as any;
    if (envInst?.scene) {
      this.envController = createEnvironmentController(envInst.scene, envInst);
      console.log('[Giro3DAdapter] environment controller created');
    }

    console.log('[Giro3DAdapter] mount: complete (unified Giro3D, basemap via catalog)');
    this.clearSelection();
  }

  /**
   * Update PotreeConverter v2 point clouds loaded through `potree-core`.
   *
   * This is deliberately hosted inside the Giro3D adapter: Potree is used only
   * as a tiled point-cloud source/LOD manager, while Giro3D/Three owns the
   * renderer, camera, scene graph, clipping and measurement context.
   */
  /**
   * Per-frame Potree update: drives potree-core's LOD/visibility/load queue.
   *
   * IMPORTANT: this MUST be the ONLY thing called every frame. Quality
   * settings (point size, color mode, etc.) are applied separately when
   * the user actually changes them — re-applying them every frame would
   * force `material.updateShaderSource()` per-frame, causing a full WebGL
   * program rebuild every frame, which both stalls the renderer and
   * silently overrides UI changes between frames.
   */
  private updatePotreePointClouds(now = performance.now(), movingCamera = false): void {
    const inst = this.instance as any;
    const camera = inst?.view?.camera ?? inst?.camera?.camera3D;
    const renderer = inst?.renderer ?? inst?.mainLoop?.gfxEngine?.renderer;
    if (!camera || !renderer) return;

    // For very large octrees (10GB+ octree.bin) potree.updatePointClouds can
    // take 50-300ms. Running that every RAF starves OrbitControls input, so
    // throttle loader/LOD updates while the user is actively moving the camera.
    const minIntervalMs = movingCamera ? 120 : 60;
    if (now - this.lastPotreeUpdateAt < minIntervalMs) return;
    this.lastPotreeUpdateAt = now;

    for (const record of this.layers.values()) {
      if (record.descriptor.kind !== 'potree-pointcloud') continue;
      const entity = record.entity as
        | {
            __geosoftPotreeCore?: {
              updatePointClouds?: (pointClouds: unknown[], camera: unknown, renderer: unknown) => unknown;
            };
          }
        | undefined;
      const potree = entity?.__geosoftPotreeCore;
      if (!potree?.updatePointClouds) continue;
      try {
        potree.updatePointClouds([record.entity], camera, renderer);
        const newNodes = this.ensurePotreeNodeBoundingSpheres(record.entity);
        if (newNodes > 0) {
          // Nodes stream in outside Giro3D's knowledge; without an explicit
          // redraw request the main loop stays render-paused and freshly
          // loaded points only appear after the next user interaction.
          inst?.notifyChange?.();
        }
      } catch (err) {
        if (this.frameCount % 120 === 0) {
          console.warn('[Giro3DAdapter] potree-core update failed', err);
        }
      }
    }
  }

  /**
   * potree-core Points nodes ship without geometry.boundingSphere. Giro3D's
   * MainLoop derives the camera near/far planes each frame from Entity3D
   * distances plus threeObjects geometries that HAVE bounding spheres — a
   * potree octree contributes neither. In a pure point-cloud scene (the
   * VirtualEye sidecar) nothing else contributes, so `distance.max` stays 0
   * and view.far collapses to minNearPlane (near == far == 0.001): the cloud
   * is loaded and visible but the degenerate frustum renders nothing.
   * Computing a bounding sphere once per loaded node feeds Giro3D's
   * automatic plane computation the true scene depth range.
   *
   * @returns the number of nodes that received a new bounding sphere this
   * call (i.e. nodes that finished streaming since the previous frame).
   */
  private ensurePotreeNodeBoundingSpheres(entity: unknown): number {
    const root = entity as { traverse?: (cb: (obj: unknown) => void) => void };
    if (typeof root.traverse !== 'function') return 0;
    let computed = 0;
    root.traverse((obj) => {
      const points = obj as {
        isPoints?: boolean;
        geometry?: { boundingSphere?: unknown; computeBoundingSphere?: () => void };
      };
      if (!points.isPoints || !points.geometry) return;
      if (points.geometry.boundingSphere) return;
      points.geometry.computeBoundingSphere?.();
      computed += 1;
    });
    return computed;
  }

  /**
   * Apply user-facing point-cloud quality settings to a single potree-core
   * entity. This is invoked once on entity creation and whenever the user
   * changes the UI (`setPointCloudQuality`) — NOT every frame.
   *
   * `updateShaderSource()` is called only when a property that affects the
   * compiled shader (pointColorType, pointSizeType, shape) actually changes.
   */
  private applyPotreeCoreQuality(
    entity: unknown,
    potree: {
      pointBudget?: number;
      maxNumNodesLoading?: number;
    },
  ): void {
    if (!entity || typeof entity !== 'object') return;
    const quality = this.pointCloudQuality;
    const applied = this.potreeAppliedState.get(entity as object) ?? {};

    // ---- Potree master object: pointBudget + load queue depth. -----------
    if (typeof quality.pointBudget === 'number' && Number.isFinite(quality.pointBudget)) {
      if (applied.pointBudget !== quality.pointBudget) {
        potree.pointBudget = quality.pointBudget;
        applied.pointBudget = quality.pointBudget;
      }
    }
    // Allow more outstanding node requests for inspection mode. The default
    // in Potree-style loaders is conservative (~4); 16 keeps the LOD queue
    // saturated when zooming a 100M+ point cloud.
    potree.maxNumNodesLoading = Math.max(Number(potree.maxNumNodesLoading ?? 4), 16);

    // ---- PointCloudOctree: LOD knob + size type forwarder. ---------------
    const octree = entity as Record<string, unknown>;
    // Potree defaults this knob to a much larger value (~50px). Our UI
    // presets are compact (1.5 / 0.75 / 0.35), so scale into a wider range
    // so coarse/balanced/fine produce visibly different LOD behavior.
    const nextMinNodePixelSize = Math.max(1, quality.subdivisionThreshold * 16);
    if (applied.minNodePixelSize !== nextMinNodePixelSize) {
      octree.minNodePixelSize = nextMinNodePixelSize;
      // Compatibility alias used by some builds.
      octree.minimumNodePixelSize = nextMinNodePixelSize;
      applied.minNodePixelSize = nextMinNodePixelSize;
    }

    // ---- PointCloudMaterial: discrete enums + scalar properties. ---------
    const material = (entity as { material?: Record<string, unknown> }).material;
    if (!material) {
      this.potreeAppliedState.set(entity as object, applied);
      return;
    }

    // ADAPTIVE point sizing (potree-core enum: FIXED=0, ATTENUATED=1, ADAPTIVE=2)
    // matches what the native Potree viewer uses. It scales points by both
    // octree level and camera distance, which is what gives Potree its
    // dense, splat-like, color-rich appearance instead of a sparse pixel cloud.
    const nextSizeType = 2;
    // PointShape enum: SQUARE=0, CIRCLE=1, PARABOLOID=2.
    const nextShape =
      quality.shape === 'circle' ? 1 :
      quality.shape === 'paraboloid' ? 2 :
      0;

    // potree-core PointColorType:
    // RGB=0, COLOR=1, DEPTH=2, HEIGHT/ELEVATION=3, INTENSITY=4,
    // CLASSIFICATION=8, RETURN_NUMBER=9, SOURCE=10, etc.
    const effectiveColorMode = this.resolvePotreeColorMode(entity, quality.colorMode);
    let nextColorType: number;
    switch (effectiveColorMode) {
      case 'rgb':            nextColorType = 0; break;
      case 'elevation':      nextColorType = 3; break;
      case 'intensity':      nextColorType = 4; break;
      case 'classification': nextColorType = 8; break;
      case 'auto':
      default:               nextColorType = 0; break; // default to RGB
    }

    // Scalar properties — uniform-only, no shader rebuild.
    const nextPointSize = quality.pointSize > 0 ? quality.pointSize : 1;
    if (applied.pointSize !== nextPointSize) {
      material.size = nextPointSize;
      applied.pointSize = nextPointSize;
    }
    // Keep RGB shader uniforms at neutral defaults so the source colors come
    // through unmodified. Industrial / LiDAR datasets like PS1A already have
    // a wide range of subtle hues; biasing brightness/contrast/gamma here
    // makes them look uniformly grey or washed out.
    material.rgbGamma = 1;
    material.rgbContrast = 0;
    material.rgbBrightness = 0;

    // CRITICAL: potree-core's default `inputColorEncoding=SRGB` +
    // `outputColorEncoding=LINEAR` makes the vertex shader apply an sRGB→linear
    // gamma transform on RGB. Since potree-core uses RawShaderMaterial,
    // Three.js does NOT re-encode the output for an sRGB framebuffer — so
    // the linearized values get written straight to the screen, which crushes
    // mid-tones to near-black and leaves a "white skeleton on black" look.
    // Setting both encodings equal (SRGB→SRGB) skips the conversion entirely
    // so the original LAS RGB renders as-is. ColorEncoding enum: LINEAR=0, SRGB=1.
    material.inputColorEncoding = 1;
    material.outputColorEncoding = 1;
    // Adaptive sizing: the shader scales points by octree level + camera
    // distance, so we set generous bounds. minSize keeps far-away points
    // visible (so the cloud doesn't look sparse from far away), and maxSize
    // gives close-range inspection room to grow into proper splats.
    material.minSize = Math.max(1, nextPointSize);
    material.maxSize = Math.max(8, nextPointSize * 8);
    const intensityRange = this.readPotreeAttributeRange(entity, 'intensity');
    if (intensityRange) {
      material.intensityRange = intensityRange;
      material.intensityGamma = quality.intensityGamma;
      material.intensityContrast = quality.intensityContrast;
      material.intensityBrightness = quality.intensityBrightness;
    }
    const metadataElevationRange = this.readPotreePositionZRange(entity);
    if (metadataElevationRange) {
      material.heightMin = metadataElevationRange[0];
      material.heightMax = metadataElevationRange[1];
      material.elevationRange = metadataElevationRange;
    } else {
      const box = readPointCloudBoundingBox(entity);
      if (box && Number.isFinite(box.min.z) && Number.isFinite(box.max.z) && box.max.z > box.min.z) {
        material.heightMin = box.min.z;
        material.heightMax = box.max.z;
        material.elevationRange = [box.min.z, box.max.z];
      }
    }

    // For RGB/Auto, use potree-core's native Potree v2 shader path. The
    // decoder already maps metadata `rgb` to a normalized `rgba` attribute,
    // and the `new_format` shader consumes it directly. Forcing RGB through
    // a hand-built legacy `color` attribute causes inconsistent colours
    // across LOD nodes. Non-RGB modes need the selectable shader branches.
    const nextNewFormat = effectiveColorMode === 'auto' || effectiveColorMode === 'rgb';

    // Discrete enums — require shader rebuild ONLY when they change.
    let shaderDirty = false;
    if (applied.newFormat !== nextNewFormat) {
      material.newFormat = nextNewFormat;
      applied.newFormat = nextNewFormat;
      shaderDirty = true;
    }
    if (applied.pointSizeType !== nextSizeType) {
      material.pointSizeType = nextSizeType;
      applied.pointSizeType = nextSizeType;
      shaderDirty = true;
    }
    if (applied.shape !== nextShape) {
      material.shape = nextShape;
      applied.shape = nextShape;
      shaderDirty = true;
    }
    const nextUseEDL = quality.occlusion;
    if (applied.useEDL !== nextUseEDL) {
      material.useEDL = nextUseEDL;
      applied.useEDL = nextUseEDL;
      shaderDirty = true;
    }
    if (applied.pointColorType !== nextColorType) {
      material.pointColorType = nextColorType;
      applied.pointColorType = nextColorType;
      shaderDirty = true;
    }

    if (shaderDirty) {
      const updateShader = material.updateShaderSource;
      if (typeof updateShader === 'function') {
        updateShader.call(material);
      }
      material.needsUpdate = true;
    }

    this.potreeAppliedState.set(entity as object, applied);
  }

  private resolvePotreeColorMode(entity: unknown, requested: PointCloudColorMode): PointCloudColorMode {
    const hasRgb = this.hasUsablePotreeRgb(entity);
    const hasIntensity = Boolean(this.readPotreeAttributeRange(entity, 'intensity'));

    if (requested === 'auto') {
      if (hasRgb) return 'rgb';
      if (hasIntensity) return 'intensity';
      return 'elevation';
    }

    // Some converted Potree outputs declare an `rgb` attribute but all
    // metadata min/max values are zero, meaning the RGB channel is empty.
    // Rendering that as RGB produces a black cloud. For inspection, fall
    // back to intensity rather than displaying a broken black view.
    if (requested === 'rgb' && !hasRgb && hasIntensity) {
      return 'intensity';
    }

    return requested;
  }

  private hasUsablePotreeRgb(entity: unknown): boolean {
    const attr = this.readPotreeMetadataAttribute(entity, 'rgb')
      ?? this.readPotreeMetadataAttribute(entity, 'rgba');
    if (!attr) return false;
    const max = Array.isArray(attr.max) ? attr.max.map(Number) : [];
    return max.some((value) => Number.isFinite(value) && value > 0);
  }

  private readPotreeAttributeRange(entity: unknown, name: string): [number, number] | null {
    const attr = this.readPotreeMetadataAttribute(entity, name);
    if (!attr || !Array.isArray(attr.min) || !Array.isArray(attr.max)) return null;
    const min = Number(attr.min[0]);
    const max = Number(attr.max[0]);
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
    return [min, max];
  }

  private readPotreePositionZRange(entity: unknown): [number, number] | null {
    const attr = this.readPotreeMetadataAttribute(entity, 'position');
    if (!attr || !Array.isArray(attr.min) || !Array.isArray(attr.max) || attr.min.length < 3 || attr.max.length < 3) {
      return null;
    }
    const minZ = Number(attr.min[2]);
    const maxZ = Number(attr.max[2]);
    if (!Number.isFinite(minZ) || !Number.isFinite(maxZ) || maxZ <= minZ) return null;
    return [minZ, maxZ];
  }

  private readPotreeMetadataAttribute(entity: unknown, name: string): { min?: unknown[]; max?: unknown[] } | null {
    const metadata = (entity as {
      pcoGeometry?: { loader?: { metadata?: { attributes?: Array<Record<string, unknown>> } } };
    } | null)?.pcoGeometry?.loader?.metadata;
    const attrs = metadata?.attributes;
    if (!Array.isArray(attrs)) return null;
    const lower = name.toLowerCase();
    return (attrs.find((attr) => String(attr.name ?? '').toLowerCase() === lower) as
      | { min?: unknown[]; max?: unknown[] }
      | undefined) ?? null;
  }

  /**
   * Adjust OrbitControls.zoomSpeed in response to wheel modifier keys.
   *
   * We use OrbitControls' built-in dolly-to-target zoom (which handles
   * cursor-pivot and asymptotic approach correctly). This handler only
   * tweaks the speed before the event reaches OrbitControls:
   *
   *   Shift            -> fine    (1/4 speed)
   *   Ctrl / Meta      -> coarse  (3x speed)
   *
   * It registers on the capture phase so the speed is updated *before*
   * OrbitControls' wheel listener runs, then restored on the next tick.
   */
  private installWheelModifierAdjuster(domEl: HTMLElement): void {
    if (this.wheelZoomHandler) {
      domEl.removeEventListener('wheel', this.wheelZoomHandler, { capture: true } as AddEventListenerOptions);
    }
    const baseZoomSpeed = 0.45;
    this.wheelZoomHandler = (event: WheelEvent) => {
      if (!this.controls) return;
      let speed = baseZoomSpeed;
      if (event.shiftKey) speed *= 0.25;
      if (event.ctrlKey || event.metaKey) speed *= 3;
      this.controls.zoomSpeed = speed;
      // Restore base speed on the next tick so the next plain scroll uses
      // the default sensitivity.
      setTimeout(() => {
        if (this.controls) this.controls.zoomSpeed = baseZoomSpeed;
      }, 0);
    };
    domEl.addEventListener('wheel', this.wheelZoomHandler, { passive: true, capture: true });
  }

  /**
   * Toggle a layer's visibility in-place. Critical for view stability: a
   * hide/show round-trip via removeLayer+addLayer would refetch tileset.json,
   * drop the LRU tile cache, re-run CRS alignment, and reframe the camera.
   * This implementation flips `entity.visible` (and Map color-layer visibility
   * for the basemap path) without touching any of that.
   */
  async setLayerVisible(layerId: string, visible: boolean): Promise<void> {
    this.desiredLayerVisibility.set(layerId, visible);
    if (layerId.startsWith('layer:environment:')) {
      if (!this.envController) {
        console.warn(`[Giro3DAdapter] setLayerVisible(env): controller not ready for ${layerId}`);
        return;
      }
      const record = this.layers.get(layerId);
      if (!record?.layer) {
        console.warn(`[Giro3DAdapter] setLayerVisible(env): ${layerId} not registered — mount layer first`);
        return;
      }
      console.warn(`[Giro3DAdapter] setLayerVisible(env): ${layerId} → ${visible}`);
      this.ensureEnvironmentLayerRegistered(record.layer);
      this.envController.setEnvironmentLayerVisible(layerId, visible);
      return;
    }
    const record = this.layers.get(layerId);
    if (!record) return;
    const entity = record.entity as any;

    // Map-layer (ColorLayer / ElevationLayer added to the Giro3D Map):
    // setting `entity.visible` alone is not sufficient — we must also
    // invalidate the map and notify the render loop.
    if (record.descriptor.attachTo === 'map-layer') {
      // Persist so drape re-mounts / colormap rebuilds honour the eye toggle.
      this.layerVisible.set(layerId, visible);
      const applied = this.applyMapLayerEntityVisibility(entity, visible, layerId);
      // G20: a true-3D terrain's visible colour is the paired hypsometric
      // ColorLayer *drape*, a SEPARATE Map layer from the height-only
      // ElevationLayer. Toggling `entity.visible` alone left the flat colour
      // image on screen — hiding the terrain only dropped the exaggerated mesh.
      // Mirror the same visibility onto the drape so hide hides BOTH and show
      // restores BOTH (the drape's stored opacity is respected on show).
      const drape = this.terrainColorDrapes.get(layerId);
      if (drape) this.applyMapLayerEntityVisibility(drape, visible, layerId);
      const mapAny = this.map as any;
      try { mapAny?.invalidate?.(); } catch { /* ignore */ }
      (this.instance as any)?.notifyChange?.();
      console.log(`[Giro3DAdapter] setLayerVisible (map-layer) ${layerId} → ${visible}${applied ? '' : ' (no setter, skipped)'}${drape ? ' (+drape)' : ''}`);
      return;
    }

    // Instance-object entities (3D Tiles, BIM, point-cloud, …).
    let applied = false;
    const ent = entity as { visible?: boolean } | undefined;
    if (ent && 'visible' in ent) {
      ent.visible = visible;
      applied = true;
    }
    // Giro3D/3d-tiles-renderer entities often render through an internal
    // THREE.Group. Toggling only the entity wrapper leaves streamed tiles visible.
    const tilesGroup = (entity as { tiles?: { group?: THREE.Object3D } } | undefined)?.tiles?.group;
    if (tilesGroup) {
      tilesGroup.visible = visible;
      applied = true;
    }
    const group = (entity as { group?: THREE.Object3D } | undefined)?.group;
    if (group) {
      group.visible = visible;
      applied = true;
    }
    const object3d = (entity as { object3d?: THREE.Object3D; object3D?: THREE.Object3D } | undefined)?.object3d
      ?? (entity as { object3D?: THREE.Object3D } | undefined)?.object3D;
    if (object3d) {
      object3d.visible = visible;
      applied = true;
    }
    // Legacy nested shape (some map-layer wrappers expose `.layer.visible`).
    const nestedLayer = (entity as { layer?: { visible?: boolean } } | undefined)?.layer;
    if (nestedLayer && typeof nestedLayer.visible === 'boolean') {
      nestedLayer.visible = visible;
      applied = true;
    }
    (this.instance as { notifyChange?: () => void } | null)?.notifyChange?.();
    console.log(`[Giro3DAdapter] setLayerVisible (instance-object) ${layerId} → ${visible}${applied ? '' : ' (no visible root found)'}`);
  }

  /**
   * Toggle visibility of a single block within a block-tiled 3D Tiles layer.
   * Uses the blockCode→scene map built by the load-model handler (each loaded
   * model scene gets a `__blockCode` stamp when its URL contains a mesh code).
   */
  setBlockVisible(layerId: string, blockCode: string, visible: boolean): void {
    const record = this.layers.get(layerId);
    if (!record) return;
    const tiles = (record.entity as { tiles?: { group?: THREE.Object3D } })?.tiles;
    if (!tiles?.group) return;

    let matched = 0;
    tiles.group.traverse((obj: THREE.Object3D) => {
      const ud = obj.userData as Record<string, unknown> | undefined;
      if (ud?.__blockCode === blockCode) {
        obj.visible = visible;
        matched++;
        return;
      }
      // Fallback: match by the object name or any parent name containing the block code.
      if (obj.name && obj.name.includes(blockCode)) {
        obj.visible = visible;
        matched++;
      }
    });
    console.log(`[Giro3DAdapter] setBlockVisible: ${blockCode} visible=${visible} matched=${matched} meshes`);
    (this.instance as { notifyChange?: () => void } | null)?.notifyChange?.();
  }

  /**
   * Set opacity on all meshes belonging to a specific block within a layer.
   */
  setBlockOpacity(layerId: string, blockCode: string, opacity: number): void {
    const record = this.layers.get(layerId);
    if (!record) return;
    const tiles = (record.entity as { tiles?: { group?: THREE.Object3D } })?.tiles;
    if (!tiles?.group) return;

    const clamped = Math.max(0, Math.min(1, opacity));
    tiles.group.traverse((obj: THREE.Object3D) => {
      const ud = obj.userData as Record<string, unknown> | undefined;
      const isBlock = ud?.__blockCode === blockCode || (obj.name && obj.name.includes(blockCode));
      if (!isBlock) return;
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh || !mesh.material) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        (mat as THREE.Material).opacity = clamped;
        (mat as THREE.Material).transparent = clamped < 1;
        (mat as THREE.Material).needsUpdate = true;
      }
    });
    (this.instance as { notifyChange?: () => void } | null)?.notifyChange?.();
  }

  async unmount(): Promise<void> {
    if (this.dblClickHandler) {
      (this.instance as any)?.domElement?.removeEventListener('dblclick', this.dblClickHandler);
      this.dblClickHandler = null;
    }
    if (this.wheelZoomHandler) {
      (this.instance as any)?.domElement?.removeEventListener('wheel', this.wheelZoomHandler, { capture: true } as AddEventListenerOptions);
      this.wheelZoomHandler = null;
    }
    this.clearSelection();
    this.layers.clear();
    this.desiredLayerVisibility.clear();
    this.layerAddInFlight.clear();
    if (this.cameraChangeDebounceTimer != null) {
      clearTimeout(this.cameraChangeDebounceTimer);
      this.cameraChangeDebounceTimer = null;
    }
    this.controls?.dispose?.();
    this.controls = null;
    this.keyLight = null;
    this.fillLight = null;
    this.rimLight = null;
    this.hemiLight = null;
    if (this.envController) {
      this.envController.dispose();
      this.envController = null;
    }
    
    // Dispose outline pass resources
    if (this.outlinePassManager) {
      this.outlinePassManager.dispose();
      this.outlinePassManager = null;
    }
    // Giro3D's `Instance.dispose()` removes the renderer canvas but leaves its
    // auto-created `_viewport` wrapper div in the host container. That stale
    // child then confuses subsequent mounts (logs "Target element has children")
    // and can shadow sibling adapters like MapLibre. Detach it explicitly.
    const viewport = (this.instance as any)?.viewport as HTMLElement | undefined;
    this.instance?.dispose?.();
    viewport?.remove();
    this.instance = null;
    this.map = null;
    this.modules = null;
    this.host = null;
    this.hasBasemap = false;
    this.fittedCopcGroups.clear();
    this.failedLayerIds.clear();
    this.firstBoundsAt = null;
    this.controlsUserInteracted = false;
    this.hasFocusedFirstTileset = false;
    this.sharedTileCacheConfigured = false;
    this.projectEcefAnchor = null;
    this.batchIndexByGmlId.clear();
    this.gmlIdByBatchIndex.clear();
    this.attrsByGmlId.clear();
    this.perBlockAlignmentByDir.clear();
    this.perBlockAlignmentPending.clear();
  }

  async addLayer(layer: Layer): Promise<void> {
    if (!this.supports(layer.kind)) {
      throw new UnsupportedFeatureError(
        'giro3d',
        `layer-kind:${layer.kind}`,
        `giro3d adapter supports [${GIRO3D_SUPPORTED.join(', ')}]`,
      );
    }

    const existing = this.layers.get(layer.id);
    if (existing?.entity) {
      console.warn(`[Giro3DAdapter] addLayer skipped: ${layer.id} already mounted`);
      return;
    }
    const inFlight = this.layerAddInFlight.get(layer.id);
    if (inFlight) {
      console.warn(`[Giro3DAdapter] addLayer joined in-flight load: ${layer.id}`);
      await inFlight;
      return;
    }
    let releaseInFlight: () => void = () => {};
    const inFlightPromise = new Promise<void>((resolve) => {
      releaseInFlight = resolve;
    });
    this.layerAddInFlight.set(layer.id, inFlightPromise);

    try {

    // Environment layers route to the environment controller, not to Giro3D entities
    if (isEnvironmentLayer(layer)) {
      if (!this.envController) {
        console.warn(`[Giro3DAdapter] addLayer (environment): controller not ready, skipping ${layer.id}`);
        return;
      }
      const style = (layer.style ?? {}) as Record<string, unknown>;
      const kind = (style.environmentKind as string) ?? 'sky';
      const params = (style.environmentParameters ?? {}) as Record<string, unknown>;
      this.envController.applyEnvironmentLayer(layer.id, kind as any, params, { visible: false });
      this.layers.set(layer.id, {
        layer,
        addedAt: new Date().toISOString(),
        descriptor: { id: layer.id, kind: 'environment' as any, url: '', attachTo: 'instance-object' as const },
        entity: null,
      });
      console.warn(`[Giro3DAdapter] addLayer (environment): ${layer.id} kind=${kind}`);
      return;
    }

    console.info(
      `[Giro3DAdapter] addLayer: ${layer.id} kind=${layer.kind} endpoints=${Object.keys(layer.endpoints).join(',')}`,
    );

    // V-CRS-A: switch render frame before any geometry (including basemap) when
    // the first 3D Tiles layer carries a converter displayCrs.
    if (
      this.displayCrsRenderEnabled
      && !this.hasAddedSceneGeometry
      && !this.rebuildingInstance
      && this.host
      && layer.kind === '3dtiles'
      && typeof layer.displayCrs === 'string'
      && layer.displayCrs.toUpperCase() !== this.activeFrameCrs.toUpperCase()
    ) {
      console.log(
        `[Giro3DAdapter] addLayer: switching render frame to displayCrs ${layer.displayCrs} ` +
          `for ${layer.id} (was ${this.activeFrameCrs})`,
      );
      await this.rebuildInstanceForRenderFrame(layer.displayCrs, layer.displayCrsProj4);
    }

    // V-CRS-A (legacy path): non-3dtiles layers with displayCrs — kept for
    // forward compatibility though current converter only stamps displayCrs on
    // 3D Tiles scene items.
    if (
      this.displayCrsRenderEnabled
      && !this.hasAddedSceneGeometry
      && !this.rebuildingInstance
      && this.host
      && layer.kind !== '3dtiles'
      && typeof layer.displayCrs === 'string'
      && layer.displayCrs.toUpperCase() !== this.activeFrameCrs.toUpperCase()
    ) {
      await this.rebuildInstanceForRenderFrame(layer.displayCrs, layer.displayCrsProj4);
    }
    if (layer.kind === '3dtiles' || layer.kind === 'bim' || layer.kind === 'pointcloud') {
      this.hasAddedSceneGeometry = true;
    }

    // For raster/terrain layers, add the basemap if not already present.
    // OSM/Web-Mercator basemaps are meaningless in a non-3857 displayCrs frame.
    if (
      (layer.kind === 'raster' || layer.kind === 'terrain')
      && !this.hasBasemap
      && this.activeFrameCrs === 'EPSG:3857'
      && this.modules
      && this.instance
    ) {
      this.map = createGiro3DMap(this.modules);
      await this.instance.add(this.map);
      this.hasBasemap = true;
    }

    // M8 — lookup slot from store to enable overlay 3D Tiles routing
    const slot = (this.host as any)?.getState?.()?.slotMap?.[layer.id];
    const descriptor = describeGiro3DLayer(layer, slot);
    this.seedPendingLayerHealth(layer, descriptor);

    if (descriptor.kind === 'native-vector-pmtiles') {
      if (this.activeFrameCrs.toUpperCase() !== 'EPSG:3857') {
        const hint =
          `Native PMTiles vectors currently decode into EPSG:3857, but the Giro3D ` +
          `render frame is ${this.activeFrameCrs}. Use MapLibre fallback/preview or ` +
          `provide a Web-Mercator display frame until vector reprojection is implemented.`;
        this.layers.set(layer.id, {
          layer,
          descriptor,
          addedAt: new Date(0).toISOString(),
          health: 'failed',
          hint,
        });
        this.failedLayerIds.add(layer.id);
        this.finalizeLayerHealth(layer.id, 'failed');
        return;
      }
      if (!this.instance) {
        throw new Error('Giro3D instance is not mounted');
      }
      const nativeVector = new NativeGiroPmtilesVectorLayer(layer);
      await nativeVector.load();
      await this.instance.add(nativeVector.object);
      this.layers.set(layer.id, {
        layer,
        descriptor,
        entity: nativeVector.object,
        nativeVector,
        addedAt: new Date(0).toISOString(),
        health: 'ready',
      });
      this.finalizeLayerHealth(layer.id, 'ready');
      (this.instance as any)?.notifyChange?.();
      console.info('[Giro3DAdapter] native PMTiles vector mounted', {
        layerId: layer.id,
        metrics: nativeVector.getMetrics(),
      });
      return;
    }

    // For 3D Tiles: RFC-112 CRS alignment integration. Fetch tileset.json,
    // apply CRS transformation, then load GLB content directly using GLTFLoader.
    if (descriptor.kind === '3dtiles' && this.instance) {
      const { json, alignment } = await this.alignAndFocusTileset(descriptor.url, layer);
      await this.prefetchB3dmBatchIndex(descriptor.url, json);
      const container = await this.loadTilesDirectly(descriptor.url, json);
      if (container && this.instance) {
        const blockTiledDisplayCrs = this.shouldUseBlockTiledDisplayCrsCorrection(layer);
        if (alignment && !alignment.rejected) {
          this.applyAlignmentMatrix(container, alignment);
          if (blockTiledDisplayCrs) {
            this.attachBlockTiledDisplayCrsCorrection(
              container, layer, descriptor.url, alignment,
            );
          }
        }
        
        this.applyLayerWorldOffset(container, layer);
        await this.instance.add(container);
        
        // M8 — apply overlay material override or standard tile fixes
        if (descriptor.isOverlay) {
          this.applyOverlayMaterialOverride(container, layer);
        }
        
        this.focusCameraOnObject(container);
        
        // Store layer record with alignment data and emit event
        const alignmentRejected = Boolean(alignment?.rejected);
        this.layers.set(layer.id, {
          layer,
          descriptor,
          entity: container,
          addedAt: new Date(0).toISOString(),
          alignment: alignment ?? undefined,
          health: alignmentRejected ? 'failed' : 'ready',
        });

        if (alignmentRejected && alignment) {
          this.failedLayerIds.add(layer.id);
          this.finalizeLayerHealth(layer.id, 'failed');
        }
        
        // Emit CRS correction event after layer is stored
        if (alignment) {
          this.emitCrsEvent(layer, alignment);
        }
        
        this.cacheModelSphere();
        this.tuneControlsForModel();
        (this.instance as any).notifyChange?.();
        return;
      }
      // Emit CRS event even if direct loading failed, if we have alignment data
      if (alignment) {
        this.emitCrsEvent(layer, alignment);
      }
      if (!this.instance) {
        console.warn('[Giro3DAdapter] Instance disposed during tile loading, aborting');
        return;
      }
      // For native 3D Tiles tilesets (.b3dm/.i3dm/.pnts), the direct GLB
      // loader is intentionally skipped and the Giro3D Tiles3D entity
      // handles streaming. This log is informational, not a failure.
      console.info('[Giro3DAdapter] Using Giro3D Tiles3D entity (native 3D Tiles streaming).');
      if (typeof descriptor.errorTarget === 'number') {
        console.log(`[Giro3DAdapter] Tile SSE target for ${layer.id}: errorTarget=${descriptor.errorTarget}`);
      }
      // Fix C: stash pre-fetched alignment on the descriptor so the fallback
      // path below can reuse it without a second tileset.json round-trip.
      if (alignment) {
        (descriptor as any).__prefetchedAlignment = alignment;
      }
    }

    if (descriptor.kind === 'bim-gltf') {
      await this.assertGlbUrlSafe(descriptor.url, 'GLB model');
    }

    // Wave-4 hardening: COPC is the default mixed-scene point-cloud path.
    // Validate runtime availability and treat construction failures as a
    // typed unsupported state so the viewer never crashes when the
    // `COPCSource` lazy import path is missing in the active build.
    if (descriptor.kind === 'copc-pointcloud' && this.modules) {
      if (typeof (this.modules as { COPCSource?: unknown }).COPCSource !== 'function') {
        this.registerUnsupportedCopc(
          layer,
          descriptor,
          'Giro3D COPCSource runtime is not available in this build.',
        );
        return;
      }
    }

    let entity: unknown | undefined;
    const useSyntheticOsmBasemap =
      layer.id === DEFAULT_BASEMAP_LAYER_ID
      || (layer.style as { isSyntheticBasemap?: boolean } | undefined)?.isSyntheticBasemap === true;
    try {
      if (useSyntheticOsmBasemap && this.modules) {
        entity = createDefaultOsmBasemapLayer(this.modules);
        console.log('[Giro3DAdapter] Synthetic OSM basemap ColorLayer created');
      } else {
        entity = this.modules ? await createGiro3DEntity(descriptor, this.modules) : undefined;
      }
    } catch (err) {
      if (descriptor.kind === 'copc-pointcloud') {
        const hint = err instanceof Error && err.message
          ? err.message
          : 'COPCSource construction failed.';
        this.registerUnsupportedCopc(layer, descriptor, hint, err);
        return;
      }
      this.failedLayerIds.add(layer.id);
      this.finalizeLayerHealth(layer.id, 'failed');
      throw err;
    }
    if (!entity) {
      this.failedLayerIds.add(layer.id);
      this.finalizeLayerHealth(layer.id, 'failed');
    }

      if (entity && descriptor.attachTo === 'instance-object' && this.instance) {
      this.applyLayerWorldOffset(entity, layer);
      await this.instance.add(entity);
      if (descriptor.kind === '3dtiles') {
        // Giro3D swaps in the shared LRU/queues during preprocess (inside add).
        // Configure AFTER add so limits apply to the shared cache all layers use.
        this.configureSharedTileResources(entity);
      }
      
      // M8 — apply overlay material override or standard tile fixes
      if (descriptor.isOverlay) {
        this.applyOverlayMaterialOverride(entity, layer);
      }
      if (descriptor.kind === 'bim-gltf') {
        this.focusCameraOnObject(entity);
      } else if (descriptor.kind === '3dtiles') {
        // Apply CRS alignment to fallback Giro3D Tiles3D entity.
        // Fix A: use the alignment already computed by alignAndFocusTileset
        // (stored above in { json, alignment }) to avoid a duplicate fetch.
        const fallbackAlignment = (descriptor as any).__prefetchedAlignment
          ?? await this.alignTilesetEntity(entity, layer);
        const blockTiledDisplayCrs = this.shouldUseBlockTiledDisplayCrsCorrection(layer);
        const tilesetUrl = (entity as any).url || layer.endpoints.tileset3d || descriptor.url;
        if (fallbackAlignment && !fallbackAlignment.rejected) {
          this.applyAlignmentMatrix(entity, fallbackAlignment);
          if (blockTiledDisplayCrs) {
            this.attachBlockTiledDisplayCrsCorrection(
              entity, layer, tilesetUrl, fallbackAlignment,
            );
          }
          // Fix A (critical): force Giro3D to re-run tile selection with the
          // correct group matrix. Without this, SSE is computed in the wrong
          // coordinate frame and zero tiles are queued until the user moves.
          const cam = (this.instance as any)?.view?.camera;
          (this.instance as any)?.notifyChange?.(cam ?? undefined);
        } else if (fallbackAlignment?.rejected) {
          this.markLayerAlignmentRejected(layer, fallbackAlignment);
        }
        // Only reposition camera for the FIRST 3D Tiles layer. Subsequent
        // layers (Tier-2) share the same Tokyo area and must not teleport.
        if (!this.hasFocusedFirstTileset) {
          this.focusCameraOnTileset(entity);
          this.hasFocusedFirstTileset = true;
        }
        // Store alignment for this layer (will be used in layer record below)
        if (fallbackAlignment) {
          (entity as any).__giro3d_alignment = fallbackAlignment;
        }
        this.attachEcefUpAxisFix(entity, layer);
        this.attachTileLoadProgress(entity, layer.id);
        this.attachPlateauTileMaterialFixes(entity);
        // Persist any active operator LoD override onto the new tileset.
        this.applyTilesetLodModeTo(entity, descriptor);
      } else if (descriptor.kind === 'copc-pointcloud') {
        this.configurePointCloud(entity, layer);
        const groupKey = copcGroupKey(layer);
          if (!this.fittedCopcGroups.has(groupKey) && !this.hasRenderableModelLayers()) {
          this.focusCameraOnPointCloud(entity);
          this.fittedCopcGroups.add(groupKey);
        }
      } else if (descriptor.kind === 'potree-pointcloud') {
        this.configurePointCloud(entity, layer);
          if (!this.hasRenderableModelLayers()) {
            this.focusCameraOnPointCloud(entity);
          }
      } else if (descriptor.kind === 'pointcloud-tiles3d') {
        this.configurePointCloud(entity, layer);
        if (!this.hasFocusedFirstTileset && !this.hasRenderableModelLayers()) {
          this.focusCameraOnPointCloud(entity);
        }
      }
    }
    if (entity && descriptor.attachTo === 'map-layer' && this.map) {
      console.log(`[Giro3DAdapter] attaching map-layer entity for ${layer.id} (kind=${descriptor.kind})`);
      try {
        await this.map.addLayer(entity);
        console.log(`[Giro3DAdapter] map.addLayer SUCCEEDED for ${layer.id}`);
      } catch (err) {
        console.error(`[Giro3DAdapter] map.addLayer FAILED for ${layer.id}:`, err);
        throw err;
      }
      // Basemap tiles should start fetching before 3D tilesets consume the queue.
      const inst = this.instance as { notifyChange?: () => void } | null;
      inst?.notifyChange?.();
      if (useSyntheticOsmBasemap) {
        const pumpEnd = Date.now() + 1500;
        const pump = () => {
          inst?.notifyChange?.();
          if (Date.now() < pumpEnd) requestAnimationFrame(pump);
        };
        requestAnimationFrame(pump);
      }
    }
    if (descriptor.kind === 'copc-pointcloud') {
      const ext = readCopcExtensions(layer);
      this.layers.set(layer.id, {
        layer,
        descriptor,
        entity,
        addedAt: new Date(0).toISOString(),
        sourceGroupId: ext.sourceGroupId,
        coordinateMode: ext.coordinateMode,
        recommendedPointBudget: ext.recommendedPointBudget,
        health: 'ready',
      });
    } else {
      // For 3D tiles fallback entities, include alignment if available
      const alignment = descriptor.kind === '3dtiles' && entity 
        ? (entity as any).__giro3d_alignment 
        : undefined;
      this.layers.set(layer.id, {
        layer,
        descriptor,
        entity,
        addedAt: new Date(0).toISOString(),
        alignment,
        health: entity
          ? (descriptor.kind === '3dtiles' ? 'pending' : 'ready')
          : 'failed',
      });
    }

    if (entity && descriptor.kind === 'potree-pointcloud') {
      this.finalizeLayerHealth(layer.id, 'ready');
    }
    if (entity && (layer.kind === 'raster' || layer.kind === 'terrain') && descriptor.attachTo === 'map-layer') {
      this.finalizeLayerHealth(layer.id, 'ready');
    }
    if (entity && descriptor.kind === 'terrain-source' && descriptor.attachTo === 'map-layer') {
      // G20: an ElevationLayer carries height only — the visible surface shows
      // whatever ColorLayer is draped (the OSM basemap streets). Pair it with a
      // hypsometric ColorLayer so the relief is colour-coded instead of looking
      // like a flat street map.
      await this.mountTerrainColorDrape(layer, descriptor, entity);
      // Spike (gap G20): re-frame now that the DEM mesh is mounted so the camera
      // sits above the real terrain surface, at an oblique angle that reveals
      // the relief (and vertical exaggeration) instead of a top-down view.
      this.reframeForTerrainElevation();
    }

    // Only cache sphere + tune controls for point clouds or the very first
    // 3D Tiles layer. Subsequent 3D Tiles layers share the same geographic
    // area; re-tuning drifts the orbit target and causes "floating" visuals.
    if (descriptor.kind !== '3dtiles' || !this.hasFocusedFirstTileset) {
      this.cacheModelSphere();
      this.tuneControlsForModel();
    }

    if (entity) {
      if (descriptor.kind === '3dtiles' && !this.plateauForensicPolicyApplied) {
        applyPlateauForensicAuditDefaults();
        this.plateauForensicPolicyApplied = true;
      }
      if (descriptor.kind === '3dtiles' && !this.envController) {
        this.applyPlateauAtmosphere();
      }
      this.reconcileRendererToneMapping();
    }
    } finally {
      try {
        const desired = this.desiredLayerVisibility.get(layer.id);
        if (typeof desired === 'boolean') {
          await this.setLayerVisible(layer.id, desired);
        }
      } finally {
        if (this.layerAddInFlight.get(layer.id) === inFlightPromise) {
          this.layerAddInFlight.delete(layer.id);
        }
        releaseInFlight();
      }
    }
  }

  /**
   * Register a COPC SceneItem as unsupported. The record is stored so the
   * chrome can render the existing error Banner/Chip primitives, a typed
   * `error` event is emitted via the host, and the call does not throw
   * (G8 — additive; G7 — typed error, no crash).
   */
  private registerUnsupportedCopc(
    layer: Layer,
    descriptor: Giro3DLayerEntityDescriptor,
    hint: string,
    cause?: unknown,
  ): void {
    const ext = readCopcExtensions(layer);
    this.layers.set(layer.id, {
      layer,
      descriptor,
      addedAt: new Date(0).toISOString(),
      sourceGroupId: ext.sourceGroupId,
      coordinateMode: ext.coordinateMode,
      recommendedPointBudget: ext.recommendedPointBudget,
      health: 'unsupported',
      hint,
    });
    const error = new CopcUnsupportedError(layer.id, hint, cause !== undefined ? { cause } : undefined);
    this.host?.emit({ type: 'error', message: error.message, cause: error });
  }

  private configurePointCloud(entity: unknown, layer: Layer): void {
    // Reserved entity-shape doc reference (kept inline so future
    // adapters retain awareness of the four giro3d point-cloud knobs):
    // pointBudget?: number | null; pointSize?: number;
    // subdivisionThreshold?: number; showVolume?: boolean.
    //
    // Wave-4: the budget is seeded only on the FIRST healthy COPC record.
    // Members that join an existing `sourceGroupId` keep the previously
    // applied knob so the UI doesn't flicker when a multi-COPC group
    // streams in piecewise.
    const isFirstHealthyCopc = this.pointCloudRecords().length === 0;
    if (isFirstHealthyCopc) {
      const budget = readRecommendedPointBudget(layer);
      const defaultBudget = budget
        ? Math.max(budget, DEFAULT_POINT_CLOUD_QUALITY.pointBudget ?? budget)
        : DEFAULT_POINT_CLOUD_QUALITY.pointBudget;
      this.pointCloudQuality = { ...DEFAULT_POINT_CLOUD_QUALITY, pointBudget: defaultBudget };
    }
    this.applyPointCloudQuality(entity, this.pointCloudQuality);
  }

  /**
   * Optional dummy/georeference bridge for local engineering assets.
   *
   * The PS1A model + point cloud are in a shared local frame. To test them
   * against georeferenced Dubai imagery/terrain without reconverting source
   * coordinates, the gTwin scene can set:
   *
   *   style.localToWorldOffset = [webMercatorX, webMercatorY, zOffset]
   *
   * The same offset is applied to 3D model Object3D containers and
   * potree-core point-cloud Object3Ds, preserving their relative alignment
   * while placing both near the chosen Dubai/Jumeirah anchor.
   */
  private applyLayerWorldOffset(entity: unknown, layer: Layer): void {
    const raw = (layer.style as { localToWorldOffset?: unknown } | undefined)?.localToWorldOffset;
    if (!Array.isArray(raw) || raw.length < 3) return;
    const [x, y, z] = raw.map((value) => Number(value));
    if (!isFiniteVec3(x, y, z)) return;
    const target = entity as {
      position?: { set?: (x: number, y: number, z: number) => void };
      updateMatrixWorld?: (force?: boolean) => void;
    };
    if (typeof target.position?.set !== 'function') return;
    target.position.set(x, y, z);
    target.updateMatrixWorld?.(true);
  }

  setPointCloudQuality(settings: Partial<PointCloudQualitySettings>): void {
    this.pointCloudQuality = { ...this.pointCloudQuality, ...settings };
    this.applyPointCloudSceneBackground(this.pointCloudQuality);
    for (const record of this.pointCloudRecords()) {
      this.applyPointCloudQuality(record.entity, this.pointCloudQuality);
    }
    (this.instance as any)?.notifyChange?.();
  }

  /**
   * WP-5: persist the active classification filter (set of ASPRS class
   * codes). An empty set disables the filter. Engines that expose a
   * classification mask API receive the update through best-effort
   * property assignment; the snapshot is always retained so the UI mirror
   * stays consistent across re-mounts.
   */
  setPointCloudClassificationFilter(classes: ReadonlySet<number>): void {
    this.pointCloudClassificationFilter = new Set(classes);
    for (const record of this.pointCloudRecords()) {
      this.applyPointCloudClassificationFilter(record.entity);
    }
    (this.instance as any)?.notifyChange?.();
  }

  /** WP-5: read the current classification filter (empty = show all). */
  getPointCloudClassificationFilter(): ReadonlySet<number> {
    return this.pointCloudClassificationFilter;
  }

  /**
   * RFC-112: Set default scale mode for CRS alignment and re-align existing 3D
   * Tiles layers. Returns a Promise that resolves once every in-flight
   * re-alignment has settled, so callers (and tests) can observe the new
   * matrix + crsCorrectionApplied event deterministically.
   */
  async setDefaultScaleMode(mode: ScaleMode): Promise<void> {
    this.defaultScaleMode = mode;
    const pending: Promise<void>[] = [];
    for (const [layerId, record] of this.layers) {
      if (record.descriptor.kind === '3dtiles' && record.alignment) {
        pending.push(this.reapplyCrsAlignment(layerId, record, mode));
      }
    }
    await Promise.all(pending);
  }

  /**
   * Return the project frame CRS the live Instance renders in. EPSG:3857 for
   * legacy/raster scenes; the converter `displayCrs` once V-CRS-A is active.
   */
  getFrameCrs(): string {
    return this.activeFrameCrs;
  }

  /** Viewer-level render-frame CRS for the global coordinate readout. */
  getActiveFrameCrs(): string | null {
    return this.activeFrameCrs ?? null;
  }

  /**
   * INIT-2026-Q3-SCENE-HIERARCHY V-CRS-A — configure the standard render frame.
   * Safe to call before mount (the Instance is created in this CRS) or before
   * the first geometry is added (the adapter rebuilds the Instance). Passing an
   * empty/undefined `displayCrs` keeps the EPSG:3857 fallback.
   */
  setRenderFrame(displayCrs?: string | null, displayCrsProj4?: string | null): void {
    this.renderFrameCrs = displayCrs && displayCrs.trim().length > 0 ? displayCrs.trim() : null;
    this.renderFrameProj4 = displayCrsProj4 && displayCrsProj4.trim().length > 0 ? displayCrsProj4.trim() : null;
  }

  /**
   * Resolve the Giro3D `CoordinateSystem` for the configured render frame.
   * Registers `displayCrs` with Giro3D/proj4 via `CoordinateSystem.register`
   * (which also registers the def with the shared proj4 instance). Any failure
   * falls back to the hard-wired EPSG:3857 system so legacy scenes never break.
   */
  private resolveCoordinateSystem(): { coordinateSystem: unknown; frameCrs: string } {
    const modules = this.modules;
    const fallback = {
      coordinateSystem: modules?.CoordinateSystem.epsg3857,
      frameCrs: 'EPSG:3857',
    };
    if (!modules) return fallback;
    const code = this.renderFrameCrs;
    if (!code || code.toUpperCase() === 'EPSG:3857') return fallback;
    const register = (modules.CoordinateSystem as unknown as {
      register?: (id: string, def: string, opts?: unknown) => unknown;
    }).register;
    if (typeof register !== 'function' || !this.renderFrameProj4) {
      console.warn(
        `[Giro3DAdapter] Cannot register displayCrs ${code} ` +
          `(register=${typeof register}, proj4=${this.renderFrameProj4 ? 'yes' : 'no'}); ` +
          'falling back to EPSG:3857.',
      );
      return fallback;
    }
    try {
      const cs = register.call(modules.CoordinateSystem, code, this.renderFrameProj4);
      return { coordinateSystem: cs, frameCrs: code };
    } catch (err) {
      console.warn(`[Giro3DAdapter] Failed to register displayCrs ${code}; falling back to EPSG:3857.`, err);
      return fallback;
    }
  }

  /**
   * V-CRS-A — recreate the Instance in `displayCrs` before any 3D geometry is
   * added. Reuses the tested unmount()/mount() teardown+setup so render loop,
   * controls, lights and handlers are rebuilt cleanly in the new frame.
   */
  private async rebuildInstanceForRenderFrame(displayCrs: string, displayCrsProj4?: string): Promise<void> {
    const host = this.host;
    if (!host || this.rebuildingInstance) return;
    this.rebuildingInstance = true;
    try {
      this.setRenderFrame(displayCrs, displayCrsProj4);
      await this.unmount();
      await this.mount(host);
    } finally {
      this.rebuildingInstance = false;
    }
  }

  /**
   * Re-center the camera on every loaded point-cloud record.
   *
   * Wave-4: when several SceneItems share a `sourceGroupId`, the camera
   * fits the group once (using the first member's entity) instead of
   * teleporting per layer. Returns the number of group fits performed
   * so callers can assert single-fit behavior in tests.
   */
  fitToPointClouds(): number {
    let fits = 0;
    for (const group of this.getCopcSourceGroups()) {
      if (group.health !== 'ready') continue;
      const first = this.layers.get(group.layerIds[0]);
      if (!first?.entity) continue;
      this.focusCameraOnPointCloud(first.entity);
      fits++;
    }
    return fits;
  }

  /**
   * Snapshot of every COPC SceneItem the adapter is tracking — including
   * `unsupported` records that have no entity attached. The chrome reads
   * this to render the existing `<Banner />` and `<CoordinateModeBadge />`
   * primitives.
   */
  getCopcSceneItems(): CopcSceneItemSnapshot[] {
    const items: CopcSceneItemSnapshot[] = [];
    for (const record of this.layers.values()) {
      if (record.descriptor.kind !== 'copc-pointcloud') continue;
      items.push({
        layerId: record.layer.id,
        sourceGroupId: record.sourceGroupId ?? null,
        coordinateMode: record.coordinateMode ?? 'georeferenced',
        recommendedPointBudget: record.recommendedPointBudget ?? null,
        health: record.health ?? 'ready',
        hint: record.hint,
      });
    }
    return items;
  }

  /**
   * COPC SceneItems folded by `sourceGroupId`. SceneItems without a
   * declared group form their own singleton group keyed on `layerId`.
   */
  getCopcSourceGroups(): CopcSourceGroup[] {
    const map = new Map<string, {
      sourceGroupId: string | null;
      layerIds: string[];
      modes: Set<CopcCoordinateMode>;
      healths: Set<SceneItemHealth>;
    }>();
    for (const record of this.layers.values()) {
      if (record.descriptor.kind !== 'copc-pointcloud') continue;
      const key = copcGroupKey(record.layer);
      let entry = map.get(key);
      if (!entry) {
        entry = {
          sourceGroupId: record.sourceGroupId ?? null,
          layerIds: [],
          modes: new Set<CopcCoordinateMode>(),
          healths: new Set<SceneItemHealth>(),
        };
        map.set(key, entry);
      }
      entry.layerIds.push(record.layer.id);
      if (record.coordinateMode) entry.modes.add(record.coordinateMode);
      entry.healths.add(record.health ?? 'ready');
    }
    const groups: CopcSourceGroup[] = [];
    for (const entry of map.values()) {
      const modes = [...entry.modes];
      const coordinateMode: CopcCoordinateMode | 'mixed' =
        modes.length === 0 ? 'georeferenced'
        : modes.length === 1 ? modes[0]
        : 'mixed';
      const health: SceneItemHealth =
        entry.healths.has('unsupported') ? 'unsupported'
        : entry.healths.has('failed') ? 'failed'
        : entry.healths.has('pending') ? 'pending'
        : 'ready';
      groups.push({
        sourceGroupId: entry.sourceGroupId,
        layerIds: entry.layerIds,
        coordinateMode,
        health,
      });
    }
    return groups;
  }

  /**
   * Consolidated coordinate mode across every healthy COPC SceneItem.
   * `'none'` when no COPC layer is loaded. The toolbar uses this to drive
   * the local-frame badge state and to gate the basemap-fit shortcut.
   */
  getPointCloudCoordinateMode(): PointCloudCoordinateModeSummary {
    const modes = new Set<CopcCoordinateMode>();
    for (const record of this.layers.values()) {
      if (record.descriptor.kind !== 'copc-pointcloud') continue;
      if ((record.health ?? 'ready') !== 'ready') continue;
      if (record.coordinateMode) modes.add(record.coordinateMode);
    }
    if (modes.size === 0) return 'none';
    if (modes.size > 1) return 'mixed';
    const only = [...modes][0];
    return only === 'local-engineering' || only === 'unknown-local'
      ? 'local-engineering'
      : 'georeferenced';
  }

  /**
   * Basemap fit is disabled when any active COPC group is in a local
   * engineering frame — the basemap is rendered in EPSG:3857 which is
   * meaningless for a local coordinate frame.
   */
  isBasemapFitDisabled(): boolean {
    const summary = this.getPointCloudCoordinateMode();
    return summary === 'local-engineering' || summary === 'mixed';
  }

  /** Health for a specific layer id (`null` if the adapter never saw it). */
  getSceneItemHealth(layerId: string): SceneItemHealth | null {
    const record = this.layers.get(layerId);
    return record?.health ?? null;
  }

  private applyPointCloudClassificationFilter(entity: unknown): void {
    const target = entity as Record<string, unknown> | null;
    if (!target) return;
    const filter = this.pointCloudClassificationFilter;
    // Best-effort: Giro3D point-cloud entities expose at least one of
    // `classificationMask`, `setClassificationFilter`, or a generic
    // `userData` slot. We try them in order and never throw — the UI
    // mirror is still updated for environments where the engine has no
    // direct filter API yet.
    try {
      const setter = (target as { setClassificationFilter?: (cs: number[]) => void })
        .setClassificationFilter;
      if (typeof setter === 'function') {
        setter.call(target, [...filter]);
        return;
      }
      if ('classificationMask' in target) {
        (target as { classificationMask: number[] }).classificationMask = [...filter];
        return;
      }
      const userData = (target as { userData?: Record<string, unknown> }).userData;
      if (userData) userData['classificationFilter'] = [...filter];
    } catch (err) {
      console.warn('[Giro3DAdapter] classification filter apply failed', err);
    }
  }

  getPointCloudQuality(): PointCloudQualitySnapshot | null {
    const records = this.pointCloudRecords();
    if (records.length === 0) return null;
    const supported = new Set<PointCloudColorMode>(['auto', 'elevation']);
    let displayedPointCount = 0;
    let pointCount = 0;
    let hasDisplayedCount = false;
    let hasPointCount = false;
    for (const record of records) {
      const entity = record.entity as any;
      for (const attr of this.safeSupportedPointAttributes(entity)) {
        const name = String(attr.name ?? '').toLowerCase();
        const interpretation = String(attr.interpretation ?? '').toLowerCase();
        if (name.includes('intensity')) supported.add('intensity');
        if (name.includes('class') || interpretation.includes('classification')) supported.add('classification');
        if (name === 'color' || name === 'rgb' || name === 'rgba' || interpretation.includes('color')) supported.add('rgb');
      if (name.includes('rgba') || name.includes('color')) supported.add('rgb');
      }
      if (typeof entity.displayedPointCount === 'number') {
        displayedPointCount += entity.displayedPointCount;
        hasDisplayedCount = true;
      }
      if (typeof entity.pointCount === 'number') {
        pointCount += entity.pointCount;
        hasPointCount = true;
      }
    }
    return {
      ...this.pointCloudQuality,
      layerCount: records.length,
      displayedPointCount: hasDisplayedCount ? displayedPointCount : null,
      pointCount: hasPointCount ? pointCount : null,
      supportedColorModes: [...supported],
    };
  }

  hasPointCloudLayers(): boolean {
    return this.pointCloudRecords().length > 0;
  }

  private pointCloudRecords(): GiroLayerRecord[] {
    // Wave-4 / Potree-v2 integration: include every point-cloud record that
    // is backed by a renderable entity. COPC and PotreeConverter v2 octrees
    // share the same quality / color / classification UI surface.
    return [...this.layers.values()].filter(
      (record) => (record.descriptor.kind === 'copc-pointcloud' || record.descriptor.kind === 'potree-pointcloud')
        && (record.health ?? 'ready') === 'ready'
        && record.entity,
    );
  }

  /**
   * Guard camera against delayed point-cloud re-centering in combined scenes.
   * When any renderable model layer is present, keep the operator's current
   * camera framing and avoid point-cloud auto-focus jumps.
   */
  private hasRenderableModelLayers(): boolean {
    return [...this.layers.values()].some(
      (record) =>
        (record.descriptor.kind === 'bim-gltf' || record.descriptor.kind === '3dtiles')
        && Boolean(record.entity),
    );
  }

  private applyPointCloudQuality(entity: unknown, quality: PointCloudQualitySettings): void {
    const pointcloud = entity as { __geosoftPotreeCore?: { pointBudget?: number } } & Record<string, unknown>;
    // Potree v2 entities own their material directly via potree-core. They
    // do NOT use Giro3D's PointCloud entity API, so route them through
    // the dedicated Potree applier and skip the COPC color-mode helper
    // (which has no effect on a potree-core PointCloudOctree).
    if (pointcloud.__geosoftPotreeCore) {
      this.applyPotreeCoreQuality(entity, pointcloud.__geosoftPotreeCore);
      this.setPointCloudOcclusion(quality.occlusion);
      return;
    }
    if ('pointBudget' in pointcloud) pointcloud.pointBudget = quality.pointBudget;
    if ('pointSize' in pointcloud) pointcloud.pointSize = quality.pointSize;
    if ('subdivisionThreshold' in pointcloud) pointcloud.subdivisionThreshold = quality.subdivisionThreshold;
    this.setPointCloudOcclusion(quality.occlusion);
    this.applyPointCloudColorMode(pointcloud, quality.colorMode);
  }

  private applyPointCloudSceneBackground(_quality: PointCloudQualitySettings): void {
    // Skip dark background override when a 3D-tiles atmosphere is active —
    // the sky color + fog take visual precedence over the dark point-cloud bg.
    if (this.atmosphereApplied) return;
    const inst = this.instance as unknown as {
      scene?: { background?: THREE.Color | null };
      notifyChange?: () => void;
    } | null;
    const scene = inst?.scene;
    if (!scene) return;
    // Unified Giro3D renderer: keep the chrome-neutral background so the
    // scene reads as "loading" rather than flashing sky-blue between
    // tile loads. The basemap is an OSM ColorLayer inside the same
    // Giro3D Map entity.
    scene.background = new THREE.Color(0x141420);
    inst?.notifyChange?.();
  }

  /**
   * Apply PLATEAU city atmosphere: muted sky-blue background + exponential
   * distance fog that fades distant buildings into the horizon.
   *
   * Called once when the first `3dtiles` layer is successfully added.
   * Skipped for point-cloud-only scenes (no 3D tiles → `atmosphereApplied`
   * stays false, `applyPointCloudSceneBackground` keeps the dark bg).
   *
   * The fog density (0.00012) is calibrated for ~500 m city-block visibility
   * without darkening foreground buildings. Sky color (#9ec8e8 = desaturated
   * sky blue) is chosen to look neutral under ACES tone mapping.
   */
  /**
   * Re-register an environment layer with the controller if the initial
   * mount raced controller startup or the registry was cleared.
   */
  private ensureEnvironmentLayerRegistered(layer: Layer): void {
    if (!this.envController || !isEnvironmentLayer(layer)) return;
    const style = (layer.style ?? {}) as Record<string, unknown>;
    const kind = (style.environmentKind as string) ?? 'sky';
    const params = (style.environmentParameters ?? {}) as Record<string, unknown>;
    this.envController.applyEnvironmentLayer(layer.id, kind as any, params, { visible: false });
  }

  private applyPlateauAtmosphere(): void {
    if (this.envController) return;
    if (this.atmosphereApplied) return;
    const inst = this.instance as any;
    const scene = inst?.scene;
    if (!scene) return;
    this.atmosphereApplied = true;
    const skyColor = new THREE.Color(0x9ec8e8);
    scene.background = skyColor;
    scene.fog = new THREE.FogExp2(skyColor, 0.00012);
    inst?.notifyChange?.();
    console.log('[Giro3DAdapter] Atmosphere: sky gradient + city fog applied');
  }

  private setPointCloudOcclusion(enabled: boolean): void {
    const options = (this.instance as any)?.renderingOptions;
    if (options && 'enablePointCloudOcclusion' in options) {
      options.enablePointCloudOcclusion = enabled;
    }
  }

  private applyPointCloudColorMode(entity: any, mode: PointCloudColorMode): void {
    const attrs = this.safeSupportedPointAttributes(entity);
    const pick = (predicate: (attr: any) => boolean) => attrs.find(predicate);
    const byName = (needle: string) => pick((attr) => String(attr.name ?? '').toLowerCase().includes(needle));
    const byInterpretation = (needle: string) => pick((attr) => String(attr.interpretation ?? '').toLowerCase().includes(needle));

    const target =
      mode === 'intensity' ? byName('intensity') :
      mode === 'classification' ? (byName('class') ?? byInterpretation('classification')) :
      mode === 'rgb' ? (byInterpretation('color') ?? byName('color') ?? byName('rgb')) :
      null;

    try {
      if (mode === 'elevation') {
        entity.setActiveAttributes?.([]);
        entity.setColoringMode?.('attribute');
      } else if (target?.name && typeof entity.setActiveAttribute === 'function') {
        entity.setActiveAttribute(target.name);
        entity.setColoringMode?.('attribute');
      } else if (mode === 'auto' && attrs[0]?.name && typeof entity.setActiveAttribute === 'function') {
        entity.setActiveAttribute(attrs[0].name);
        entity.setColoringMode?.('attribute');
      }
    } catch (err) {
      console.warn('[Giro3DAdapter] point cloud color mode unavailable:', mode, err);
    }
  }

  private safeSupportedPointAttributes(entity: any): any[] {
    if (entity?.pcoGeometry?.pointAttributes?.attributes) {
      const attrs = entity.pcoGeometry.pointAttributes.attributes;
      return Array.isArray(attrs) ? attrs : [];
    }
    try {
      const attrs = entity?.getSupportedAttributes?.();
      return Array.isArray(attrs) ? attrs : [];
    } catch {
      return [];
    }
  }

  private focusCameraOnPointCloud(entity: unknown): void {
    const box = readPointCloudBoundingBox(entity);
    if (!box || box.isEmpty()) {
      this.startPointCloudBoundsMonitor(entity);
      return;
    }
    this.knownBounds = boundsFromBox(box);
    this.positionCameraAt(
      this.knownBounds.center[0],
      this.knownBounds.center[1],
      this.knownBounds.center[2],
      this.knownBounds.diagonal,
    );
    this.cacheModelSphere();
    this.tuneControlsForModel();
  }

  private startPointCloudBoundsMonitor(entity: unknown): void {
    const startTime = performance.now();
    const maxDurationMs = 30_000;
    const pollMs = 500;
    // First poll delayed to 1500ms so Giro3D's async renderer has time to
    // initialise the view camera — avoids the NaN camera position on the
    // first positionCameraAt call.
    const initialDelayMs = 1500;

    const monitor = () => {
      if (!this.instance) return;
      const box = readPointCloudBoundingBox(entity);
      if (box && !box.isEmpty()) {
        this.knownBounds = boundsFromBox(box);
        this.positionCameraAt(
          this.knownBounds.center[0],
          this.knownBounds.center[1],
          this.knownBounds.center[2],
          this.knownBounds.diagonal,
        );
        this.cacheModelSphere();
        this.tuneControlsForModel();
        return;
      }
      if (performance.now() - startTime < maxDurationMs) {
        setTimeout(monitor, pollMs);
      }
    };
    setTimeout(monitor, initialDelayMs);
  }

  /**
   * Cache the model bounding sphere. Called after each layer add. The sphere
   * is used by `updateCameraClipping()` to compute stable near/far planes.
   *
   * IMPORTANT: For 3D Tiles, this is called twice — once from tileset.json
   * (Z-up convention, before geometry loads) and once from real geometry
   * (Y-up convention, after tiles load). The Y/Z axes can be swapped
   * between the two. We MUST re-sync controls.target to the precise
   * geometry center so that zooming moves the camera toward the model,
   * not toward an outdated coordinate.
   */
  private cacheModelSphere(): void {
    // Camera clipping (near/far) needs to encompass EVERYTHING in the
    // scene, otherwise far point-cloud nodes would be culled while the
    // model preset framing only knows about the model bounds. We use
    // the union (model + point cloud) here even though camera presets
    // use model-only bounds.
    const bounds = this.getModelBounds({ includePointClouds: true });
    if (!bounds) {
      this.modelSphere = null;
      return;
    }
    const [cx, cy, cz] = bounds.center;
    const radius = bounds.diagonal / 2;
    if (!isFiniteVec3(cx, cy, cz) || !Number.isFinite(radius) || radius <= 0) {
      this.modelSphere = null;
      return;
    }
    this.modelSphere = new THREE.Sphere(new THREE.Vector3(cx, cy, cz), radius);
    console.log(
      `[Giro3DAdapter] Model sphere cached: center=(${cx.toFixed(1)}, ${cy.toFixed(1)}, ${cz.toFixed(1)}), radius=${radius.toFixed(1)}`,
    );

    // Re-sync OrbitControls target to the precise model center. Without this,
    // rotation orbits around the OLD target (from tileset.json bounds, possibly
    // Y/Z-swapped) and parts of the model swing out of frustum.
    //
    // Stability rule: this re-sync is meant to converge during the initial
    // load while bounds keep refining. Once the user has interacted with the
    // camera (`controlsUserInteracted`) OR the model has been stable for the
    // first few seconds, we relax the threshold to ~50% of radius — small
    // drift is no longer worth snapping the operator's view.
    void (this.controlsUserInteracted
      || (this.firstBoundsAt !== null && performance.now() - this.firstBoundsAt > 8000));
    if (this.firstBoundsAt === null) {
      this.firstBoundsAt = performance.now();
    }
    // Target drift re-sync disabled. With multiple PLATEAU layers sharing
    // the same area but having different bounding-volume centers (some at
    // z=-198 for vegetation), re-syncing the orbit target to the "union
    // center" made buildings appear to float and zoom orbit to underground.
    // The initial positionCameraAt sets the correct target for the primary
    // building layer; subsequent layers must not disturb it.
  }

  /**
   * Tune OrbitControls min/max distance to scale with model size. Without
   * this, fixed limits like minDistance=0.01/maxDistance=100000 either
   * prevent zoom or allow zoom past the universe.
   */
  private tuneControlsForModel(): void {
    if (!this.modelSphere) return;
    const r = this.modelSphere.radius;
    // Pure, scene-shape-aware clamp math (see controlsTuning.ts). Object radius
    // alone is the wrong cap for zoom-OUT once terrain/basemap/raster map-layers
    // are mounted: those span far beyond a single 3D object, so a tight r*200
    // cap (e.g. a 30 m building → 6 km) traps the operator and makes
    // terrain/basemap feel "stuck". The helper widens the cap to the frame
    // extent when map-layers are present and keeps the far plane past the orbit
    // cap so zoom-out never clips the scene to black.
    const clamp = computeOrbitClamp({
      modelRadius: r,
      frameCrs: this.activeFrameCrs ?? null,
      hasMapLayers: this.hasMapLayerEntities(),
    });
    if (!clamp) return;

    if (this.controls) {
      this.controls.minDistance = clamp.minDistance;
      this.controls.maxDistance = clamp.maxDistance;
      console.log(
        `[Giro3DAdapter] Controls tuned: minDist=${clamp.minDistance.toFixed(4)}, maxDist=${clamp.maxDistance.toFixed(1)}${this.hasMapLayerEntities() ? ' (scene-extent cap)' : ''}`,
      );
    }

    const inst = this.instance as any;
    const view = inst?.view;
    if (view) {
      if (clamp.maxFarPlane > clamp.minNearPlane) {
        view.minNearPlane = clamp.minNearPlane;
        view.maxFarPlane = clamp.maxFarPlane;
      }
      console.log(
        `[Giro3DAdapter] View clamps tuned: minNear=${view.minNearPlane.toFixed(4)}, maxFar=${view.maxFarPlane.toFixed(0)}`,
      );
    }
  }

  /**
   * True when any map-layer (terrain/basemap/raster `ColorLayer` or
   * `ElevationLayer`, which `attachTo: 'map-layer'`) is mounted, or a synthetic
   * basemap was created. These span far beyond a single instance-object, so
   * their presence is what makes a tight object-scaled zoom-out cap wrong.
   */
  private hasMapLayerEntities(): boolean {
    if (this.hasBasemap) return true;
    for (const record of this.layers.values()) {
      if (record.descriptor.attachTo === 'map-layer') return true;
    }
    return false;
  }

  /**
   * With logarithmic depth buffer enabled, near/far ratio has ZERO impact
   * on depth precision. The log-depth shader computes per-fragment depth
   * in log-space, so even near=0.1 / far=10,000,000 works perfectly.
   *
   * Therefore the strategy is dead simple:
   * - Near: a tiny fixed value (scaled to model size so it's never zero).
   * - Far: generous enough to always contain the full model from any angle.
   *
   * This eliminates ALL rotation/zoom clipping — there's no formula that
   * can produce a "wrong" value because both planes are maximally permissive.
   */
  // @ts-expect-error Kept for future re-enablement of camera clipping
  private updateCameraClipping(camera: any): void {
    if (!camera) return;
    const inst = this.instance as any;
    const view = inst?.view;

    // Fallback near/far: guaranteed-finite defaults applied whenever the
    // model-sphere or camera state has not produced usable numbers yet.
    // The defaults match the mount-time view clamps (0.001 / 1e10) so the
    // renderer never sees NaN values — this prevents the per-frame
    // "Invalid near/far plane distance" warning from giro3d's view.update().
    const SAFE_NEAR = 0.1;
    const SAFE_FAR = 1_000_000;
    let nearPlane: number = SAFE_NEAR;
    let farPlane: number = SAFE_FAR;

    const ensureFiniteCameraPosition = (): boolean => {
      const pos = camera.position;
      const finite = pos
        && Number.isFinite(pos.x)
        && Number.isFinite(pos.y)
        && Number.isFinite(pos.z);
      if (finite) return true;
      const target = this.controls?.target;
      if (!target || !isFiniteVec3(target.x, target.y, target.z)) return false;
      const fallbackDistance = Math.max(
        this.modelSphere && Number.isFinite(this.modelSphere.radius) && this.modelSphere.radius > 0
          ? this.modelSphere.radius * 2
          : 1000,
        100,
      );
      const nextX = target.x + fallbackDistance;
      const nextY = target.y + fallbackDistance;
      const nextZ = target.z + fallbackDistance;
      if (typeof pos?.set === 'function') {
        pos.set(nextX, nextY, nextZ);
      } else if (pos) {
        pos.x = nextX;
        pos.y = nextY;
        pos.z = nextZ;
      }
      const repaired = camera.position;
      return Boolean(
        repaired
        && Number.isFinite(repaired.x)
        && Number.isFinite(repaired.y)
        && Number.isFinite(repaired.z),
      );
    };

    const camFinite = ensureFiniteCameraPosition();
    const camPos = camera.position;

    if (
      camFinite
      && this.modelSphere
      && Number.isFinite(this.modelSphere.radius)
      && this.modelSphere.radius > 0
    ) {
      const center = this.modelSphere.center;
      const r = this.modelSphere.radius;
      if (isFiniteVec3(center.x, center.y, center.z)) {
        const dx = camPos.x - center.x;
        const dy = camPos.y - center.y;
        const dz = camPos.z - center.z;
        const distToCenter = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (Number.isFinite(distToCenter)) {
          const candidateNear = Math.max(r * 0.00001, 0.01);
          const candidateFar = Math.max((distToCenter + r) * 3, r * 20);
          if (
            Number.isFinite(candidateNear)
            && Number.isFinite(candidateFar)
            && candidateNear > 0
            && candidateFar > candidateNear
          ) {
            nearPlane = candidateNear;
            farPlane = candidateFar;
          }
        }
      }
    } else if (camFinite && this.controls?.target) {
      const t = this.controls.target;
      if (isFiniteVec3(t.x, t.y, t.z)) {
        const dx = camPos.x - t.x;
        const dy = camPos.y - t.y;
        const dz = camPos.z - t.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (Number.isFinite(dist) && dist > 0) {
          const candidateFar = Math.max(10_000, dist * 100);
          if (Number.isFinite(candidateFar) && candidateFar > 0.01) {
            nearPlane = 0.01;
            farPlane = candidateFar;
          }
        }
      }
    }

    if (
      !Number.isFinite(nearPlane)
      || !Number.isFinite(farPlane)
      || nearPlane <= 0
      || farPlane <= nearPlane
    ) {
      nearPlane = SAFE_NEAR;
      farPlane = SAFE_FAR;
    }

    if (view) {
      view.near = nearPlane;
      view.far = farPlane;
      if (!Number.isFinite(view.minNearPlane) || view.minNearPlane <= 0) {
        view.minNearPlane = 0.001;
      }
      if (!Number.isFinite(view.maxFarPlane) || view.maxFarPlane <= 0) {
        view.maxFarPlane = 1e10;
      }
    }
    camera.near = nearPlane;
    camera.far = farPlane;
    camera.updateProjectionMatrix?.();

    if (this.frameCount % 300 === 0) {
      console.debug(
        `[clip] near=${nearPlane.toFixed(4)} far=${farPlane.toFixed(0)} camFinite=${camFinite}`,
      );
    }
  }

  /**
   * Load 3D Tiles directly via Three.js GLTFLoader, bypassing Giro3D's Tiles3D
   * entity. Fetches tileset.json (or uses provided), selects a safe initial GLB set, loads with
   * bounded concurrency, applies material fixes, and returns a container Group.
   */
  private async loadTilesDirectly(tilesetUrl: string, json?: any): Promise<THREE.Group | null> {
    try {
      // Use provided JSON or fetch it
      let tileset = json;
      if (!tileset) {
        const cacheBust = `?_t=${Date.now()}`;
        const resp = await fetch(tilesetUrl + cacheBust);
        if (!resp.ok) {
          console.error(`[Giro3DAdapter] Failed to fetch tileset: ${resp.status}`);
          return null;
        }
        tileset = await resp.json();
      }
      const baseUrl = tilesetUrl.substring(0, tilesetUrl.lastIndexOf('/') + 1);
      const cacheBust = `?_t=${Date.now()}`;

      const root = tileset.root as TilesetTileNode | undefined;
      const allContentUris = this.collectContentUris(root);
      const nativeTileUris = allContentUris.filter((uri) => !this.isDirectGltfTileUri(uri));
      if (nativeTileUris.length > 0) {
        console.info(
          `[Giro3DAdapter] Direct GLB loading skipped: tileset contains native 3D Tiles payloads ` +
          `(${nativeTileUris.slice(0, 3).join(', ')}${nativeTileUris.length > 3 ? ', ...' : ''}). ` +
          'Using Giro3D Tiles3D entity.',
        );
        return null;
      }

      const manifest = await this.fetchConverterTileManifest(baseUrl);
      const isReplace = root?.refine === 'REPLACE';
      let urisToLoad: string[];

      if (manifest?.tiles?.length) {
        urisToLoad = this.selectInitialManifestUris(manifest);
      } else if (isReplace) {
        urisToLoad = this.selectInitialReplaceUris(root);
      } else if (allContentUris.length > 0) {
        urisToLoad = allContentUris.slice(0, GIRO3D_MAX_INITIAL_TILE_COUNT);
      } else {
        console.warn('[Giro3DAdapter] No GLB URIs found in tileset');
        return null;
      }

      urisToLoad = await this.preflightTileUris(urisToLoad, baseUrl);

      console.log(
        `[Giro3DAdapter] Direct tile load: ${urisToLoad.length}/${allContentUris.length} GLBs ` +
        `(${manifest ? 'converter manifest: bounded initial LOD tiles' : isReplace ? 'REPLACE: shallow bounded initial set' : 'bounded direct set'})`,
      );
      if (manifest) {
        console.log(
          `[Giro3DAdapter] Manifest: ${manifest.tile_count} tiles, ` +
          `${manifest.total_mb.toFixed(1)} MB LOD0, ` +
          `${(manifest.total_estimated_triangles / 1_000_000).toFixed(1)}M triangles, ` +
          `${manifest.oversized_tiles} oversized tiles`,
        );
      }

      const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
      const loader = new GLTFLoader();

      // The converter mesh-glb pipeline writes meshopt-compressed glTF
      // buffers (and may also draco-encode geometry). Without these
      // decoders, GLTFLoader throws `setMeshoptDecoder must be called
      // before loading compressed files` on every tile, which causes
      // direct tile loading to fail and forces fallback to the giro3d
      // built-in Tiles3D entity (which streams a continuously refining
      // bounds — the "appears then disappears" symptom).
      try {
        const { MeshoptDecoder } = await import(
          'three/examples/jsm/libs/meshopt_decoder.module.js'
        );
        loader.setMeshoptDecoder(MeshoptDecoder);
      } catch (err) {
        console.warn('[Giro3DAdapter] MeshoptDecoder unavailable; compressed GLBs may fail to load', err);
      }
      try {
        const { DRACOLoader } = await import('three/examples/jsm/loaders/DRACOLoader.js');
        const dracoLoader = new DRACOLoader();
        // Use unpkg-hosted decoder so this works in dev without copying
        // wasm/js into the public folder. Production deployments can
        // self-host these by replacing this path.
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
        loader.setDRACOLoader(dracoLoader);
      } catch (err) {
        console.warn('[Giro3DAdapter] DRACOLoader unavailable; draco-compressed GLBs may fail to load', err);
      }

      const container = new THREE.Group();
      container.name = 'direct-tiles-container';

      let totalTriangles = 0;
      let totalVertices = 0;

      let meshIndex = 0;
      let tilesLoaded = 0;
      const totalTiles = urisToLoad.length;
      this.emitDirectTileLoadProgress(0, totalTiles);

      const loadOneTile = async (uri: string) => {
        try {
          const glbUrl = uri.startsWith('http') ? uri : baseUrl + uri;

          const gltf = await loader.loadAsync(glbUrl + cacheBust);
          gltf.scene.traverse((child: any) => {
            if (child.isMesh && child.geometry) {
              const geom = child.geometry;
              const posAttr = geom.attributes?.position;
              const vCount = posAttr ? posAttr.count : 0;
              if (posAttr) totalVertices += vCount;
              const tris = geom.index ? geom.index.count / 3 : (posAttr ? posAttr.count / 3 : 0);
              totalTriangles += tris;

              const genericNames = ['mesh_0', 'mesh', 'Mesh', ''];
              const hasGoodName = child.name && !genericNames.includes(child.name);
              const baseName = hasGoodName ? child.name : `part_${meshIndex}`;
              child.name = `${baseName} [${(vCount / 1000).toFixed(1)}K vtx, ${(tris / 1000).toFixed(1)}K tri]`;
              meshIndex++;
            }
          });
          container.add(gltf.scene);
          tilesLoaded++;
          this.emitDirectTileLoadProgress(tilesLoaded, totalTiles);
          console.log(`[Giro3DAdapter] Tile ${tilesLoaded}/${urisToLoad.length}: ${uri}`);
        } catch (err) {
          console.error(`[Giro3DAdapter] Failed to load GLB: ${uri}`, err);
        }
      };

      // Load tiles with conservative concurrency to reduce peak browser memory.
      for (let i = 0; i < urisToLoad.length; i += GIRO3D_TILE_LOAD_CONCURRENCY) {
        const batch = urisToLoad.slice(i, i + GIRO3D_TILE_LOAD_CONCURRENCY);
        await Promise.all(batch.map(loadOneTile));
      }

      if (container.children.length === 0) {
        console.error('[Giro3DAdapter] No GLBs loaded successfully');
        return null;
      }

      // GLB/glTF uses Y-up convention; our camera uses Z-up. Rotate +90°
      // around X to convert Y-up → Z-up (top of model faces +Z).
      container.rotation.x = Math.PI / 2;
      container.updateMatrixWorld(true);

      console.log(
        `[Giro3DAdapter] Direct load complete: ${container.children.length} scene(s), ` +
        `${meshIndex} meshes, ` +
        `${(totalTriangles / 1_000_000).toFixed(1)}M triangles, ` +
        `${(totalVertices / 1_000_000).toFixed(1)}M vertices (Y→Z axis corrected)`,
      );

      this.emitDirectTileLoadComplete(container.children.length);

      return container;
    } catch (err) {
      if (err instanceof Error && err.message.includes(GIRO3D_ARTIFACT_TOO_LARGE_CODE)) {
        throw err;
      }
      console.error('[Giro3DAdapter] Direct tile loading error:', err);
      return null;
    }
  }

  private async fetchConverterTileManifest(baseUrl: string): Promise<ConverterTileManifest | null> {
    // Older/simple converter mesh outputs (for example `*_tiles/`) do not emit
    // the optional `_qc/tile_manifest.json`. Avoid repeated 404s in DevTools;
    // those scenes still render from `tileset.json` via the bounded fallback.
    if (!/_(?:3dtiles|city|tier)\//i.test(baseUrl) && !/3dcity|citygml/i.test(baseUrl)) {
      return null;
    }
    if (this.missingConverterTileManifestUrls.has(baseUrl)) {
      return null;
    }
    try {
      const url = `${baseUrl}_qc/tile_manifest.json?_t=${Date.now()}`;
      const resp = await fetch(url);
      if (!resp.ok) {
        if (resp.status === 404) this.missingConverterTileManifestUrls.add(baseUrl);
        return null;
      }
      const manifest = (await resp.json()) as ConverterTileManifest;
      if (!Array.isArray(manifest.tiles) || manifest.tiles.length === 0) return null;
      return manifest;
    } catch (err) {
      console.warn('[Giro3DAdapter] Converter tile manifest unavailable:', err);
      return null;
    }
  }

  private selectInitialManifestUris(manifest: ConverterTileManifest): string[] {
    // Start with LOD2 for immediate visibility and safe browser memory.
    // LOD0 remains available in the output and converter preview; GeoViewer
    // dynamic LOD swapping will refine this further.
    const preferredLod = 2;
    const uris = manifest.tiles.map((tile) => {
      const lod =
        tile.lods?.find((entry) => entry.lod === preferredLod) ??
        tile.lods?.find((entry) => entry.lod === 3) ??
        tile.lods?.find((entry) => entry.lod === 1) ??
        tile.lods?.find((entry) => entry.lod === 0);
      return lod?.uri ?? tile.uri;
    });
    if (uris.length > GIRO3D_MAX_INITIAL_TILE_COUNT) {
      console.warn(
        `[Giro3DAdapter] Initial tile manifest capped at ${GIRO3D_MAX_INITIAL_TILE_COUNT}/${uris.length} GLBs`,
      );
    }
    return uris.slice(0, GIRO3D_MAX_INITIAL_TILE_COUNT);
  }

  private selectInitialReplaceUris(root: TilesetTileNode | undefined): string[] {
    if (!root) return [];
    const children = Array.isArray(root.children) ? root.children : [];
    const shallowUris = children
      .map((child) => this.findFirstRenderableContent(child))
      .filter((uri): uri is string => Boolean(uri));

    if (shallowUris.length > 0) {
      if (shallowUris.length > GIRO3D_MAX_INITIAL_TILE_COUNT) {
        console.warn(
          `[Giro3DAdapter] REPLACE shallow set capped at ${GIRO3D_MAX_INITIAL_TILE_COUNT}/${shallowUris.length} GLBs`,
        );
      }
      return shallowUris.slice(0, GIRO3D_MAX_INITIAL_TILE_COUNT);
    }

    const rootUri = root.content?.uri;
    if (rootUri) return [rootUri];
    return this.collectContentUris(root).slice(0, GIRO3D_MAX_INITIAL_TILE_COUNT);
  }

  private findFirstRenderableContent(node: TilesetTileNode): string | null {
    if (node.content?.uri) return node.content.uri;
    if (!Array.isArray(node.children)) return null;
    for (const child of node.children) {
      const uri = this.findFirstRenderableContent(child);
      if (uri) return uri;
    }
    return null;
  }

  private collectContentUris(root: TilesetTileNode | undefined): string[] {
    if (!root) return [];
    const uris: string[] = [];
    const visit = (node: TilesetTileNode) => {
      if (node.content?.uri) uris.push(node.content.uri);
      if (Array.isArray(node.children)) {
        for (const child of node.children) visit(child);
      }
    };
    visit(root);
    return uris;
  }

  private resolveTilesetContentUrl(tilesetUrl: string, contentUri: string): string {
    try {
      return new URL(contentUri, tilesetUrl).toString();
    } catch {
      const base = tilesetUrl.slice(0, tilesetUrl.lastIndexOf('/') + 1);
      return contentUri.startsWith('http') ? contentUri : `${base}${contentUri}`;
    }
  }

  private async prefetchB3dmBatchIndex(
    tilesetUrl: string,
    tileset: { readonly root?: TilesetTileNode } | null,
  ): Promise<void> {
    // Tileset fetch/parse can fail (alignAndFocusTileset returns json: null) —
    // don't null-deref; the failure is surfaced via markLayerAlignmentRejected.
    if (!tileset?.root) return;
    const root = tileset.root;
    const uris = this.collectContentUris(root).filter((uri) =>
      uri.split(/[?#]/, 1)[0]?.toLowerCase().endsWith('.b3dm'),
    );
    if (uris.length === 0) return;

    let mapped = 0;
    let attrColumns: string[] = [];
    for (const uri of uris.slice(0, 8)) {
      try {
        const url = this.resolveTilesetContentUrl(tilesetUrl, uri);
        const resp = await fetch(url);
        if (!resp.ok) continue;
        const json = this.readB3dmBatchTableJson(await resp.arrayBuffer());
        const gmlIds = json?.gml_id;
        if (!Array.isArray(gmlIds)) continue;

        // Capture every columnar batch-table property (class, usage,
        // measuredHeight, storeys, gen_* …) so the property panel can show
        // authoritative building attributes that exactly match the geometry —
        // independent of any stale object-index metadata.
        const columns = Object.entries(json ?? {}).filter(
          ([key, value]) => key !== 'gml_id' && Array.isArray(value),
        ) as Array<[string, unknown[]]>;
        if (attrColumns.length === 0) {
          attrColumns = columns.map(([key]) => key);
        }

        gmlIds.forEach((value, index) => {
          if (value == null) return;
          const gid = String(value);
          this.batchIndexByGmlId.set(gid, String(index));
          this.gmlIdByBatchIndex.set(String(index), gid);
          const attrs: Record<string, unknown> = {};
          for (const [key, col] of columns) {
            const v = col[index];
            if (v !== null && v !== undefined && v !== '') {
              attrs[key] = v;
            }
          }
          if (Object.keys(attrs).length > 0) {
            this.attrsByGmlId.set(gid, attrs);
          }
          mapped++;
        });
      } catch (err) {
        console.warn('[Giro3DAdapter] b3dm batch table prefetch failed:', err);
      }
    }
    if (mapped > 0) {
      console.log(
        `[Giro3DAdapter] Prefetched ${mapped} gml_id → batch id mappings; ` +
        `attribute columns: [${attrColumns.join(', ')}]`,
      );
    }
  }

  private readB3dmBatchTableJson(buffer: ArrayBuffer): Record<string, unknown> | null {
    if (buffer.byteLength < 28) return null;
    const view = new DataView(buffer);
    const magic = String.fromCharCode(
      view.getUint8(0),
      view.getUint8(1),
      view.getUint8(2),
      view.getUint8(3),
    );
    if (magic !== 'b3dm') return null;
    const featureTableJsonByteLength = view.getUint32(12, true);
    const featureTableBinaryByteLength = view.getUint32(16, true);
    const batchTableJsonByteLength = view.getUint32(20, true);
    if (batchTableJsonByteLength <= 0) return null;

    const batchJsonStart =
      28 + featureTableJsonByteLength + featureTableBinaryByteLength;
    const batchJsonEnd = batchJsonStart + batchTableJsonByteLength;
    if (batchJsonEnd > buffer.byteLength) return null;

    const text = new TextDecoder()
      .decode(new Uint8Array(buffer, batchJsonStart, batchTableJsonByteLength))
      .trim();
    if (!text) return null;
    return JSON.parse(text) as Record<string, unknown>;
  }

  private isDirectGltfTileUri(uri: string): boolean {
    const pathname = uri.split(/[?#]/, 1)[0]?.toLowerCase() ?? '';
    return pathname.endsWith('.glb') || pathname.endsWith('.gltf');
  }

  private async preflightTileUris(uris: string[], baseUrl: string): Promise<string[]> {
    const accepted: string[] = [];
    let knownBytes = 0;

    for (const uri of uris) {
      if (accepted.length >= GIRO3D_MAX_INITIAL_TILE_COUNT) break;
      const url = uri.startsWith('http') ? uri : baseUrl + uri;
      const bytes = await this.readContentLength(url);

      if (bytes && bytes > GIRO3D_MAX_GLB_BYTES) {
        throw new Error(
          `${GIRO3D_ARTIFACT_TOO_LARGE_CODE}: ${uri} is ${(bytes / 1024 / 1024).toFixed(0)} MB, ` +
          `above the ${(GIRO3D_MAX_GLB_BYTES / 1024 / 1024).toFixed(0)} MB per-GLB browser cap. ` +
          'Convert this model to smaller tiles before opening it in Giro3D.',
        );
      }

      if (bytes && knownBytes + bytes > GIRO3D_MAX_INITIAL_TILE_BYTES) {
        console.warn(
          `[Giro3DAdapter] Initial tile byte cap reached at ${accepted.length} GLBs ` +
          `(${(knownBytes / 1024 / 1024).toFixed(0)} MB known)`,
        );
        break;
      }

      if (bytes) knownBytes += bytes;
      accepted.push(uri);
    }

    if (accepted.length === 0 && uris.length > 0) {
      throw new Error(
        `${GIRO3D_ARTIFACT_TOO_LARGE_CODE}: no safe initial GLB tiles remain after browser safety checks.`,
      );
    }

    return accepted;
  }

  private async assertGlbUrlSafe(url: string, label: string): Promise<void> {
    const bytes = await this.readContentLength(url);
    if (bytes && bytes > GIRO3D_MAX_GLB_BYTES) {
      throw new Error(
        `${GIRO3D_ARTIFACT_TOO_LARGE_CODE}: ${label} is ${(bytes / 1024 / 1024).toFixed(0)} MB, ` +
        `above the ${(GIRO3D_MAX_GLB_BYTES / 1024 / 1024).toFixed(0)} MB browser cap. ` +
        'Use a 3D Tiles output for this model.',
      );
    }
  }

  private async readContentLength(url: string): Promise<number | null> {
    try {
      const head = await fetch(url, { method: 'HEAD' });
      if (!head.ok) return null;
      const len = Number(head.headers.get('content-length') || 0);
      return Number.isFinite(len) && len > 0 ? len : null;
    } catch {
      return null;
    }
  }

  /**
   * RFC-112: Fetch tileset.json and apply CRS alignment. Called during addLayer
   * for 3D Tiles to align content and position camera using the CRS aligner.
   */
  private async alignAndFocusTileset(tilesetUrl: string, layer: Layer): Promise<{ json: any; alignment: Alignment | null }> {
    try {
      const resp = await fetch(tilesetUrl);
      if (!resp.ok) {
        console.warn(`[Giro3DAdapter] Failed to fetch tileset: ${resp.status}`);
        this.markLayerLoadFailed(
          layer,
          `tileset fetch failed (HTTP ${resp.status}) — check the layer's tileset3d endpoint URL`,
        );
        return { json: null, alignment: null };
      }
      const json = await resp.json();

      // Derive CRS alignment parameters
      const sourceCrs = this.deriveTilesetSourceCrs(layer, json);
      const bounds = this.deriveTilesetBounds(json, layer, sourceCrs);
      const scaleMode = this.deriveTilesetScaleMode(layer);

      if (!sourceCrs || !bounds) {
        console.log(`[Giro3DAdapter] Skipping CRS alignment for ${layer.id} - no source CRS or bounds detected`);
        return { json, alignment: null };
      }

      // Run CRS alignment
      const alignment = await this.runLayerAlignment(sourceCrs, bounds, scaleMode);

      // Position camera using aligned anchor — only for the first tileset.
      if (alignment && !alignment.rejected) {
        const [x, y, z] = alignment.anchorFrame;
        const diagonal = this.estimateTilesetDiagonal(json, alignment);
        console.log(
          `[Giro3DAdapter] CRS applied for ${layer.id}: ${alignment.correctionLabel} | ` +
          `anchor=(lat,lon)=${alignment.anchorLatLon[0].toFixed(4)},${alignment.anchorLatLon[1].toFixed(4)} | ` +
          `appliedScale=${alignment.appliedScale.toFixed(3)}`
        );
        if (!this.hasFocusedFirstTileset) {
          this.positionCameraAt(x, y, z, diagonal);
          this.hasFocusedFirstTileset = true;
        }
        this.knownBounds = {
          center: [x, y, z],
          size: [diagonal, diagonal, diagonal],
          diagonal,
        };
      } else if (alignment?.rejected) {
        console.warn(`[Giro3DAdapter] CRS alignment rejected for ${layer.id}: ${alignment.rejected.reason}`);
        this.markLayerAlignmentRejected(layer, alignment);
      }

      return { json, alignment };
    } catch (err) {
      console.warn('[Giro3DAdapter] Failed to align tileset:', err);
      // A tileset URL that returns HTML (e.g. a container path resolved against
      // the dev server) or malformed JSON must announce itself like a CRS
      // rejection — a silently-empty scene is an operations incident (G16/G25).
      this.markLayerLoadFailed(
        layer,
        `tileset unreadable (${err instanceof Error ? err.message : String(err)}) — endpoint may not be a 3D Tiles URL`,
      );
      return { json: null, alignment: null };
    }
  }

  /**
   * G16 follow-up — surface tileset fetch/parse failures through the same
   * visible-alert path as CRS alignment rejections (banner + health=failed).
   */
  private markLayerLoadFailed(layer: Layer, reason: string): void {
    const identity = [
      1, 0, 0, 0,
      0, 1, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1,
    ];
    const failed = {
      matrix: identity,
      anchorFrame: [0, 0, 0],
      anchorLatLon: [0, 0],
      mercatorScaleAtAnchor: 1,
      appliedScale: 1,
      scaleMode: 'visual',
      correctionLabel: 'tileset-load-failed',
      correctionDetail: reason,
      rejected: { reason, code: 'TILESET_LOAD_FAILED' },
    } as unknown as Alignment;
    this.markLayerAlignmentRejected(layer, failed);
  }

  /**
   * RFC-112 / G16: Infer storage CRS from a 3D Tiles root bounding volume.
   * Region/box volumes in published CityGML tilesets imply EPSG:4978 (ECEF).
   */
  private inferCrsFromTilesetBoundingVolume(json: any): string | null {
    const region = json?.root?.boundingVolume?.region;
    const box = json?.root?.boundingVolume?.box;

    if (Array.isArray(region) && region.length >= 6) {
      return 'EPSG:4978';
    }

    if (Array.isArray(box)) {
      return 'EPSG:4978';
    }

    return null;
  }

  /**
   * RFC-112: Derive source CRS for a tileset based on layer metadata and tileset content.
   */
  private deriveTilesetSourceCrs(layer: Layer, json: any): string | null {
    // For 3D Tiles, the geometry storage CRS is `layer.crs` (normally
    // EPSG:4978/ECEF). `layer.sourceCrs` is provenance-only: the original
    // survey/source CRS before the converter published global 3D Tiles. Using
    // sourceCrs here makes the displayCrs alignment see EPSG:6697 geography
    // instead of EPSG:4978 ECEF and reject the non-3857 render frame.
    if (layer.kind === '3dtiles' && layer.crs) {
      return layer.crs;
    }

    // G16: when the scene item omits `crs`, prefer BV inference over provenance
    // `sourceCrs` — LOCAL survey CRS without `localBox` bounds rejects alignment
    // and leaves a silent blank scene.
    if (layer.kind === '3dtiles' && !layer.crs) {
      const inferred = this.inferCrsFromTilesetBoundingVolume(json);
      if (inferred) return inferred;
      if (layer.sourceCrs) return layer.sourceCrs;
      return null;
    }

    // Non-3D-Tiles layers may still use sourceCrs as the actual geometry CRS.
    if (layer.sourceCrs) {
      return layer.sourceCrs;
    }

    return this.inferCrsFromTilesetBoundingVolume(json);
  }

  /**
   * G16: Surface CRS alignment rejections to the host + mark layer health failed.
   */
  private markLayerAlignmentRejected(layer: Layer, alignment: Alignment): void {
    this.failedLayerIds.add(layer.id);
    const existing = this.layers.get(layer.id);
    if (existing) {
      this.layers.set(layer.id, {
        ...existing,
        alignment,
        health: 'failed',
      });
    } else {
      this.layers.set(layer.id, {
        layer,
        descriptor: { kind: '3dtiles', url: layer.endpoints.tileset3d ?? '', attachTo: 'instance-object' } as Giro3DLayerEntityDescriptor,
        entity: undefined,
        addedAt: new Date(0).toISOString(),
        alignment,
        health: 'failed',
      });
    }
    this.finalizeLayerHealth(layer.id, 'failed');
    this.emitCrsEvent(layer, alignment);
  }

  /**
   * RFC-112: Derive bounds for CRS aligner from tileset and layer metadata.
   */
  private deriveTilesetBounds(json: any, layer: Layer, sourceCrs?: string | null): any | null {
    const region = json?.root?.boundingVolume?.region;
    const box = json?.root?.boundingVolume?.box;
    const transform = json?.root?.transform;
    const normalizedSourceCrs = (sourceCrs ?? '').toUpperCase();
    const isProjectedNonEcef =
      Boolean(normalizedSourceCrs) &&
      normalizedSourceCrs !== 'EPSG:4978' &&
      normalizedSourceCrs !== 'EPSG:4326' &&
      normalizedSourceCrs !== 'OGC:CRS84' &&
      normalizedSourceCrs !== 'LOCAL';

    // VQA/local-projected converter outputs often carry a projected source CRS
    // but the GLB tiles are local/small, with the layer bbox as the real map
    // anchor. If we prefer the tileset box, the projected aligner anchors at
    // 0,0 and the 3D model appears far from the Dubai basemap/terrain.
    if (isProjectedNonEcef && Array.isArray(layer.bbox) && layer.bbox.length >= 4) {
      return { kind: 'bbox4326', bbox: layer.bbox };
    }

    if (Array.isArray(region) && region.length >= 6) {
      return { kind: 'region', region };
    }

    if (Array.isArray(box)) {
      return {
        kind: 'boxWithTransform',
        box,
        transform: Array.isArray(transform) ? transform : undefined,
      };
    }

    if (Array.isArray(layer.bbox) && layer.bbox.length >= 4) {
      return { kind: 'bbox4326', bbox: layer.bbox };
    }

    return null;
  }

  /**
   * RFC-112: Derive scale mode from layer style or use adapter default.
   * Block-tiled CityGML layers prefer engineering (unit scale) to minimise
   * Mercator inflation when the displayCrs render frame is unavailable.
   */
  private deriveTilesetScaleMode(layer: Layer): ScaleMode {
    const layerScaleMode = layer.style?.scaleMode;
    if (layerScaleMode === 'visual' || layerScaleMode === 'engineering') {
      return layerScaleMode;
    }
    if (this.isBlockTiled(layer)) {
      return 'engineering';
    }
    return this.defaultScaleMode;
  }

  private isBlockTiled(layer: Layer): boolean {
    return (layer.style as { blockTiled?: boolean } | undefined)?.blockTiled === true;
  }

  /**
   * Policy 118 §3.2 — block-tiled + displayCrs: parent union-BV matrix enables
   * tile SSE/frustum; per-child correction uses the same world-centre formula.
   */
  private shouldUseBlockTiledDisplayCrsCorrection(_layer: Layer): boolean {
    // With ENU rotation in the alignment matrix, the parent's single
    // ECEF→displayCrs transform is accurate for all child blocks within
    // ~10 km. Per-child delta correction is unnecessary and introduces
    // large vertical offsets through the inverse rotation.
    return false;
  }

  private normalizeTileUrlPath(url: string): string {
    const raw = url.split(/[?#]/, 1)[0] ?? url;
    try {
      return decodeURIComponent(new URL(raw, 'http://local/').pathname).replace(/\/$/, '');
    } catch {
      return raw.replace(/\/$/, '');
    }
  }

  private tilesetDirectory(url: string): string {
    const norm = this.normalizeTileUrlPath(url);
    const idx = norm.lastIndexOf('/');
    return idx >= 0 ? norm.slice(0, idx) : norm;
  }

  private deriveBoundsFromTilesetRoot(root: unknown): SourceBounds | null {
    const node = root as {
      boundingVolume?: { region?: number[]; box?: number[] };
      transform?: number[];
    };
    const region = node?.boundingVolume?.region;
    if (Array.isArray(region) && region.length >= 6) {
      return { kind: 'region', region: region as [number, number, number, number, number, number] };
    }
    const box = node?.boundingVolume?.box;
    const transform = node?.transform;
    if (Array.isArray(box)) {
      return {
        kind: 'boxWithTransform',
        box,
        transform: Array.isArray(transform) ? transform : undefined,
      };
    }
    return null;
  }

  private resolveBlockAlignmentMatrix(contentUrl: string): number[] | null {
    const dir = this.normalizeTileUrlPath(contentUrl);
    let best: { key: string; matrix: number[] } | null = null;
    for (const [key, matrix] of this.perBlockAlignmentByDir) {
      if (dir === key || dir.startsWith(`${key}/`)) {
        if (!best || key.length > best.key.length) {
          best = { key, matrix };
        }
      }
    }
    return best?.matrix ?? null;
  }

  private resolveBlockAlignmentKey(contentUrl: string): string | null {
    const dir = this.normalizeTileUrlPath(contentUrl);
    let best: string | null = null;
    for (const key of this.perBlockAlignmentByDir.keys()) {
      if (dir === key || dir.startsWith(`${key}/`)) {
        if (!best || key.length > best.length) best = key;
      }
    }
    if (best) return best;
    for (const key of this.perBlockAlignmentPending.keys()) {
      if (dir === key || dir.startsWith(`${key}/`)) {
        if (!best || key.length > best.length) best = key;
      }
    }
    return best;
  }

  private async awaitBlockAlignmentMatrix(contentUrl: string): Promise<number[] | null> {
    const cached = this.resolveBlockAlignmentMatrix(contentUrl);
    if (cached) return cached;
    const key = this.resolveBlockAlignmentKey(contentUrl);
    if (!key) return null;
    const pending = this.perBlockAlignmentPending.get(key);
    if (pending) {
      const matrix = await pending;
      return matrix ?? this.resolveBlockAlignmentMatrix(contentUrl);
    }
    return null;
  }

  /**
   * Policy 118 — per-child projection correction for block-tiled displayCrs.
   * Parent matrix (union BV) keeps tile SSE in range; each child tileset gets
   * the same world-centre → displayCrs formula; delta = M_parent⁻¹ × M_child.
   */
  private attachBlockTiledDisplayCrsCorrection(
    entity: unknown,
    layer: Layer,
    rootTilesetUrl: string,
    parentAlignment: Alignment,
  ): void {
    const tiles = (entity as { tiles?: { addEventListener?: (...args: unknown[]) => void } })?.tiles;
    if (!tiles || typeof tiles.addEventListener !== 'function') return;

    const rootDir = this.tilesetDirectory(rootTilesetUrl);
    const scaleMode = this.deriveTilesetScaleMode(layer);
    const sourceCrs = layer.crs || 'EPSG:4978';
    const parentMat = new THREE.Matrix4().fromArray(parentAlignment.matrix);
    const parentInv = parentMat.clone().invert();

    tiles.addEventListener('load-tileset', (event: { tileset?: unknown; url?: string }) => {
      const url = event?.url;
      const tileset = event?.tileset as { root?: unknown } | undefined;
      if (!url || !tileset?.root) return;

      const tilesetDir = this.tilesetDirectory(url);
      if (tilesetDir === rootDir) return;

      const pending = (async (): Promise<number[] | null> => {
        const bounds = this.deriveBoundsFromTilesetRoot(tileset.root);
        if (!bounds) return null;
        const alignment = await this.runLayerAlignment(sourceCrs, bounds, scaleMode);
        if (!alignment || alignment.rejected) {
          console.warn(
            `[Giro3DAdapter] Child displayCrs correction rejected for ${url}: ` +
              `${alignment?.rejected?.reason ?? 'unknown'}`,
          );
          return null;
        }
        this.perBlockAlignmentByDir.set(tilesetDir, alignment.matrix);
        console.log(
          `[Giro3DAdapter] Child displayCrs correction cached for ${tilesetDir}: ` +
            `${alignment.correctionLabel} (scale ${alignment.appliedScale.toFixed(3)})`,
        );
        return alignment.matrix;
      })();
      this.perBlockAlignmentPending.set(tilesetDir, pending);
    });

    tiles.addEventListener('load-model', (event: { scene?: unknown; url?: string }) => {
      const scene = event?.scene as THREE.Object3D | undefined;
      const url = event?.url;
      if (!scene || !url) return;

      void (async () => {
        const matrixArr = await this.awaitBlockAlignmentMatrix(url);
        if (!matrixArr) return;

        try {
          scene.updateMatrix();
          const childMatrix = new THREE.Matrix4().fromArray(matrixArr);
          const delta = parentInv.clone().multiply(childMatrix);
          scene.matrix.premultiply(delta);
          scene.matrix.decompose(scene.position, scene.quaternion, scene.scale);
          scene.updateMatrixWorld(true);
          (this.instance as any)?.notifyChange?.();
        } catch (err) {
          console.warn('[Giro3DAdapter] Failed to apply child displayCrs correction:', err);
        }
      })();
    });
  }

  /**
   * RFC-112: Estimate diagonal size from tileset bounds for camera positioning.
   */
  private estimateTilesetDiagonal(json: any, alignment: Alignment): number {
    const region = json?.root?.boundingVolume?.region;
    if (Array.isArray(region) && region.length >= 6) {
      const [west, south, east, north, minHeight, maxHeight] = region;
      const latSpan = Math.abs(north - south) * 180 / Math.PI;
      const lonSpan = Math.abs(east - west) * 180 / Math.PI;
      const earthRadius = 6378137;
      const meterSpanX = lonSpan * Math.PI / 180 * earthRadius * alignment.appliedScale;
      const meterSpanY = latSpan * Math.PI / 180 * earthRadius * alignment.appliedScale;
      const meterSpanZ = Math.abs(maxHeight - minHeight);
      return Math.sqrt(meterSpanX ** 2 + meterSpanY ** 2 + meterSpanZ ** 2);
    }

    const box = json?.root?.boundingVolume?.box;
    if (Array.isArray(box) && box.length >= 12) {
      const halfX = Math.abs(box[3]) || 1;
      const halfY = Math.abs(box[7]) || 1;
      const halfZ = Math.abs(box[11]) || 1;
      return Math.sqrt((halfX*2)**2 + (halfY*2)**2 + (halfZ*2)**2) * alignment.appliedScale;
    }

    // Fallback
    return 1000;
  }

  /**
   * RFC-112: Re-apply CRS alignment for a layer with new scale mode.
   */
  private async reapplyCrsAlignment(layerId: string, record: GiroLayerRecord, newScaleMode: ScaleMode): Promise<void> {
    if (!record.alignment || !record.entity) return;

    // Re-run alignment with new scale mode using the same parameters as before
    // but with the new scale mode
    const sourceCrs = record.layer.sourceCrs || 'EPSG:4978'; // Default from original alignment
    
    // Use layer bbox as bounds for reapplication (simplified approach for scale mode change)
    const bounds: SourceBounds = { kind: 'bbox4326' as const, bbox: record.layer.bbox };

    try {
      const newAlignment = await this.runLayerAlignment(sourceCrs, bounds, newScaleMode);

      if (newAlignment && !newAlignment.rejected) {
        // Apply new matrix to entity
        this.applyAlignmentMatrix(record.entity, newAlignment);
        
        // Update record
        const updatedRecord: GiroLayerRecord = { ...record, alignment: newAlignment };
        this.layers.set(layerId, updatedRecord);

        // Emit event
        this.emitCrsEvent(record.layer, newAlignment);
      }
    } catch (error) {
      console.warn(`[Giro3DAdapter] Failed to re-apply alignment for ${layerId}:`, error);
    }
  }

  /**
   * RFC-112: Apply CRS alignment matrix to a 3D entity (container or Tiles3D entity).
   */
  private applyAlignmentMatrix(entity: any, alignment: Alignment): void {
    const matrix = new THREE.Matrix4().fromArray(alignment.matrix);
    
    // For direct GLB containers (loadTilesDirectly path)
    if (entity.matrixAutoUpdate !== undefined) {
      entity.matrixAutoUpdate = false;
      entity.matrix.copy(matrix);
      entity.updateMatrixWorld(true);
      return;
    }
    
    // For Giro3D Tiles3D entities (fallback path)
    if (entity.tiles?.group) {
      const group = entity.tiles.group;
      group.matrixAutoUpdate = false;
      group.matrix.copy(matrix);
      group.updateMatrixWorld(true);
      
      // Fix D: remove previous listener to prevent accumulation / stale matrix.
      const entityRef = entity as any;
      if (entityRef.__giro3d_reapply_listener) {
        entity.tiles.removeEventListener?.('load-tileset', entityRef.__giro3d_reapply_listener);
      }
      const reapply = () => {
        group.matrixAutoUpdate = false;
        group.matrix.copy(matrix);
        group.updateMatrixWorld(true);
        (this.instance as any)?.notifyChange?.((this.instance as any)?.view?.camera);
      };
      entity.tiles.addEventListener?.('load-tileset', reapply);
      entityRef.__giro3d_reapply_listener = reapply;
    }
  }

  // ─── Q8 Operator Quality Signals ─────────────────────────────────────────
  // Separate region — do not merge with Q1 / Q4+Q5+M10 / M8 code paths.

  /**
   * Align a layer into EPSG:3857, reusing the project ECEF anchor for every
   * subsequent EPSG:4978 tileset so roads/bridges/furniture share the same
   * ENU frame as buildings (prevents vertical drift between feature types).
   */
  private async runLayerAlignment(
    sourceCrs: string,
    bounds: SourceBounds,
    scaleMode: ScaleMode,
  ): Promise<Alignment> {
    // Policy 118: displayCrs uses translation-only alignment per single tileset;
    // block-tiled layers skip group alignment (child root.transform is authoritative).
    // Legacy EPSG:3857 keeps shared-anchor co-registration across feature types.
    const renderingInDisplayCrs = this.activeFrameCrs !== 'EPSG:3857';
    const usingShared = Boolean(
      !renderingInDisplayCrs && this.projectEcefAnchor && sourceCrs === 'EPSG:4978',
    );
    const alignment = await align({
      sourceCrs,
      frameCrs: this.activeFrameCrs,
      bounds,
      scaleMode,
      sharedAnchor: usingShared ? this.projectEcefAnchor ?? undefined : undefined,
      ...(renderingInDisplayCrs && this.renderFrameProj4
        ? { frameCrsProj4: this.renderFrameProj4 }
        : {}),
    });

    if (
      !renderingInDisplayCrs &&
      !this.projectEcefAnchor &&
      alignment &&
      !alignment.rejected &&
      sourceCrs === 'EPSG:4978'
    ) {
      this.projectEcefAnchor = {
        lat: alignment.anchorLatLon[0],
        lon: alignment.anchorLatLon[1],
        height: alignment.anchorFrame[2],
      };
      console.log(
        `[Giro3DAdapter] Project ECEF anchor set: lat=${this.projectEcefAnchor.lat.toFixed(4)} lon=${this.projectEcefAnchor.lon.toFixed(4)} height=${(this.projectEcefAnchor.height ?? 0).toFixed(1)}m`,
      );
    } else if (usingShared) {
      console.log('[Giro3DAdapter] CRS alignment reuses project ECEF anchor');
    }

    return alignment;
  }

  /**
   * Neutralise the 3d-tiles-renderer glTF up-axis correction for ECEF tilesets.
   *
   * The converter writes b3dm glTF vertices directly in ECEF axes (Z-up ECEF),
   * but 3d-tiles-renderer defaults `gltfUpAxis='y'` and bakes a +90° X rotation
   * into every tile (`_upRotationMatrix`), tilting the geometry ~camera-visible.
   * For our ECEF (EPSG:4978) content the correct adjustment is identity; the
   * dataset's ENU alignment matrix on the group then renders buildings upright.
   * Runtime evidence: relToGroupEuler=(90,0,0); localSize≈ECEF box extents.
   */
  private attachEcefUpAxisFix(entity: unknown, layer: Layer): void {
    const sourceCrs = (layer.crs || '').toUpperCase();
    if (sourceCrs !== 'EPSG:4978') return;
    const tiles = (entity as {
      tiles?: {
        _upRotationMatrix?: { identity?: () => void };
        addEventListener?: (...args: unknown[]) => void;
      };
    })?.tiles;
    if (!tiles) return;
    const onLoadTileset = (event: { tileset?: { asset?: { gltfUpAxis?: string } } }) => {
      // Honor an explicit up-axis declaration: spec-compliant tilesets that
      // stamp `asset.gltfUpAxis` are handled correctly by the renderer, so we
      // must NOT override them. Only neutralise the default +90° X rotation for
      // legacy converter output that ships ECEF-axis glTF without a declaration.
      const declared = event?.tileset?.asset?.gltfUpAxis;
      if (declared) return;
      tiles._upRotationMatrix?.identity?.();
    };
    if (typeof tiles.addEventListener === 'function') {
      tiles.addEventListener('load-tileset', onLoadTileset);
    }
  }

  /**
   * Per-tile material fix on stream — lightweight alternative to the 1 Hz scene
   * sweep. Patches only the tile that just loaded so textured PLATEAU buildings
   * get correct sRGB maps without regressing load performance.
   */
  private attachPlateauTileMaterialFixes(entity: unknown): void {
    const tiles = (entity as { tiles?: { addEventListener?: (...args: unknown[]) => void } })?.tiles;
    if (!tiles || typeof tiles.addEventListener !== 'function') return;

    tiles.addEventListener('load-model', (event: { scene?: unknown; tile?: unknown; url?: string }) => {
      const scene = event?.scene as { traverse?: (cb: (child: unknown) => void) => void; userData?: Record<string, unknown> } | undefined;
      if (!scene) return;

      // Stamp blockCode on the scene for setBlockVisible lookups.
      const modelUrl = event.url
        ?? (event.tile as any)?.content?.uri
        ?? '';
      const blockMatch = /(\d{8})/.exec(modelUrl);
      if (blockMatch) {
        const s = scene as any;
        if (!s.userData) s.userData = {};
        s.userData.__blockCode = blockMatch[1];
        // Also stamp all child meshes so traverse always finds them.
        if (typeof s.traverse === 'function') {
          s.traverse((c: any) => {
            if (!c.userData) c.userData = {};
            c.userData.__blockCode = blockMatch[1];
          });
        }
      }

      // INIT-2026-Q3-SCENE-HIERARCHY geometry-binding — tile-local gml_id stamp.
      // The 3d-tiles-renderer keeps the b3dm batch table on the loaded model
      // group (`scene.batchTable`), NOT on individual meshes. `_BATCHID` is only
      // unique WITHIN a tile, so a global batch-number match lights up one
      // building per tile across every block. Stamp each mesh with this tile's
      // gml_id column so selection can resolve the batch id tile-locally and
      // highlight exactly the one building whose gml_id matches.
      try {
        const sc = scene as any;
        const bt = sc.batchTable ?? sc.userData?.batchTable;
        let gmlIds: unknown = null;
        if (bt) {
          if (typeof bt.getData === 'function') gmlIds = bt.getData('gml_id');
          else if (Array.isArray(bt.gml_id)) gmlIds = bt.gml_id;
          else if (bt.data && Array.isArray(bt.data.gml_id)) gmlIds = bt.data.gml_id;
        }
        if (gmlIds && (gmlIds as ArrayLike<unknown>).length) {
          (scene as { traverse?: (cb: (c: unknown) => void) => void }).traverse?.((c: unknown) => {
            const m = c as THREE.Mesh;
            if ((m as unknown as { isMesh?: boolean }).isMesh) {
              (m.userData as Record<string, unknown>).__gmlIds = gmlIds;
            }
          });
        }
      } catch { /* non-fatal: highlight falls back to numeric match */ }

      patchPlateauTileScene(scene);
      this.reapplyLayerOpacityForEntity(entity);
      (this.instance as any)?.notifyChange?.();
    });
  }

  /**
   * Re-apply the current opacity value to every mesh in the entity. Required
   * for 3D Tiles because new tiles stream in continuously; a one-time
   * traverse() only catches meshes that were already loaded.
   */
  private reapplyLayerOpacityForEntity(entity: unknown): void {
    let layerId: string | null = null;
    for (const [id, record] of this.layers.entries()) {
      if (record.entity === entity) {
        layerId = id;
        break;
      }
    }
    if (!layerId) return;
    const opacity = this.layerOpacity.get(layerId);
    if (typeof opacity !== 'number' || opacity >= 1) return;
    this.applyEntityOpacity(entity, opacity);
  }

  /** Walk an entity's Object3D tree and clamp every material's opacity. */
  private applyEntityOpacity(entity: unknown, opacity: number): void {
    const ent = entity as { traverse?: (cb: (child: unknown) => void) => void } | undefined;
    if (typeof ent?.traverse !== 'function') return;
    ent.traverse((child: unknown) => {
      const mesh = child as { material?: unknown };
      if (!mesh.material) return;
      const apply = (mat: unknown) => {
        const material = mat as { transparent?: boolean; opacity?: number; depthWrite?: boolean; needsUpdate?: boolean };
        material.transparent = opacity < 1;
        material.opacity = opacity;
        if (opacity < 1) {
          material.depthWrite = false;
        }
        material.needsUpdate = true;
      };
      if (Array.isArray(mesh.material)) mesh.material.forEach(apply);
      else apply(mesh.material);
    });
  }

  /**
   * @deprecated Use patchPlateauCoarseMaterial from plateauTileMaterials.ts
   */
  // @ts-expect-error Deprecated method kept for backward compatibility
  private patchPlateauStreamedMaterial(_mat: unknown, _geometry?: unknown): void {
    // Delegated to plateauTileMaterials.ts via attachPlateauTileMaterialFixes.
  }

  /**
   * @deprecated Use patchPlateauCoarseMaterial from plateauTileMaterials.ts
   */
  private patchSingleMaterial(mat: unknown, hasVertexColors: boolean, geometry?: unknown): void {
    if (!mat) return;
    const geom = geometry ?? (hasVertexColors ? { attributes: { color: true } } : undefined);
    patchPlateauCoarseMaterial(mat as THREE.Material & Record<string, unknown>, geom);
  }

  /**
   * Size the shared tile LRU + download/parse queues after Giro3D preprocess.
   * All Tiles3D entities in one Instance share one cache; textured PLATEAU
   * tiles are byte-heavy, so the default 400 MB cap evicts tiles before render.
   */
  private configureSharedTileResources(entity: unknown): void {
    const tiles = (entity as { tiles?: {
      lruCache?: { maxSize?: number; minSize?: number; maxBytesSize?: number; minBytesSize?: number; cachedBytes?: number };
      parseQueue?: { maxJobs?: number };
      downloadQueue?: { maxJobs?: number };
    } })?.tiles;
    if (!tiles) return;

    const ONE_GB = 1024 * 1024 * 1024;
    if (tiles.lruCache) {
      tiles.lruCache.maxSize = 15000;
      tiles.lruCache.minSize = 8000;
      tiles.lruCache.maxBytesSize = 3 * ONE_GB;
      tiles.lruCache.minBytesSize = ONE_GB;
    }
    // Throttled to keep the main thread responsive during multi-layer streaming.
    // The b3dm parse step does GPU uploads on the main thread; running 10 in
    // parallel caused 150ms+ click-handler violations and visible UI freezes
    // when several tilesets started streaming at once. 4 parse jobs / 8 download
    // jobs still saturates a modern broadband link while leaving enough budget
    // for React renders and user input.
    if (tiles.parseQueue) tiles.parseQueue.maxJobs = 4;
    if (tiles.downloadQueue) tiles.downloadQueue.maxJobs = 8;

    if (!this.sharedTileCacheConfigured) {
      const maxGb = tiles.lruCache?.maxBytesSize ? (tiles.lruCache.maxBytesSize / ONE_GB).toFixed(1) : '?';
      console.log(
        `[Giro3DAdapter] Shared tile cache configured: maxSize=${tiles.lruCache?.maxSize ?? '?'} maxBytes=${maxGb}GB parseJobs=${tiles.parseQueue?.maxJobs ?? '?'} downloadJobs=${tiles.downloadQueue?.maxJobs ?? '?'}`,
      );
      this.sharedTileCacheConfigured = true;
    }

    maybeRegisterPlateauLodFade(entity);
  }

  /** Camera-to-orbit-target distance in scene units (~meters for Tokyo 3857 scenes). */
  getCameraTargetDistanceM(): number | null {
    const inst = this.instance as { view?: { camera?: { position?: { x: number; y: number; z: number } } } } | null;
    const camera = inst?.view?.camera;
    const target = (this.controls as { target?: { x: number; y: number; z: number } } | null)?.target;
    const pos = camera?.position;
    if (!pos || !target) return null;
    const dx = pos.x - target.x;
    const dy = pos.y - target.y;
    const dz = pos.z - target.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    return Number.isFinite(dist) ? dist : null;
  }

  /** ACES for PBR city tiles; NoToneMapping when only point clouds are mounted. */
  private reconcileRendererToneMapping(): void {
    const inst = this.instance as {
      renderer?: { toneMapping?: THREE.ToneMapping; toneMappingExposure?: number };
      mainLoop?: { gfxEngine?: { renderer?: { toneMapping?: THREE.ToneMapping; toneMappingExposure?: number } } };
      notifyChange?: () => void;
    } | null;
    const renderer = inst?.renderer ?? inst?.mainLoop?.gfxEngine?.renderer;
    const kinds = [...this.layers.values()].map((record) => record.layer?.kind).filter(Boolean) as LayerKind[];
    const changed = applyPlateauRendererToneMapping(renderer, kinds);
    if (!changed || !renderer) return;

    const mode =
      renderer.toneMapping === THREE.ACESFilmicToneMapping ? 'ACESFilmic' : 'NoToneMapping';
    console.log(
      `[Giro3DAdapter] Renderer tone mapping → ${mode} exposure=${renderer.toneMappingExposure ?? '?'}`,
    );
    inst?.notifyChange?.();
  }

  getTileLoadProgress(): TileLoadProgress {
    return this.tileLoadProgress;
  }

  /** Per-layer tile stats snapshot. Returns a new Map each call; safe to store in React state. */
  getTileProgressByLayer(): Map<string, { loaded: number; total: number; inFlight: number }> {
    return new Map(this.tileProgressByLayer);
  }

  subscribeTileLoadProgress(listener: (progress: TileLoadProgress) => void): () => void {
    this.tileLoadProgressListeners.add(listener);
    return () => {
      this.tileLoadProgressListeners.delete(listener);
    };
  }

  /** Hook tile-load progress from a Tiles3D / 3d-tiles-renderer entity. */
  attachTileLoadProgress(entity: unknown, layerId: string): void {
    const tiles = (entity as { tiles?: TilesProgressSource })?.tiles;
    if (!tiles || typeof tiles.addEventListener !== 'function') return;

    // Resolve a friendly layer name for diagnostics.
    const layer = this.layers.get(layerId)?.layer;
    const layerName = layer?.label || layer?.id || layerId;
    const shortName = layerName.replace(/^layer:run-[a-f0-9]+:0:tileset$/, layerId);

    let lastLoaded = 0;
    let lastLogAt = 0;
    let firstLoadAt = 0;
    const startTime = performance.now();

    const report = (eventLabel: string) => {
      const stats = tiles.stats ?? {};
      const queued = stats.queued ?? 0;
      const downloading = stats.downloading ?? 0;
      const parsing = stats.parsing ?? 0;
      const inFlight =
        eventLabel === 'end' ? 0 : queued + downloading + parsing;
      const loaded = stats.loaded ?? 0;
      const total = Math.max(loaded + inFlight, stats.inCacheSinceLoad ?? 0, loaded);
      const phase: TileLoadPhase =
        eventLabel === 'end'
          ? 'done'
          : !this.tileLoadInitialComplete
            ? 'initial'
            : inFlight > 0 || tiles.isLoading
              ? 'streaming'
              : 'done';
      this.updateLayerTileProgress(layerId, loaded, total, inFlight, phase);
      if (loaded > 0) {
        if (firstLoadAt === 0) firstLoadAt = performance.now();
        this.finalizeLayerHealth(layerId, 'ready');
      }

      // Diagnostic log: throttled to once per second OR when count changes by 10+.
      const now = performance.now();
      const loadDelta = loaded - lastLoaded;
      const elapsedSinceLastLog = now - lastLogAt;
      if (elapsedSinceLastLog > 1000 || loadDelta >= 10 || eventLabel === 'end') {
        const elapsedTotal = ((now - startTime) / 1000).toFixed(1);
        const cacheBytes = tiles.lruCache?.cachedBytes ?? 0;
        const cacheMaxBytes = tiles.lruCache?.maxBytesSize ?? 0;
        const cacheMb = (cacheBytes / (1024 * 1024)).toFixed(0);
        const cacheMaxMb = cacheMaxBytes > 0 ? (cacheMaxBytes / (1024 * 1024)).toFixed(0) : '?';
        console.log(
          `[Tiles] ${shortName} | ${eventLabel} | loaded=${loaded} downloading=${downloading} parsing=${parsing} queued=${queued} cacheMB=${cacheMb}/${cacheMaxMb} | t=${elapsedTotal}s`,
        );
        lastLoaded = loaded;
        lastLogAt = now;
      }
    };

    tiles.addEventListener('tiles-load-start', () => report('start'));
    tiles.addEventListener('load-tileset', () => report('tileset'));
    tiles.addEventListener('tiles-load-end', () => {
      const stats = tiles.stats ?? {};
      const loaded = stats.loaded ?? 0;
      this.tileLoadInitialComplete = true;
      this.updateLayerTileProgress(layerId, loaded, Math.max(loaded, 1), 0, 'done');
      this.finalizeLayerHealth(layerId, 'ready');
      report('end');
    });
    // Per-tile content loaded — log throttled progress
    tiles.addEventListener('load-content', () => report('content'));
    tiles.addEventListener('load-error', (err: any) => {
      console.warn(`[Tiles] ${shortName} | LOAD ERROR | url=${err?.url ?? '?'} status=${err?.status ?? '?'}`);
    });
    report('attach');
  }

  private seedPendingLayerHealth(layer: Layer, descriptor: Giro3DLayerEntityDescriptor): void {
    this.layers.set(layer.id, {
      layer,
      descriptor,
      addedAt: new Date().toISOString(),
      health: 'pending',
    });
  }

  private finalizeLayerHealth(layerId: string, health: SceneItemHealth): void {
    const record = this.layers.get(layerId);
    if (!record) return;
    this.layers.set(layerId, { ...record, health });
  }

  private updateLayerTileProgress(
    layerId: string,
    loaded: number,
    total: number,
    inFlight: number,
    phase: TileLoadPhase,
  ): void {
    this.tileProgressByLayer.set(layerId, { loaded, total, inFlight });
    this.republishAggregateTileProgress(phase);
  }

  private republishAggregateTileProgress(lastPhase: TileLoadPhase): void {
    let loaded = 0;
    let total = 0;
    let anyInFlight = false;
    for (const stats of this.tileProgressByLayer.values()) {
      loaded += stats.loaded;
      total += stats.total;
      if (stats.inFlight > 0) anyInFlight = true;
    }
    const phase: TileLoadPhase =
      !this.tileLoadInitialComplete
        ? 'initial'
        : anyInFlight
          ? 'streaming'
          : lastPhase === 'done' && this.tileProgressByLayer.size > 0
            ? 'done'
            : lastPhase;
    this.publishTileLoadProgress(loaded, total, phase);
  }

  private publishTileLoadProgress(loaded: number, total: number, phase: TileLoadPhase): void {
    const next: TileLoadProgress = { loaded, total, phase };
    this.tileLoadProgress = next;
    this.host?.emit({ type: 'loading', loaded, total, phase });
    for (const listener of this.tileLoadProgressListeners) {
      listener(next);
    }
  }

  private emitDirectTileLoadProgress(loaded: number, total: number): void {
    const phase: TileLoadPhase = this.tileLoadInitialComplete ? 'streaming' : 'initial';
    this.publishTileLoadProgress(loaded, total, phase);
  }

  private emitDirectTileLoadComplete(total: number): void {
    this.tileLoadInitialComplete = true;
    this.publishTileLoadProgress(total, total, 'done');
  }

  /**
   * RFC-112: Emit CRS correction event to host.
   */
  private emitCrsEvent(layer: Layer, alignment: Alignment): void {
    const event = {
      type: 'crsCorrectionApplied' as const,
      layerId: layer.id,
      sourceCrs: alignment.correctionLabel.split(' → ')[0] || 'unknown',
      frameCrs: this.activeFrameCrs,
      scaleMode: alignment.scaleMode,
      appliedScale: alignment.appliedScale,
      mercatorScaleAtAnchor: alignment.mercatorScaleAtAnchor,
      anchorLatLon: alignment.anchorLatLon,
      anchorFrame: alignment.anchorFrame,
      correctionLabel: alignment.correctionLabel,
      correctionDetail: alignment.correctionDetail,
      rejected: alignment.rejected,
    };
    this.host?.emit(event);
  }

  /**
   * RFC-112: Apply CRS alignment to fallback Giro3D Tiles3D entity.
   */
  private async alignTilesetEntity(entity: any, layer: Layer): Promise<Alignment | null> {
    try {
      // For Giro3D Tiles3D entity, we need to fetch tileset.json ourselves
      // since we don't have it from the direct loading path
      const url = entity.url || layer.endpoints.tileset3d;
      if (!url) return null;

      const resp = await fetch(url);
      if (!resp.ok) return null;
      const json = await resp.json();

      const sourceCrs = this.deriveTilesetSourceCrs(layer, json);
      const bounds = this.deriveTilesetBounds(json, layer, sourceCrs);
      const scaleMode = this.deriveTilesetScaleMode(layer);

      if (!sourceCrs || !bounds) return null;

      const alignment = await this.runLayerAlignment(sourceCrs, bounds, scaleMode);

      if (alignment) {
        // Stamp the alignment on the entity so the outer addLayer() path can
        // persist it onto the GiroLayerRecord. Without this stash, the record
        // ends up with `alignment: undefined` and `setDefaultScaleMode()` has
        // nothing to re-align (RFC-112 setDefaultScaleMode contract).
        (entity as { __giro3d_alignment?: Alignment }).__giro3d_alignment = alignment;
        this.emitCrsEvent(layer, alignment);
      }

      return alignment;
    } catch (error) {
      console.warn(`[Giro3DAdapter] Failed to align fallback Tiles3D entity:`, error);
      return null;
    }
  }

  /**
   * Focus the camera on a Tiles3D entity after tiles start loading.
   * Retries with actual geometry data for precise framing.
   */
  private focusCameraOnTileset(entity: unknown): void {
    const ent = entity as any;
    if (!ent) return;

    // Auto-focus only the first tileset. With many concurrent layers (e.g.
    // PLATEAU Tier-2's 17 datasets) re-framing per layer ends with the
    // camera somewhere only the last layer's bbox can see — leaving the
    // viewport on empty space. Subsequent layers still attach normally;
    // they just don't hijack the camera.
    if (this.hasFocusedFirstTileset) {
      return;
    }

    // Try to read the root bounding volume (available from tileset.json parse)
    const root = ent?.root ?? ent?.tileset?.root;
    const bv = root?.boundingVolume?.box ?? ent?.boundingVolume?.box;

    if (bv && bv.length === 12) {
      // Resolve the focus target in the engine's WORLD/scene frame, never the
      // raw box coordinates. For a georeferenced tileset the raw OBB center is
      // an ECEF coordinate (mega-metres from the origin) while the geometry is
      // rendered wherever `applyAlignmentMatrix` placed the tiles group (the
      // aligned displayCrs/3857 frame, near the anchor). Targeting the raw box
      // is exactly what made buildings "fly the camera far away".
      //
      // Prefer the entity's actual streamed world bounds when any tile has
      // loaded (unambiguous); otherwise project the raw root OBB through the
      // entity's world transform so we still land on the rendered geometry.
      const focus = this.resolveTilesetWorldFocus(ent, bv);

      if (focus && focus.diagonal > 0) {
        const [cx, cy, cz] = focus.center;
        this.positionCameraAt(cx, cy, cz, focus.diagonal, true);
        this.hasFocusedFirstTileset = true;

        // Fix C4: Set TilesRenderer resolution
        const tr = ent?.tilesRenderer ?? ent?.renderer;
        if (tr && typeof tr.setResolutionFromRenderer === 'function') {
          const cam = (this.instance as any)?.view?.camera ?? (this.instance as any)?.camera?.camera3D;
          const ren = (this.instance as any)?.renderer ?? (this.instance as any)?.viewer?.renderer;
          if (cam && ren) {
            tr.setResolutionFromRenderer(cam, ren);
          }
        }
        return;
      }
    }

    // Do not start a long-running bounds monitor here. It repeatedly retunes
    // controls/clipping while 3D Tiles stream, which made zoom unstable and
    // spammed "Model sphere cached / Controls tuned" every second.
  }

  /**
   * Resolve a 3D Tiles focus target (center + diagonal) in the engine's WORLD
   * frame. Prefers real streamed geometry bounds; falls back to projecting the
   * tileset.json root oriented-bounding-box through the entity's applied world
   * transform (the CRS-alignment matrix, or the native 3D Tiles georef when no
   * alignment ran). Returns null when neither source yields a finite box.
   *
   * This is the generic fix for "georeferenced 3D Tiles fly the camera far
   * away": the raw OBB lives in the tileset's native (often ECEF) frame, but
   * the geometry is rendered in the aligned frame — so the camera must target
   * the transformed position, not the raw coordinates.
   */
  private resolveTilesetWorldFocus(
    entity: unknown,
    bv: number[],
  ): { center: [number, number, number]; diagonal: number } | null {
    const streamed = this.computeEntityWorldBounds(entity);
    if (streamed) {
      const center: [number, number, number] = [
        (streamed.min.x + streamed.max.x) / 2,
        (streamed.min.y + streamed.max.y) / 2,
        (streamed.min.z + streamed.max.z) / 2,
      ];
      const diagonal = Math.sqrt(
        (streamed.max.x - streamed.min.x) ** 2 +
          (streamed.max.y - streamed.min.y) ** 2 +
          (streamed.max.z - streamed.min.z) ** 2,
      );
      if (isFiniteVec3(center[0], center[1], center[2]) && diagonal > 0) {
        return { center, diagonal };
      }
    }

    if (!Array.isArray(bv) || bv.length < 12) return null;
    const ent = entity as any;
    const group = ent?.tiles?.group ?? ent;
    group?.updateMatrixWorld?.(true);
    const world: THREE.Matrix4 =
      group?.matrixWorld instanceof THREE.Matrix4 ? group.matrixWorld : new THREE.Matrix4();

    // 3D Tiles OBB layout: [cx,cy,cz, xAxis(3), yAxis(3), zAxis(3)] where each
    // axis is a half-extent vector. Project all 8 corners so the resulting
    // world AABB accounts for the matrix's rotation AND any applied scale.
    const center = new THREE.Vector3(bv[0], bv[1], bv[2]);
    const ax = new THREE.Vector3(bv[3], bv[4], bv[5]);
    const ay = new THREE.Vector3(bv[6], bv[7], bv[8]);
    const az = new THREE.Vector3(bv[9], bv[10], bv[11]);

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    const corner = new THREE.Vector3();
    for (const sx of [-1, 1]) {
      for (const sy of [-1, 1]) {
        for (const sz of [-1, 1]) {
          corner
            .copy(center)
            .addScaledVector(ax, sx)
            .addScaledVector(ay, sy)
            .addScaledVector(az, sz)
            .applyMatrix4(world);
          if (!isFiniteVec3(corner.x, corner.y, corner.z)) continue;
          minX = Math.min(minX, corner.x); maxX = Math.max(maxX, corner.x);
          minY = Math.min(minY, corner.y); maxY = Math.max(maxY, corner.y);
          minZ = Math.min(minZ, corner.z); maxZ = Math.max(maxZ, corner.z);
        }
      }
    }
    if (!Number.isFinite(minX)) return null;
    const wc: [number, number, number] = [
      (minX + maxX) / 2,
      (minY + maxY) / 2,
      (minZ + maxZ) / 2,
    ];
    const diagonal = Math.sqrt((maxX - minX) ** 2 + (maxY - minY) ** 2 + (maxZ - minZ) ** 2);
    if (!isFiniteVec3(wc[0], wc[1], wc[2]) || !(diagonal > 0)) return null;
    return { center: wc, diagonal };
  }

  /**
   * Continuously poll the Tiles3D entity's bounding box for up to 120 seconds.
   * Updates the model sphere whenever the bounds become more precise.
   * This handles huge models where tiles take a long time to load and the
   * initial tileset.json bounds may be in a different coordinate frame
   * than the actual rendered geometry (Y-up vs Z-up).
   */
  // @ts-expect-error Kept for future tile bounds monitoring
  private startTilesetBoundsMonitor(ent: any): void {
    const startTime = performance.now();
    const maxDurationMs = 120_000;
    const intervalMs = 1000;
    let lastRadius = -1;
    let lastCenterKey = '';
    let cameraAlreadyPositioned = false;

    const monitor = () => {
      if (!this.instance) return;
      const elapsed = performance.now() - startTime;

      let hasGeom = false;
      if (typeof ent.traverse === 'function') {
        ent.traverse((child: any) => {
          if (child.geometry) hasGeom = true;
        });
      }

      // Always try to patch materials on loaded tile meshes, even before
      // we detect precise geometry bounds. Tiles can load incrementally.
      this.applyTileMaterialFixes(ent);

      if (hasGeom) {
        // Compute new bounds. cacheModelSphere does this and also re-syncs
        // controls.target if it has drifted significantly.
        this.cacheModelSphere();
        this.tuneControlsForModel();

        const sphere = this.modelSphere;
        if (sphere && sphere.radius > 0) {
          const center = sphere.center;
          const newCenterKey = `${center.x.toFixed(0)}|${center.y.toFixed(0)}|${center.z.toFixed(0)}`;
          const radiusChanged = Math.abs(sphere.radius - lastRadius) / Math.max(sphere.radius, Math.abs(lastRadius)) > 0.05;
          const centerChanged = newCenterKey !== lastCenterKey;

          if (radiusChanged || centerChanged) {
            console.log(
              `[Giro3DAdapter] Bounds refined after ${(elapsed / 1000).toFixed(1)}s: ` +
              `center=(${center.x.toFixed(1)}, ${center.y.toFixed(1)}, ${center.z.toFixed(1)}), ` +
              `radius=${sphere.radius.toFixed(1)}`,
            );
            // Re-position camera ONLY on the FIRST precise bounds — after that,
            // we don't want to teleport the user's view every time tiles load.
            if (!cameraAlreadyPositioned) {
              this.focusCameraOnObject(ent);
              cameraAlreadyPositioned = true;
            }
            lastRadius = sphere.radius;
            lastCenterKey = newCenterKey;
          }
        }
      }

      if (elapsed < maxDurationMs) {
        setTimeout(monitor, intervalMs);
      }
    };
    setTimeout(monitor, 500);
  }

  private positionCameraAt(cx: number, cy: number, cz: number, diagonal: number, topDown = false, _retries = 0): void {
    const inst = this.instance as any;
    const cam = inst?.view?.camera ?? inst?.camera?.camera3D;
    if (!cam) return;
    if (!isFiniteVec3(cx, cy, cz) || !Number.isFinite(diagonal) || diagonal <= 0) {
      return;
    }

    // PLATEAU LOD3 datasets (furniture, vegetation, trees) need the camera
    // within ~2-4 km of the city center to even start streaming tiles.
    // But that 1.2 km floor buries compact local-engineering models (e.g.
    // VirtualEye Potree sidecar clouds with ~100 m diagonals) as sub-pixel
    // specks, so scale the floor down for small scenes.
    const minDistance = Math.min(1200, Math.max(diagonal * 0.9, 10));
    const distance = Math.max(diagonal * 0.45, minDistance);
    if (!Number.isFinite(distance) || distance <= 0) return;
    if (topDown) {
      cam.position.set(cx, cy, cz + distance);
    } else {
      cam.position.set(cx + distance * 0.6, cy + distance * 0.6, cz + distance * 0.6);
    }
    cam.up?.set(0, 0, 1);
    cam.lookAt(cx, cy, cz);
    const nearPlane = 0.1;
    const farPlane = 10_000_000;
    if (!Number.isFinite(nearPlane) || !Number.isFinite(farPlane) || farPlane <= nearPlane) return;
    cam.near = nearPlane;
    cam.far = farPlane;
    cam.updateProjectionMatrix?.();

    if (this.controls?.target) {
      this.controls.target.set(cx, cy, cz);
      this.controls.update?.();
    }

    const px = cam.position.x;
    const py = cam.position.y;
    const pz = cam.position.z;
    if (Number.isFinite(px) && Number.isFinite(py) && Number.isFinite(pz)) {
      console.log(
        `[Giro3DAdapter] Camera positioned: pos=(${px.toFixed(1)}, ${py.toFixed(1)}, ${pz.toFixed(1)}) target=(${cx.toFixed(1)}, ${cy.toFixed(1)}, ${cz.toFixed(1)}) dist=${distance.toFixed(1)}`,
      );
    } else if (_retries < 10) {
      // Giro3D's view is not yet initialised — the renderer overwrites
      // camera.position on the first few frames. Retry after a frame.
      requestAnimationFrame(() => this.positionCameraAt(cx, cy, cz, diagonal, topDown, _retries + 1));
      return;
    } else {
      console.warn('[Giro3DAdapter] Camera position still NaN after 10 retries — view may not support direct positioning');
    }
    inst?.notifyChange?.();
  }

  private focusCameraOnObject(object: unknown): void {
    try {
      const inst = this.instance as any;
      const obj = object as any;
      if (!inst || !obj || typeof obj.traverse !== 'function') return;

      const camera = inst.view?.camera ?? inst.camera?.camera3D;
      if (!camera) return;

      let minX = Infinity, minY = Infinity, minZ = Infinity;
      let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
      let hasGeom = false;

      obj.traverse((child: any) => {
        if (child.geometry) {
          child.geometry.computeBoundingBox?.();
          const bb = child.geometry.boundingBox;
          if (bb) {
            child.updateWorldMatrix(true, false);
            const m = child.matrixWorld;
            const corners = [
              [bb.min.x, bb.min.y, bb.min.z], [bb.max.x, bb.min.y, bb.min.z],
              [bb.min.x, bb.max.y, bb.min.z], [bb.max.x, bb.max.y, bb.min.z],
              [bb.min.x, bb.min.y, bb.max.z], [bb.max.x, bb.min.y, bb.max.z],
              [bb.min.x, bb.max.y, bb.max.z], [bb.max.x, bb.max.y, bb.max.z],
            ];
            for (const [cx, cy, cz] of corners) {
              const wx = m.elements[0]*cx + m.elements[4]*cy + m.elements[8]*cz + m.elements[12];
              const wy = m.elements[1]*cx + m.elements[5]*cy + m.elements[9]*cz + m.elements[13];
              const wz = m.elements[2]*cx + m.elements[6]*cy + m.elements[10]*cz + m.elements[14];
              minX = Math.min(minX, wx); maxX = Math.max(maxX, wx);
              minY = Math.min(minY, wy); maxY = Math.max(maxY, wy);
              minZ = Math.min(minZ, wz); maxZ = Math.max(maxZ, wz);
            }
            hasGeom = true;
          }
        }
      });

      if (!hasGeom) return;

      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      const cz = (minZ + maxZ) / 2;
      const dx = maxX - minX;
      const dy = maxY - minY;
      const dz = maxZ - minZ;
      const diagonal = Math.sqrt(dx*dx + dy*dy + dz*dz);
      if (!isFiniteVec3(cx, cy, cz) || !Number.isFinite(diagonal) || diagonal <= 0) return;
      const distance = diagonal * 1.5;
      if (!Number.isFinite(distance) || distance <= 0) return;

      camera.position.set(cx + distance * 0.6, cy + distance * 0.6, cz + distance * 0.6);
      camera.up?.set(0, 0, 1);
      camera.lookAt(cx, cy, cz);
      const nearPlane = Math.max(0.01, diagonal * 0.0005);
      const farPlane = Math.max(diagonal * 10, 1000);
      if (!Number.isFinite(nearPlane) || !Number.isFinite(farPlane) || farPlane <= nearPlane) return;
      camera.near = nearPlane;
      camera.far = farPlane;
      camera.updateProjectionMatrix?.();

      if (this.controls?.target) {
        this.controls.target.set(cx, cy, cz);
        this.controls.update?.();
      }

      console.log(
        `[Giro3DAdapter] focusCameraOnObject: center=(${cx.toFixed(1)}, ${cy.toFixed(1)}, ${cz.toFixed(1)}) diagonal=${diagonal.toFixed(1)}`,
      );

      inst.notifyChange?.();
    } catch (err) {
      console.warn('[Giro3DAdapter] focusCameraOnObject failed', err);
    }
  }

  /**
   * M8 — Apply overlay material override for 3D Tiles in overlays slot.
   * Replaces standard PBR materials with MeshBasicMaterial, enables transparency,
   * disables depth writing, and applies hazard color mapping from layer style.
   *
   * Also surfaces a one-time navigation hint per layer: PLATEAU overlay
   * tilesets (flood, high-tide, underground) only cover narrow geographic
   * extents (river basins, hazard zones, subway alignments). Users who toggle
   * them visible from a camera position outside that extent see nothing — the
   * data is there, just off-screen. Logging the hint encourages them to use
   * the Zoom button on the layer card.
   */
  private applyOverlayMaterialOverride(entity: any, layer: Layer): number {
    if (!entity || typeof entity.traverse !== 'function') return 0;

    // Re-entrancy guard — the WeakSet check prevents the entire mesh walk
    // from running again if the same entity has already been processed
    // (e.g. when a layer is toggled off then on without a remount). Walking
    // a fully-streamed tileset with thousands of meshes was a measurable
    // source of main-thread freezes during rapid Show/Hide cycles.
    if (this.overlayEntities.has(entity)) return 0;
    this.overlayEntities.add(entity);

    const layerStyle = resolveLayerStyle(layer.id, layer.label);
    const uniformColor = layerStyle ? getUniformColor(layerStyle) : '#3787ff';
    const opacity = layerStyle?.opacity ?? 0.85;

    let patched = 0;

    entity.traverse((child: any) => {
      if (!child.isMesh) return;
      if (this.patchedMeshes.has(child)) return;

      const material = child.material;
      if (!material) return;

      if (Array.isArray(material)) {
        for (let i = 0; i < material.length; i++) {
          material[i] = this.createOverlayMaterial(material[i], uniformColor, opacity);
        }
      } else {
        child.material = this.createOverlayMaterial(material, uniformColor, opacity);
      }

      child.renderOrder = 3;

      this.patchedMeshes.add(child);
      patched++;
    });

    if (patched > 0) {
      console.log(
        `[Giro3DAdapter] Overlay material applied to ${patched} mesh(es) on ${layer.label ?? layer.id}: `
        + `color=${uniformColor} opacity=${opacity}. `
        + `Coverage is typically a narrow geographic zone — use the Zoom button on the layer card if you don't see it from your current camera.`,
      );
      (this.instance as any)?.notifyChange?.();
    }

    return patched;
  }

  /**
   * M8 — Create overlay material from existing material.
   * Replaces PBR materials with MeshBasicMaterial for overlay semantics.
   */
  private createOverlayMaterial(originalMaterial: any, color: string, opacity: number): THREE.Material {
    const overlayMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    
    // Preserve texture if present (for textured hazard overlays)
    if (originalMaterial.map) {
      overlayMaterial.map = originalMaterial.map;
    }
    
    return overlayMaterial;
  }

  /**
   * Apply rendering fixes to all tile meshes: flat shading for hard industrial
   * edges, vertex colors from our GLB pipeline, DoubleSide for thin surfaces,
   * and tuned PBR parameters. Uses a WeakSet to skip already-processed meshes.
   * Returns the number of newly patched meshes.
   */
  private applyTileMaterialFixes(root: any): number {
    // Baseline mode: avoid runtime material patch traversal for native 3D
    // Tiles. The authored materials in b3dm content should render as-is.
    return 0;
    if (!root || typeof root.traverse !== 'function') return 0;
    
    // M8 — guard: skip Q1 material patching for overlay entities
    if (this.overlayEntities.has(root)) {
      return 0;
    }
    
    let patched = 0;

    root.traverse((child: any) => {
      if (!child.isMesh) return;
      if (this.patchedMeshes.has(child)) return;

      const geom = child.geometry;
      const mat = child.material;
      if (!geom || !mat) return;

      const hasVertexColors = !!(geom.attributes?.color || geom.attributes?.COLOR_0);

      if (Array.isArray(mat)) {
        for (const m of mat) {
          this.patchSingleMaterial(m, hasVertexColors, geom);
        }
      } else {
        this.patchSingleMaterial(mat, hasVertexColors, geom);
      }

      this.patchedMeshes.add(child);
      patched++;
    });

    if (patched > 0) {
      console.log(`[Giro3DAdapter] Patched ${patched} tile mesh(es): conditional material policy applied`);
      (this.instance as any)?.notifyChange?.();
    }
    return patched;
  }

  // patchSingleMaterial lives above attachPlateauTileMaterialFixes (delegates to plateauTileMaterials.ts).

  /** Attach a translucent face-fill highlight as a child of `mesh`. */
  private attachFaceHighlight(mesh: THREE.Mesh, facesToHighlight: Set<number>): boolean {
    const geom = mesh.geometry;
    const posAttr = geom?.attributes?.position as THREE.BufferAttribute | undefined;
    const indexArr = geom?.index?.array as ArrayLike<number> | undefined;

    if (
      !posAttr
      || facesToHighlight.size === 0
      || facesToHighlight.size > MAX_SELECTION_HIGHLIGHT_FACES
    ) {
      if (facesToHighlight.size > MAX_SELECTION_HIGHLIGHT_FACES) {
        console.warn(
          `[Giro3DAdapter] Selection highlight skipped: ${facesToHighlight.size} faces exceeds cap (${MAX_SELECTION_HIGHLIGHT_FACES})`,
        );
      }
      return false;
    }

    const positions: number[] = [];
    for (const fi of facesToHighlight) {
      for (let vi = 0; vi < 3; vi++) {
        const rawIdx = indexArr ? indexArr[fi * 3 + vi] : fi * 3 + vi;
        positions.push(
          posAttr.getX(rawIdx),
          posAttr.getY(rawIdx),
          posAttr.getZ(rawIdx),
        );
      }
    }
    const posArray = new Float32Array(positions);
    const hlGeom = new THREE.BufferGeometry();
    hlGeom.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
    const hlMat = new THREE.MeshBasicMaterial({
      color: 0xffeb3b,
      transparent: true,
      opacity: 0.75,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -10,
      polygonOffsetUnits: -10,
    });
    const hlMesh = new THREE.Mesh(hlGeom, hlMat);
    hlMesh.renderOrder = 999;
    hlMesh.frustumCulled = false;
    mesh.add(hlMesh);
    // Ensure the highlight has a valid world matrix immediately so a
    // subsequent fly-to can read its true world bounds (tile meshes may not
    // have been updated this frame yet).
    mesh.updateWorldMatrix(true, false);
    hlMesh.updateMatrixWorld(true);
    this.selectionHighlights.push(hlMesh);
    console.log(`[Giro3DAdapter] Selection highlight: ${facesToHighlight.size} faces on ${mesh.name}`);
    return true;
  }

  private notifySelectionRender(): void {
    const inst = this.instance as any;
    inst?.notifyChange?.();
    const pumpEnd = Date.now() + 2000;
    const pump = () => {
      inst?.notifyChange?.();
      if (Date.now() < pumpEnd) requestAnimationFrame(pump);
    };
    requestAnimationFrame(pump);
  }

  /** Highlight the selected component with a cyan bounding-box wireframe.
   *
   * Using a wireframe box instead of emissive avoids glowing the entire
   * batched PLATEAU tile (which contains many buildings). The box is built
   * from only the face indices that belong to the clicked building component
   * (isolated via batch-ID or flood-fill in componentPicker).
   */
  private selectObject(
    mesh: THREE.Mesh,
    addToSelection = false,
    componentFaces?: Set<number> | null,
  ): void {
    const featureId = this.lastPickedFeatureId;

    if (!addToSelection) {
      this.clearSelection();
    }

    if (featureId) {
      this.selectedFeatureIds.add(featureId);
    }
    this.selectedObjects.add(mesh);

    const facesToHighlight = (componentFaces && componentFaces.size > 0)
      ? componentFaces
      : null;

    if (facesToHighlight) {
      this.attachFaceHighlight(mesh, facesToHighlight);
    } else {
      console.warn('[Giro3DAdapter] No component faces — highlight skipped');
    }

    if (this.outlinePassManager) {
      this.outlinePassManager.setSelectedObjects([]);
    }

    // Force Giro3D to re-render the newly added highlight child meshes.
    // Tiles3D tile nodes can be aggressive with child culling; pump notifyChange
    // for a short burst and also request a tile refresh on the parent if possible.
    this.notifySelectionRender();

    // If the parent mesh belongs to a Tiles3D tile, nudge the tile to refresh
    const tile = (mesh as any).tilesRenderer || (mesh.parent as any)?.tilesRenderer;
    if (tile && typeof tile.markTileUsed === 'function') {
      try { tile.markTileUsed((mesh as any).tile); } catch { /* best-effort tile refresh */ }
    }
  }

  /** Clear any active selection highlight */
  clearSelection(): void {
    for (const hlMesh of this.selectionHighlights) {
      hlMesh.parent?.remove(hlMesh);
      hlMesh.geometry?.dispose();
      (hlMesh.material as THREE.Material)?.dispose?.();
    }
    this.selectionHighlights = [];

    // Clear outline pass selection
    this.selectedObjects.clear();
    this.selectedFeatureIds.clear();
    if (this.outlinePassManager) {
      this.outlinePassManager.setSelectedObjects([]);
    }

    this.lastPickedFeatureId = null;
    this.lastPickedFeatureSelection = null;
    this.lastPickedBatchTableProperties = null;
    (this.instance as any)?.notifyChange?.();
  }

  isolateNode(node: import('@/components/three-d/ThreeDModelToolCard').ModelTreeNode): void {
    const targets = collectModelNodeTargets(node);
    this.hiddenModelNodeIds.clear();
    this.applyModelNodeVisibility((candidate) => {
      const normalized = normalizeNodeToken(candidate);
      if (!normalized) return false;
      return targets.has(normalized);
    });
  }

  showAllNodes(): void {
    this.hiddenModelNodeIds.clear();
    this.applyModelNodeVisibility(() => true);
  }

  hideNode(node: import('@/components/three-d/ThreeDModelToolCard').ModelTreeNode): void {
    for (const token of collectModelNodeTargets(node)) {
      this.hiddenModelNodeIds.add(token);
    }
    this.applyModelNodeVisibility((candidate) => {
      const normalized = normalizeNodeToken(candidate);
      if (!normalized) return true;
      return !this.hiddenModelNodeIds.has(normalized);
    });
  }

  showNode(node: import('@/components/three-d/ThreeDModelToolCard').ModelTreeNode): void {
    for (const token of collectModelNodeTargets(node)) {
      this.hiddenModelNodeIds.delete(token);
    }
    this.applyModelNodeVisibility((candidate) => {
      const normalized = normalizeNodeToken(candidate);
      if (!normalized) return true;
      return !this.hiddenModelNodeIds.has(normalized);
    });
  }

  /**
   * Extract full batch table properties from a picked mesh for PLATEAU data.
   * This looks for 3D tiles batch table data in the tile's userData.
   */
  private extractBatchTableProperties(
    mesh: THREE.Mesh,
    _featureId: string | null,
    hitFaceIndex?: number | null
  ): Record<string, unknown> {
    const properties: Record<string, unknown> = {};

    // Look for batch table in mesh ancestors (3d-tiles-renderer stores it)
    let current: THREE.Object3D | null = mesh;
    while (current) {
      const userData = current.userData;
      
      // Check for 3d-tiles-renderer batch table
      if (userData?.batchTable) {
        const batchTable = userData.batchTable;
        if (typeof batchTable === 'object' && batchTable !== null) {
          // If we have a feature ID, try to get the batch index
          let batchIndex: number | null = null;
          
          // Try to get batch index from the batch-id attribute. The
          // 3d-tiles-renderer names it `_batchid` (lowercase); legacy/other
          // encoders use `_BATCHID` / `_BATCH_ID` / `batchId`. Check all.
          if (typeof hitFaceIndex === 'number' && mesh.geometry) {
            const batchAttr =
              mesh.geometry.attributes._batchid ??
              mesh.geometry.attributes['_batchid'] ??
              mesh.geometry.attributes._BATCHID ??
              mesh.geometry.attributes['_BATCHID'] ??
              mesh.geometry.attributes._BATCH_ID ??
              mesh.geometry.attributes['_BATCH_ID'] ??
              mesh.geometry.attributes.batchId ??
              mesh.geometry.attributes['batchId'];
            if (batchAttr?.array) {
              const indexArray = mesh.geometry.index?.array;
              let hitVertIdx: number;
              if (indexArray) {
                hitVertIdx = indexArray[hitFaceIndex * 3];
              } else {
                hitVertIdx = hitFaceIndex * 3;
              }
              batchIndex = (batchAttr.array as ArrayLike<number>)[hitVertIdx];
            }
          }
          
          // Extract properties from batch table
          if (typeof batchIndex === 'number' && batchIndex >= 0) {
            // Batch table format: { propertyName: [value1, value2, ...] }
            for (const [key, values] of Object.entries(batchTable)) {
              if (Array.isArray(values) && batchIndex < values.length) {
                properties[key] = values[batchIndex];
              }
            }
          } else {
            // Fallback: use first entry or single values
            for (const [key, value] of Object.entries(batchTable)) {
              if (Array.isArray(value) && value.length > 0) {
                properties[key] = value[0];
              } else if (!Array.isArray(value)) {
                properties[key] = value;
              }
            }
          }
        }
      }
      
      // Also check userData directly for properties
      if (userData?.properties) {
        Object.assign(properties, userData.properties);
      }
      
      if (userData?.attributes) {
        Object.assign(properties, userData.attributes);
      }

      // Check extras for geosoft metadata
      if (userData?.extras?.geosoft?.properties) {
        Object.assign(properties, userData.extras.geosoft.properties);
      }

      current = current.parent;
    }

    return properties;
  }

  private buildPickedFeatureSelection(
    mesh: THREE.Mesh,
    featureId: string | null,
    _hitFaceIndex?: number | null,
  ): import('@/components/three-d/ThreeDModelToolCard').ThreeDFeatureSelection {
    const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    const userData = asUnknownRecord(mesh.userData);
    const extras = asUnknownRecord(userData?.extras);
    const geosoft =
      asUnknownRecord(userData?.geosoft) ??
      asUnknownRecord(extras?.geosoft);
    const properties = normalizeFeatureProperties([
      userData?.properties,
      geosoft?.properties,
      extras?.properties,
      userData?.attributes,
      geosoft?.attributes,
      extras?.attributes,
    ]);
    const objectName =
      readStringField(userData, ['objectName', 'label', 'name']) ??
      readStringField(geosoft, ['objectName', 'label', 'name']) ??
      readStringField(extras, ['objectName', 'label', 'name']) ??
      (mesh.name || undefined) ??
      readStringField(asUnknownRecord(material), ['name']);
    const materialName =
      readStringField(userData, ['materialName']) ??
      readStringField(geosoft, ['materialName']) ??
      readStringField(extras, ['materialName']) ??
      readStringField(asUnknownRecord(material), ['name']);
    const resolvedFeatureId =
      featureId ??
      readStringField(userData, ['featureId']) ??
      readStringField(geosoft, ['featureId']) ??
      readStringField(extras, ['featureId']) ??
      null;
    const ifcGuidFromProperties = properties.find((prop) =>
      /ifcguid|ifc_guid|ifc guid/i.test(prop.key),
    )?.value;
    const ifcGuid =
      readStringField(userData, ['ifcGuid', 'ifcGUID', 'IfcGUID']) ??
      readStringField(geosoft, ['ifcGuid', 'ifcGUID', 'IfcGUID']) ??
      readStringField(extras, ['ifcGuid', 'ifcGUID', 'IfcGUID']) ??
      ifcGuidFromProperties ??
      resolvedFeatureId ??
      undefined;
    const nodeId =
      readStringField(userData, ['nodeId', 'indexNodeId', 'modelNodeId']) ??
      readStringField(geosoft, ['nodeId', 'indexNodeId', 'modelNodeId']) ??
      readStringField(extras, ['nodeId', 'indexNodeId', 'modelNodeId']) ??
      resolvedFeatureId ??
      undefined;
    return {
      featureId: resolvedFeatureId ?? undefined,
      ifcGuid,
      objectName,
      materialName,
      nodeId,
      properties: properties.length > 0 ? properties : undefined,
    };
  }

  /**
   * Switch the rendering mode across all loaded meshes.
   * - 'default': original vertex colors with PBR
   * - 'greyscale': flat grey tones, no color — reveals form and shape
   * - 'xray': semi-transparent with edge emphasis
   * - 'wireframe': wireframe overlay
   */
  setRenderMode(mode: RenderMode): void {
    this.renderMode = mode;
    const inst = this.instance as any;
    if (!inst?.scene) return;

    inst.scene.traverse((child: any) => {
      if (!child.isMesh || !child.material) return;
      const mat = child.material;

      // Save original material on first switch
      if (!this.originalMaterials.has(child)) {
        this.originalMaterials.set(child, mat.clone());
      }

      switch (mode) {
        case 'greyscale': {
          mat.vertexColors = false;
          mat.color?.setHex(0x888888);
          mat.metalness = 0.0;
          mat.roughness = 0.85;
          mat.transparent = false;
          mat.opacity = 1.0;
          mat.wireframe = false;
          break;
        }
        case 'xray': {
          mat.vertexColors = false;
          mat.color?.setHex(0x6699cc);
          mat.metalness = 0.0;
          mat.roughness = 0.5;
          mat.transparent = true;
          mat.opacity = 0.25;
          mat.wireframe = false;
          mat.depthWrite = false;
          break;
        }
        case 'wireframe': {
          mat.wireframe = true;
          mat.vertexColors = true;
          mat.transparent = false;
          mat.opacity = 1.0;
          break;
        }
        default: {
          // Restore from saved original
          const orig = this.originalMaterials.get(child);
          if (orig) {
            const o = orig as any;
            mat.vertexColors = o.vertexColors ?? true;
            mat.color?.copy(o.color ?? new THREE.Color(0xffffff));
            mat.metalness = o.metalness ?? 0.08;
            mat.roughness = o.roughness ?? 0.55;
            mat.transparent = false;
            mat.opacity = 1.0;
            mat.wireframe = false;
            mat.depthWrite = true;
          }
          break;
        }
      }
      mat.needsUpdate = true;
    });

    inst.notifyChange?.();
    console.log(`[Giro3DAdapter] Render mode: ${mode}`);
  }

  /** Get the current render mode */
  getRenderMode(): RenderMode { return this.renderMode; }

  // -------------------------------------------------------------------------
  // WP-6 — public API consumed by `<ThreeDModelToolCard />` and
  // `<SectionAndClipToolCard />`. Mirrors the controller helpers in
  // `cameraPresets.ts` and `clipController.ts` so the right-rail card
  // never reaches into Three.js directly.
  // -------------------------------------------------------------------------

  /** Apply a camera preset (Top, Front, Back, Right, Left, Iso, Fit). */
  setCameraPreset(preset: CameraPreset): boolean {
    // Section planes are scene-wide analysis tools: they must cut both
    // the engineering model and the point cloud in the same local frame.
    // Use combined bounds here; camera presets remain model-only via
    // getModelBounds() defaults.
    const bounds = this.getModelBounds({ includePointClouds: true });
    const target = computeCameraPresetTarget(preset, bounds);
    if (!target) return false;
    return applyCameraPresetTarget(this.cameraSurface(), target);
  }

  /** Re-frame the camera on the union of every loaded model entity. */
  fitToModel(): boolean {
    // Volume boxes are also scene-wide. Compute the box against the union
    // of model + point-cloud bounds so the default placement is centred
    // on all visible reality/model data, not just the model tile.
    const bounds = this.getModelBounds({ includePointClouds: true });
    if (!bounds) return false;
    const target = computeCameraPresetTarget('fit', bounds);
    return applyCameraPresetTarget(this.cameraSurface(), target);
  }

  /**
   * Apply a section plane (X / Y / Z axis-aligned, optional custom
   * normal). Pass `null` to clear the active plane. The implementation
   * mirrors the legacy toolbar behaviour: every traversed material gets
   * a single plane in its `clippingPlanes` array.
   */
  setSectionPlane(spec: SectionPlaneSpec | null): boolean {
    const inst = this.instance as any;
    if (!inst) return false;

    if (this.activeSectionPlane) {
      this.applyClipPlanesToScene(inst, []);
      this.activeSectionPlane = null;
    }

    if (!spec) {
      inst.notifyChange?.();
      return true;
    }

    const bounds = this.getModelBounds();
    const plane = computeClipPlane(spec, bounds);
    if (!plane) return false;

    void this.constructAndApplyClipPlane(inst, plane).then(() => {
      this.activeSectionPlane = spec;
    });
    return true;
  }

  /**
   * Apply a clipping box (six axis-aligned planes). Pass `null` to
   * clear it. Tightening to a selection is handled in pure helpers so
   * the right-rail card can compute the box and call this method once.
   */
  setClippingBox(box: ClippingBoxSpec | null): boolean {
    const inst = this.instance as any;
    if (!inst) return false;

    if (this.activeClippingBox) {
      this.applyClipPlanesToScene(inst, []);
      this.applyPotreeClipBox(null, null);
      this.activeClippingBox = null;
    }

    if (!box) {
      this.applyPotreeClipBox(null, null);
      inst.notifyChange?.();
      return true;
    }

    const bounds = this.getModelBounds();
    const planes = computeClippingBoxPlanes(box, bounds);
    if (!planes) return false;

    this.applyPotreeClipBox(box, bounds);
    void this.constructAndApplyClipPlanes(inst, planes).then(() => {
      this.activeClippingBox = box;
    });
    return true;
  }

  /** Coordinate mode of the active 3D model layer (for the badge). */
  getCoordinateMode(): 'georeferenced' | 'local' {
    const modelRecord = [...this.layers.values()].find((r) =>
      r.descriptor.kind === 'bim-gltf' || r.descriptor.kind === '3dtiles',
    );
    if (!modelRecord) return 'georeferenced';
    const style = modelRecord.layer.style as { coordinateMode?: unknown } | undefined;
    if (typeof style?.coordinateMode === 'string') {
      return style.coordinateMode === 'georeferenced' ? 'georeferenced' : 'local';
    }
    const crs = (modelRecord.layer.crs ?? '').trim().toLowerCase();
    if (crs === '' || crs === 'local' || crs === 'epsg:0') return 'local';
    return 'georeferenced';
  }

  private cameraSurface(): Giro3DCameraTarget | null {
    const inst = this.instance as any;
    if (!inst) return null;
    return {
      camera: inst.view?.camera ?? null,
      controls: this.controls
        ? { target: this.controls.target, update: () => this.controls.update?.() }
        : null,
      notifyChange: () => inst.notifyChange?.(),
    };
  }

  private async constructAndApplyClipPlane(inst: any, plane: ClipPlaneVector): Promise<void> {
    const planes = await this.materializePlanes([plane]);
    this.applyClipPlanesToScene(inst, planes);
  }

  private async constructAndApplyClipPlanes(inst: any, planes: ClipPlaneVector[]): Promise<void> {
    const materialized = await this.materializePlanes(planes);
    this.applyClipPlanesToScene(inst, materialized);
  }

  private async materializePlanes(planes: ClipPlaneVector[]): Promise<THREE.Plane[]> {
    return planes.map((plane) => new THREE.Plane(
      new THREE.Vector3(plane.normal[0], plane.normal[1], plane.normal[2]),
      plane.constant,
    ));
  }

  private applyClipPlanesToScene(inst: any, planes: THREE.Plane[]): void {
    const renderer = inst.renderer ?? inst.mainLoop?.gfxEngine?.renderer;
    if (renderer) renderer.localClippingEnabled = planes.length > 0;
    const scene = inst.scene;
    const applyToMaterial = (material: any) => {
      if (!material) return;
      if (Array.isArray(material)) {
        for (const entry of material) applyToMaterial(entry);
        return;
      }
      material.clippingPlanes = planes;
      material.clipShadows = planes.length > 0;
      material.needsUpdate = true;
      // potree-core's RawShaderMaterial uses defines for clipping
      // uniforms. Force a shader refresh on user-driven clip changes so
      // inherited clippingPlanes are synced into its internal uniforms
      // immediately rather than waiting for a later LOD update.
      material.updateShaderSource?.();
    };

    if (scene) {
      scene.traverse((child: any) => {
        if (child.material) applyToMaterial(child.material);
      });
    }
    // Potree-core point clouds keep their material on the octree entity
    // rather than on child meshes. Apply the same planes there too so
    // section/volume tools affect the point cloud as well as the model.
    for (const record of this.pointCloudRecords()) {
      const material = (record.entity as { material?: unknown } | undefined)?.material;
      applyToMaterial(material);
    }
    this.updatePotreePointClouds(performance.now(), true);
    inst.notifyChange?.();
  }

  /**
   * Apply Potree's native clip-box path. The point-cloud shader does not
   * obey Three.js mesh clipping in the same way regular model materials do;
   * it exposes `setClipBoxes()` and a `clipMode` enum instead. The box is
   * computed from the same combined-scene normalized box used by model
   * clipping, so volume-box movement/size affects model + cloud together.
   */
  private applyPotreeClipBox(
    box: ClippingBoxSpec | null,
    bounds: ModelBounds | null,
  ): void {
    for (const record of this.pointCloudRecords()) {
      const material = (record.entity as { material?: any } | undefined)?.material;
      if (!material) continue;
      if (!box || !bounds) {
        material.setClipBoxes?.([]);
        material.useClipBox = false;
        // ClipMode.DISABLED = 0 in potree-core.
        material.clipMode = 0;
        material.updateShaderSource?.();
        material.needsUpdate = true;
        continue;
      }
      const clipBox = buildPotreeClipBox(box, bounds);
      material.setClipBoxes?.([clipBox]);
      material.useClipBox = true;
      // ClipMode.CLIP_OUTSIDE = 1 in potree-core.
      material.clipMode = 1;
      material.updateShaderSource?.();
      material.needsUpdate = true;
    }
    this.updatePotreePointClouds(performance.now(), true);
  }

  /** Adjust key/fill/rim light intensities */
  setLighting(key: number, fill: number, rim: number, ambient: number): void {
    if (this.keyLight) this.keyLight.intensity = key;
    if (this.fillLight) this.fillLight.intensity = fill;
    if (this.rimLight) this.rimLight.intensity = rim;
    if (this.hemiLight) this.hemiLight.intensity = ambient;
    (this.instance as any)?.notifyChange?.();
  }

  async removeLayer(layerId: string): Promise<void> {
    if (layerId.startsWith('layer:environment:') && this.envController) {
      this.envController.setEnvironmentLayerVisible(layerId, false);
      this.layers.delete(layerId);
      return;
    }
    const inFlight = this.layerAddInFlight.get(layerId);
    if (inFlight) {
      this.desiredLayerVisibility.set(layerId, false);
      await inFlight;
    }
    const record = this.layers.get(layerId);
    if (record?.entity) {
      const ent = record.entity as any;
      if (record.nativeVector) {
        this.instance?.remove?.(record.entity);
        record.nativeVector.dispose();
        (this.instance as any)?.notifyChange?.();
      } else if (ent.name === 'direct-tiles-container') {
        this.instance?.remove?.(ent);
        ent.traverse?.((child: any) => {
          child.geometry?.dispose?.();
          if (child.material) {
            if (Array.isArray(child.material)) child.material.forEach((m: any) => m.dispose?.());
            else child.material.dispose?.();
          }
        });
        (this.instance as any)?.notifyChange?.();
      } else if (record.descriptor.attachTo === 'instance-object') {
        this.instance?.remove?.(record.entity);
      } else {
        this.map?.removeLayer?.(record.entity, { disposeLayer: true });
      }
    }
    // G20 — tear down the paired hypsometric color drape with its terrain layer.
    const drape = this.terrainColorDrapes.get(layerId);
    if (drape) {
      try {
        this.map?.removeLayer?.(drape, { disposeLayer: true });
      } catch (err) {
        console.warn(`[Giro3DAdapter] failed to remove terrain color drape for ${layerId}:`, err);
      }
      this.terrainColorDrapes.delete(layerId);
      (this.instance as any)?.notifyChange?.();
    }
    // G20 — drop the persisted ramp/visibility for a fully removed terrain layer
    // so a later re-add starts from defaults (drape re-mount handles re-stream).
    this.terrainColorRamp.delete(layerId);
    this.layerVisible.delete(layerId);
    if (record && record.descriptor.kind === 'copc-pointcloud') {
      const key = copcGroupKey(record.layer);
      const groupHasOthers = [...this.layers.values()].some(
        (other) => other !== record
          && other.descriptor.kind === 'copc-pointcloud'
          && copcGroupKey(other.layer) === key,
      );
      if (!groupHasOthers) this.fittedCopcGroups.delete(key);
    }
    this.layers.delete(layerId);
    this.failedLayerIds.delete(layerId);
    this.tileProgressByLayer.delete(layerId);
    this.republishAggregateTileProgress('done');
    this.reconcileRendererToneMapping();
  }

  flyTo(camera: ViewerCamera): void {
    this.camera = camera;
    this.host?.emit({ type: 'cameraChange', camera });

    // For BIM layers, fly to the first loaded model
    const bimRecord = [...this.layers.values()].find(r => r.descriptor.kind === 'bim-gltf');
    if (bimRecord?.entity) {
      this.focusCameraOnObject(bimRecord.entity);
      return;
    }

    // G18 — terrain / raster / vector scenes render in the Web Mercator frame.
    // Move the live Three.js camera + orbit target to the requested geodetic
    // pose so saved-view / deep-link / refresh restores actually reposition the
    // view. Previously this was a no-op for non-BIM layers, which left the
    // camera at the OrbitControls default (null island) and produced a blank
    // screen after a refresh.
    if (this.applyViewerCamera(camera)) {
      return;
    }

    // Controls / Giro3D view not ready yet — retry briefly before giving up.
    let attempts = 0;
    const retry = () => {
      attempts += 1;
      if (this.applyViewerCamera(camera)) {
        return;
      } else if (attempts < 20) {
        setTimeout(retry, 150);
      }
    };
    setTimeout(retry, 150);
  }

  /**
   * Position the live camera + orbit target from a geodetic `ViewerCamera`
   * (lon/lat degrees, optional metric `height`, optional `hpr`).
   *
   * Only supported in the Web Mercator (EPSG:3857) render frame, which is the
   * frame used by terrain/raster/basemap scenes. Returns `false` when the view
   * or controls are not ready yet (the caller retries) or when the frame is a
   * custom display CRS where geodetic positioning is not meaningful.
   *
   * The reconstruction mirrors {@link readCurrentViewerCamera} so a
   * read → flyTo round-trip is stable: the orbit target is projected onto the
   * scene ground plane along the saved heading/pitch.
   */
  private applyViewerCamera(camera: ViewerCamera): boolean {
    if (this.activeFrameCrs !== 'EPSG:3857') return false;
    const inst = this.instance as any;
    const cam = inst?.view?.camera ?? inst?.camera?.camera3D;
    if (!cam || !this.controls?.target) return false;

    const lonLat = camera.lonLat;
    if (!Array.isArray(lonLat) || lonLat.length < 2) return false;
    const [lon, lat] = lonLat;
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;
    // Reject the placeholder null-island pose — never restore to [0,0].
    if (isNullIslandLonLat(lon, lat)) return false;

    const toMercatorX = (l: number) => (l / 180) * 20037508.342789244;
    const toMercatorY = (la: number) => {
      const rad = (la * Math.PI) / 180;
      const y = Math.log(Math.tan(Math.PI / 4 + rad / 2));
      return (y / Math.PI) * 20037508.342789244;
    };

    const px = toMercatorX(lon);
    const py = toMercatorY(lat);
    if (!Number.isFinite(px) || !Number.isFinite(py)) return false;

    const sceneZ = this.resolveSceneElevationForFlyTo();
    let pz = Number.isFinite(camera.height as number)
      ? (camera.height as number)
      : sceneZ + 3000;
    // Keep the camera above the ground plane so the look-down geometry is valid.
    if (pz <= sceneZ + 1) pz = sceneZ + Math.max(50, Math.abs(pz - sceneZ));

    // Reconstruct the orbit target from heading/pitch, assuming it sits on the
    // scene ground plane (matches readCurrentViewerCamera's hpr derivation).
    let heading = 0;
    let pitch = -45;
    const hpr = camera.hpr;
    if (Array.isArray(hpr) && hpr.length >= 2
      && Number.isFinite(hpr[0]) && Number.isFinite(hpr[1])) {
      heading = hpr[0];
      pitch = hpr[1];
    }
    let pitchRad = (pitch * Math.PI) / 180;
    // Clamp away from the horizon so the ground intersection stays finite.
    const maxPitchRad = (-3 * Math.PI) / 180;
    if (pitchRad > maxPitchRad) pitchRad = (-45 * Math.PI) / 180;
    const headingRad = (heading * Math.PI) / 180;
    const dz = sceneZ - pz; // negative — camera looks down toward the ground
    const dist = dz / Math.sin(pitchRad); // positive distance to the target
    const horiz = dist * Math.cos(pitchRad);
    const tx = px + horiz * Math.sin(headingRad);
    const ty = py + horiz * Math.cos(headingRad);
    const tz = sceneZ;
    if (!isFiniteVec3(tx, ty, tz)) return false;

    cam.position.set(px, py, pz);
    cam.up?.set(0, 0, 1);
    cam.near = 0.1;
    cam.far = 10_000_000;
    cam.lookAt(tx, ty, tz);
    cam.updateProjectionMatrix?.();
    this.controls.target.set(tx, ty, tz);
    this.controls.update?.();
    inst?.notifyChange?.();

    console.log(
      `[Giro3DAdapter] flyTo(pose): pos=(${px.toFixed(1)}, ${py.toFixed(1)}, ${pz.toFixed(1)}) ` +
      `target=(${tx.toFixed(1)}, ${ty.toFixed(1)}, ${tz.toFixed(1)}) heading=${heading.toFixed(1)} pitch=${pitch.toFixed(1)}`,
    );
    return true;
  }

  /**
   * G1 fix — debounced emission of cameraChange after user orbit/pan/zoom ends.
   * Converts the Three.js camera pose to geodetic ViewerCamera and emits through
   * the host so TwinShell's store stays current for saved views and share links.
   */
  private emitCameraChangeDebounced(): void {
    if (this.cameraChangeDebounceTimer != null) {
      clearTimeout(this.cameraChangeDebounceTimer);
    }
    this.cameraChangeDebounceTimer = setTimeout(() => {
      this.cameraChangeDebounceTimer = null;
      const viewerCam = this.readCurrentViewerCamera();
      if (viewerCam) {
        this.camera = viewerCam;
        console.log('[G1] cameraChange →', viewerCam);
        this.host?.emit({ type: 'cameraChange', camera: viewerCam });
      }
    }, 300);
  }

  /**
   * Convert the live Three.js camera + controls to a geodetic ViewerCamera.
   * Supports EPSG:3857 (inverse Web Mercator) and falls back to the project
   * anchor for other render frames (custom displayCrs).
   */
  private readCurrentViewerCamera(): ViewerCamera | null {
    const inst = this.instance as any;
    const cam: THREE.PerspectiveCamera | undefined =
      inst?.view?.camera ?? inst?.camera?.camera3D;
    if (!cam) return null;
    const pos = cam.position;
    if (!Number.isFinite(pos.x) || !Number.isFinite(pos.y) || !Number.isFinite(pos.z)) return null;
    const target = this.controls?.target as THREE.Vector3 | undefined;
    const center = target && Number.isFinite(target.x) && Number.isFinite(target.y) && Number.isFinite(target.z)
      ? target
      : pos;

    let lon: number;
    let lat: number;
    const height = pos.z;

    if (this.activeFrameCrs === 'EPSG:3857') {
      // Use the orbit target / look-at point as the viewer centre. Using the
      // camera position itself makes the MapLibre PMTiles overlay drift out of
      // view in oblique 3D poses: the camera can sit far away from the object it
      // is looking at, while the overlay must center on the look-at point.
      lon = (center.x / 20037508.342789244) * 180;
      const expArg = (center.y / 20037508.342789244) * Math.PI;
      lat = (2 * Math.atan(Math.exp(expArg)) - Math.PI / 2) * (180 / Math.PI);
    } else if (this.projectEcefAnchor) {
      lon = this.projectEcefAnchor.lon;
      lat = this.projectEcefAnchor.lat;
    } else {
      return null;
    }

    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    // OrbitControls default before the first fly — never emit null island.
    if (isNullIslandLonLat(lon, lat)) return null;

    let hpr: [number, number, number] | undefined;
    if (target && Number.isFinite(target.x) && Number.isFinite(target.y) && Number.isFinite(target.z)) {
      const dx = target.x - pos.x;
      const dy = target.y - pos.y;
      const dz = target.z - pos.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist > 0) {
        const heading = Math.atan2(dx, dy) * (180 / Math.PI);
        const pitch = Math.asin(dz / dist) * (180 / Math.PI);
        hpr = [heading, pitch, 0];
      }
    }

    return { lonLat: [lon, lat], height, hpr };
  }

  /**
   * Apply opacity to a single Giro3D map-layer entity (ColorLayer / ElevationLayer).
   *
   * Giro3D's `ColorLayer.opacity` setter invalidates the owning Map so the tile
   * renderer re-blends next frame; some versions expose `setOpacity()` instead.
   * Returns whether an opacity setter was found. Used for both the terrain entity
   * and its paired hypsometric color drape (gap G20).
   */
  private applyMapLayerEntityOpacity(entity: any, clamped: number): boolean {
    if (!entity) return false;
    try {
      if ('opacity' in entity) {
        entity.opacity = clamped;
        return true;
      }
    } catch {
      /* fall through to alternative paths */
    }
    if (typeof entity.setOpacity === 'function') {
      try {
        entity.setOpacity(clamped);
        return true;
      } catch {
        /* ignore */
      }
    }
    return false;
  }

  /**
   * Apply visibility to a single Giro3D map-layer entity (ColorLayer /
   * ElevationLayer). Mirrors the original `setLayerVisible` map-layer logic so
   * the terrain entity AND its paired hypsometric drape stay in lock-step.
   * Falls back to opacity (respecting the stored slider value on show) when the
   * entity has no `visible` setter. Returns whether a setter was found.
   */
  private applyMapLayerEntityVisibility(entity: any, visible: boolean, layerId: string): boolean {
    if (!entity) return false;
    try {
      if ('visible' in entity) {
        entity.visible = visible;
        return true;
      }
    } catch {
      /* fall through to opacity fallback */
    }
    if ('opacity' in entity) {
      try {
        entity.opacity = visible ? (this.layerOpacity.get(layerId) ?? 1) : 0;
        return true;
      } catch {
        /* ignore */
      }
    }
    return false;
  }

  setLayerOpacity?(layerId: string, opacity: number): void {
    if (layerId.startsWith('layer:environment:') && this.envController) {
      this.envController.setEnvironmentLayerOpacity(layerId, Math.max(0, Math.min(1, opacity)));
      return;
    }
    const record = this.layers.get(layerId);
    if (!record?.entity) return;
    const clamped = Math.max(0, Math.min(1, opacity));
    this.layerOpacity.set(layerId, clamped);
    const entity = record.entity as any;

    // Map-layer ColorLayer / ElevationLayer: set the layer's `opacity`
    // directly. Giro3D's ColorLayer.opacity setter invalidates the Map
    // entity which causes the tile renderer to re-blend on next frame.
    if (record.descriptor.attachTo === 'map-layer') {
      const applied = this.applyMapLayerEntityOpacity(entity, clamped);
      // G20: for a true-3D terrain the visible colour comes from the paired
      // hypsometric ColorLayer *drape*, not the height-only ElevationLayer —
      // applying opacity to the ElevationLayer alone leaves the slider looking
      // dead. Apply it to the drape too so the visible surface fades.
      const drape = this.terrainColorDrapes.get(layerId);
      if (drape) this.applyMapLayerEntityOpacity(drape, clamped);
      // Some Map wrappers expose a `material` whose uniforms control alpha.
      const mapAny = this.map as any;
      if (mapAny && typeof mapAny.invalidate === 'function') {
        try {
          mapAny.invalidate();
        } catch {
          /* ignore */
        }
      }
      (this.instance as any)?.notifyChange?.();
      console.log(`[Giro3DAdapter] setLayerOpacity (map-layer) ${layerId} → ${clamped}${applied ? '' : ' (no opacity setter)'}${drape ? ' (+drape)' : ''}`);
      return;
    }

    // Instance-object (3D Tiles / BIM / point cloud): walk loaded meshes
    // now. Streaming 3D Tiles will pick up the same opacity via the
    // `load-model` hook in attachPlateauTileMaterialFixes.
    this.applyEntityOpacity(entity, clamped);
    (this.instance as any)?.notifyChange?.();
    console.log(`[Giro3DAdapter] setLayerOpacity (instance) ${layerId} → ${clamped}`);
  }

  setNativeVectorStyle(layerId: string, patch: NativeVectorStylePatch): void {
    const record = this.layers.get(layerId);
    if (!record?.nativeVector) return;
    record.nativeVector.applyStylePatch(patch);
    (this.instance as any)?.notifyChange?.();
  }

  setEnvironmentParameter(layerId: string, key: string, value: unknown): void {
    if (this.envController) {
      this.envController.setEnvironmentParameter(layerId, key, value);
    }
  }

  /**
   * Wave-2 A4 — reorder a rendered layer up/down.
   * Returns `true` when a move was applied.
   */
  setLayerOrder(layerId: string, direction: -1 | 1): boolean {
    const record = this.layers.get(layerId);
    if (!record?.entity) return false;

    if (record.descriptor.attachTo === 'map-layer') {
      const mapAny = this.map as any;
      if (mapAny && typeof mapAny.moveLayer === 'function') {
        const mapLayers = Array.isArray(mapAny.layers) ? mapAny.layers : [];
        const current = mapLayers.indexOf(record.entity);
        const target = current + direction;
        if (current >= 0 && target >= 0 && target < mapLayers.length) {
          try {
            mapAny.moveLayer(record.entity, target);
            (this.instance as any)?.notifyChange?.();
            return true;
          } catch (err) {
            console.warn('[Giro3DAdapter] setLayerOrder map-layer move failed', layerId, err);
            return false;
          }
        }
      }
      return false;
    }

    const entity = record.entity as { renderOrder?: number };
    if (typeof entity.renderOrder !== 'number' || !Number.isFinite(entity.renderOrder)) {
      entity.renderOrder = 0;
    }
    entity.renderOrder = Math.max(0, entity.renderOrder + direction);
    (this.instance as any)?.notifyChange?.();
    return true;
  }

  /**
   * Wave-1 B1 — adjust brightness / contrast / saturation on a raster
   * `ColorLayer`. Each property is clamped to `0..2` (1 = unchanged).
   * Silently no-ops for non-raster layers so callers can fan the same
   * filter event to every engine adapter.
   */
  setRasterFilter(
    layerId: string,
    filter: { brightness?: number; contrast?: number; saturation?: number },
  ): void {
    const record = this.layers.get(layerId);
    if (!record?.entity) return;
    if (record.descriptor.attachTo !== 'map-layer') return;
    const colorLayer = record.entity as any;
    const clamp = (v: number | undefined): number => {
      if (typeof v !== 'number' || !Number.isFinite(v)) return 1;
      return Math.max(0, Math.min(2, v));
    };
    try {
      if (filter.brightness !== undefined) colorLayer.brightness = clamp(filter.brightness);
      if (filter.contrast !== undefined) colorLayer.contrast = clamp(filter.contrast);
      if (filter.saturation !== undefined) colorLayer.saturation = clamp(filter.saturation);
    } catch (err) {
      console.warn('[Giro3DAdapter] setRasterFilter failed', layerId, err);
      return;
    }
    (this.instance as any)?.notifyChange?.();
  }

  /**
   * Wave-1 A7 — return a coarse health classification for a layer the
   * adapter knows about. Used by the SceneTreePanel to render a green /
   * amber / red / grey dot per card.
   *
   *   - `failed`       : the adapter rejected `addLayer` for this id, or a
   *                      typed COPC-unsupported error fired.
   *   - `unsupported`  : the layer kind is supported but the runtime is
   *                      missing (e.g. COPCSource import unavailable).
   *   - `loading`      : the adapter accepted the layer but the entity
   *                      hasn't fully attached / streamed yet.
   *   - `ready`        : the entity is mounted and rendering.
   */
  getLayerHealth(layerId: string): 'ready' | 'loading' | 'failed' | 'unsupported' {
    if (layerId.startsWith('layer:environment:')) return 'ready';
    if (this.failedLayerIds.has(layerId)) return 'failed';
    const record = this.layers.get(layerId);
    if (!record) return 'loading';
    if (record.health === 'unsupported') return 'unsupported';
    if (record.health === 'failed') return 'failed';
    if (record.health === 'pending') return 'loading';
    if (!record.entity) return 'loading';
    return 'ready';
  }

  /**
   * Live render diagnostics for the global Layer Properties console
   * (`LayerDiagnosticsProvider`). Returns `null` for layers this adapter
   * neither owns nor recognises, so the `ViewerHost` aggregator can fall
   * through to another adapter. MUST stay side-effect free.
   *
   * Layer-KIND aware only: every branch keys off the resolved descriptor
   * (`attachTo`) or the environment-controller id convention — never a
   * scene-specific layer id.
   */
  getLayerRenderDiagnostics(layerId: string): LayerRenderDiagnostics | null {
    // Environment layers are delegated to the environment controller and have
    // no GiroLayerRecord, but Giro3D still owns their render path.
    if (layerId.startsWith('layer:environment:')) {
      return {
        adapterId: this.id,
        renderPath: 'environment-controller',
        mounted: true,
        engineVisible: null,
        frameCrs: this.activeFrameCrs,
        alignmentStatus: 'not-applicable',
        health: this.getLayerHealth(layerId),
        lastError: null,
      };
    }

    const record = this.layers.get(layerId);
    if (!record) return null; // unknown to Giro3D — let another adapter answer.

    const attachTo = record.descriptor.attachTo;
    const renderPath: RenderPathKind =
      record.descriptor.kind === 'native-vector-pmtiles'
        ? 'giro3d-native-vector'
        : attachTo === 'instance-object' ? 'giro3d-instance-object' : 'giro3d-map-layer';

    const mounted = Boolean(record.entity);
    const health = this.getLayerHealth(layerId);

    // Map-layers honour `layerVisible`; instance-objects honour the latest
    // desired-visibility request. Default to visible once mounted.
    const engineVisible =
      this.layerVisible.get(layerId) ??
      this.desiredLayerVisibility.get(layerId) ??
      (mounted ? true : null);

    // The descriptor id is the ColorLayer name (map-layer) or scene object
    // name (instance-object). Surface the paired hypsometric drape when present.
    const ids = [record.descriptor.id];
    if (this.terrainColorDrapes.has(layerId)) ids.push(`${record.descriptor.id}::hypso`);

    const alignment = record.alignment;
    let alignmentStatus: LayerAlignmentStatus;
    if (alignment?.rejected) alignmentStatus = 'rejected';
    else if (this.activeFrameCrs.toUpperCase() !== 'EPSG:3857') alignmentStatus = 'frame-switched';
    else if (alignment) alignmentStatus = 'aligned';
    else alignmentStatus = 'not-applicable';

    const lastError =
      alignment?.rejected?.reason ??
      (this.failedLayerIds.has(layerId) ? (record.hint ?? 'Layer failed to render') : null);

    const nativeVectorMetrics: NativePmtilesVectorMetrics | null =
      record.nativeVector?.getMetrics() ?? null;

    // 3D model metadata for tilesets and BIM
    let modelMeta: Partial<LayerRenderDiagnostics> = {};
    const isModelKind = record.descriptor.kind === '3dtiles'
      || record.descriptor.kind === 'bim-gltf'
      || record.descriptor.kind === 'pointcloud-tiles3d';
    if (isModelKind) {
      const worldBounds = record.entity ? this.computeEntityWorldBounds(record.entity) : null;
      const wbArr = worldBounds
        ? [worldBounds.min.x, worldBounds.min.y, worldBounds.min.z,
           worldBounds.max.x, worldBounds.max.y, worldBounds.max.z]
        : null;

      const compression = this.detectModelCompression(record);
      const objCount = record.entity ? this.countEntityObjects(record.entity) : null;
      const triCount = record.entity ? this.countEntityTriangles(record.entity) : null;

      modelMeta = {
        modelRootUrl: record.descriptor.url,
        worldBounds: wbArr,
        alignmentTransform: alignment?.matrix ?? null,
        modelSourceCrs: record.layer.sourceCrs ?? null,
        compression: compression.length > 0 ? compression : null,
        upAxis: this.detectUpAxis(record),
        tilesetRootGeometricError: (record.entity as any)?.root?.geometricError ?? null,
        tileCount: (record.entity as any)?.tiles?.stats?.loaded ?? null,
        objectCount: objCount,
        triangleCount: triCount,
      };
    }

    // Point-cloud-specific diagnostics
    let pcMeta: Partial<LayerRenderDiagnostics> = {};
    const isPcKind = record.descriptor.kind === 'copc-pointcloud'
      || record.descriptor.kind === 'potree-pointcloud'
      || record.descriptor.kind === 'pointcloud-tiles3d';
    if (isPcKind && mounted) {
      const entity = record.entity as any;
      const quality = this.pointCloudQuality;
      pcMeta = {
        pointBudget: quality.pointBudget,
        pointSize: quality.pointSize,
        pointCloudColorMode: quality.colorMode,
        displayedPointCount: typeof entity?.progress?.displayedCount === 'number'
          ? entity.progress.displayedCount
          : null,
        totalPointCount: typeof entity?.pointCount === 'number'
          ? entity.pointCount
          : null,
      };
    }

    return {
      adapterId: this.id,
      renderPath,
      mounted,
      engineVisible,
      ...(attachTo === 'instance-object'
        ? { engineObjectIds: ids }
        : { engineLayerIds: ids }),
      frameCrs: this.activeFrameCrs,
      appliedScale: alignment?.appliedScale ?? null,
      alignmentStatus,
      selectedSourceLayer: nativeVectorMetrics?.sourceLayer.applied ?? undefined,
      declaredSourceLayer: nativeVectorMetrics?.sourceLayer.declared ?? undefined,
      availableSourceLayers: nativeVectorMetrics?.sourceLayer.available,
      pmtilesArchive: nativeVectorMetrics?.archive ?? undefined,
      decodedTileCount: nativeVectorMetrics?.decodedTileCount ?? undefined,
      renderedFeatureCount: nativeVectorMetrics?.renderedFeatureCount ?? undefined,
      lastTileError: nativeVectorMetrics?.lastTileError ?? undefined,
      renderFrameAssumptions: nativeVectorMetrics?.renderFrameAssumptions,
      health,
      lastError,
      ...modelMeta,
      ...pcMeta,
    };
  }

  private detectModelCompression(record: { descriptor: Giro3DLayerEntityDescriptor; entity?: unknown }): string[] {
    const methods: string[] = [];
    if (!record.entity) return methods;
    const ent = record.entity as { traverse?: (cb: (child: unknown) => void) => void };
    if (typeof ent?.traverse !== 'function') return methods;
    let hasMeshopt = false;
    let hasDraco = false;
    ent.traverse((child: unknown) => {
      const mesh = child as { geometry?: { userData?: Record<string, unknown>; attributes?: Record<string, unknown> } };
      if (!mesh.geometry) return;
      if (mesh.geometry.userData?.['meshoptDecoder'] || mesh.geometry.userData?.['meshopt']) hasMeshopt = true;
      if (mesh.geometry.userData?.['draco'] || mesh.geometry.userData?.['dracoDecode']) hasDraco = true;
    });
    if (hasMeshopt) methods.push('meshopt');
    if (hasDraco) methods.push('draco');
    const style = record.descriptor.style as Record<string, unknown> | undefined;
    if (style?.compression) {
      const c = String(style.compression).toLowerCase();
      if (c.includes('meshopt') && !methods.includes('meshopt')) methods.push('meshopt');
      if (c.includes('draco') && !methods.includes('draco')) methods.push('draco');
    }
    return methods;
  }

  private detectUpAxis(record: { descriptor: Giro3DLayerEntityDescriptor; layer: Layer }): string | null {
    const style = record.layer.style as Record<string, unknown> | undefined;
    if (typeof style?.upAxis === 'string') return style.upAxis;
    if (record.layer.sourceCrs === 'EPSG:4978') return 'ECEF';
    return null;
  }

  private countEntityObjects(entity: unknown): number | null {
    const ent = entity as { traverse?: (cb: (child: unknown) => void) => void };
    if (typeof ent?.traverse !== 'function') return null;
    let count = 0;
    ent.traverse((child: unknown) => {
      const mesh = child as { geometry?: unknown };
      if (mesh.geometry) count++;
    });
    return count;
  }

  private countEntityTriangles(entity: unknown): number | null {
    const ent = entity as { traverse?: (cb: (child: unknown) => void) => void };
    if (typeof ent?.traverse !== 'function') return null;
    let triangles = 0;
    ent.traverse((child: unknown) => {
      const mesh = child as { geometry?: { index?: { count: number }; attributes?: { position?: { count: number } } } };
      if (!mesh.geometry) return;
      if (mesh.geometry.index) {
        triangles += Math.floor(mesh.geometry.index.count / 3);
      } else if (mesh.geometry.attributes?.position) {
        triangles += Math.floor(mesh.geometry.attributes.position.count / 3);
      }
    });
    return triangles;
  }

  /**
   * Compute world-space (scene-space) bounding box of an Object3D tree.
   * Returns null when there is no renderable geometry yet (e.g. tileset
   * still streaming and no tiles have attached).
   */
  private computeEntityWorldBounds(entity: unknown): {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
  } | null {
    const roots = this.resolveEntityObject3DRoots(entity);
    if (roots.length === 0) return null;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    let hasGeom = false;
    for (const root of roots) {
      root.traverse((child: unknown) => {
        const node = child as {
          geometry?: { computeBoundingBox?: () => void; boundingBox?: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } } };
          updateWorldMatrix?: (a: boolean, b: boolean) => void;
          matrixWorld?: { elements: number[] };
        };
        if (!node.geometry) return;
        node.geometry.computeBoundingBox?.();
        const bb = node.geometry.boundingBox;
        if (!bb || !node.matrixWorld) return;
        node.updateWorldMatrix?.(true, false);
        const m = node.matrixWorld.elements;
        const corners: Array<[number, number, number]> = [
          [bb.min.x, bb.min.y, bb.min.z], [bb.max.x, bb.min.y, bb.min.z],
          [bb.min.x, bb.max.y, bb.min.z], [bb.max.x, bb.max.y, bb.min.z],
          [bb.min.x, bb.min.y, bb.max.z], [bb.max.x, bb.min.y, bb.max.z],
          [bb.min.x, bb.max.y, bb.max.z], [bb.max.x, bb.max.y, bb.max.z],
        ];
        for (const [cx, cy, cz] of corners) {
          const wx = m[0]*cx + m[4]*cy + m[8]*cz + m[12];
          const wy = m[1]*cx + m[5]*cy + m[9]*cz + m[13];
          const wz = m[2]*cx + m[6]*cy + m[10]*cz + m[14];
          if (!Number.isFinite(wx) || !Number.isFinite(wy) || !Number.isFinite(wz)) continue;
          minX = Math.min(minX, wx); maxX = Math.max(maxX, wx);
          minY = Math.min(minY, wy); maxY = Math.max(maxY, wy);
          minZ = Math.min(minZ, wz); maxZ = Math.max(maxZ, wz);
          hasGeom = true;
        }
      });
    }
    if (!hasGeom) return null;
    return { min: { x: minX, y: minY, z: minZ }, max: { x: maxX, y: maxY, z: maxZ } };
  }

  private resolveEntityObject3DRoots(entity: unknown): THREE.Object3D[] {
    const roots: THREE.Object3D[] = [];
    const add = (candidate: unknown) => {
      if (candidate instanceof THREE.Object3D && !roots.includes(candidate)) roots.push(candidate);
    };
    add(entity);
    const shaped = entity as {
      tiles?: { group?: THREE.Object3D };
      group?: THREE.Object3D;
      object3d?: THREE.Object3D;
      object3D?: THREE.Object3D;
    } | undefined;
    add(shaped?.tiles?.group);
    add(shaped?.group);
    add(shaped?.object3d);
    add(shaped?.object3D);
    return roots;
  }

  /** Animate the camera to look at a scene-space bounding box. */
  private animateCameraToBounds(
    bounds: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } },
  ): boolean {
    const inst = this.instance as any;
    const camera = inst?.view?.camera ?? inst?.camera?.camera3D;
    if (!camera || !this.controls) return false;
    const cx = (bounds.min.x + bounds.max.x) / 2;
    const cy = (bounds.min.y + bounds.max.y) / 2;
    const cz = (bounds.min.z + bounds.max.z) / 2;
    const dx = bounds.max.x - bounds.min.x;
    const dy = bounds.max.y - bounds.min.y;
    const dz = bounds.max.z - bounds.min.z;
    const diagonal = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (!Number.isFinite(diagonal) || diagonal <= 0) return false;
    const distance = Math.max(diagonal * 0.9, 80);
    const startTarget = this.controls.target.clone();
    const startPos = camera.position.clone();
    const TargetCtor = startTarget.constructor as new (x: number, y: number, z: number) => typeof startTarget;
    const PosCtor = startPos.constructor as new (x: number, y: number, z: number) => typeof startPos;
    const endTarget = new TargetCtor(cx, cy, cz);
    const endPos = new PosCtor(
      cx + distance * 0.6,
      cy + distance * 0.6,
      cz + distance * 0.6,
    );
    const duration = 600;
    const start = performance.now();
    const ease = (t: number) => 0.5 - 0.5 * Math.cos(Math.PI * t);
    const step = () => {
      const now = performance.now();
      const t = Math.min(1, (now - start) / duration);
      const k = ease(t);
      camera.position.lerpVectors(startPos, endPos, k);
      this.controls!.target.lerpVectors(startTarget, endTarget, k);
      this.controls!.update?.();
      camera.updateProjectionMatrix?.();
      inst?.notifyChange?.();
      if (t < 1) requestAnimationFrame(step);
      else {
        const viewerCam = this.readCurrentViewerCamera();
        if (viewerCam) {
          this.camera = viewerCam;
          this.host?.emit({ type: 'cameraChange', camera: viewerCam });
        }
      }
    };
    requestAnimationFrame(step);
    console.log(
      `[Giro3DAdapter] flyToLayer bounds: center=(${cx.toFixed(1)}, ${cy.toFixed(1)}, ${cz.toFixed(1)}) diagonal=${diagonal.toFixed(1)} distance=${distance.toFixed(1)}`,
    );
    return true;
  }

  /**
   * Fly the camera to a layer's actual rendered extent in scene space.
   * Returns false when the layer is not mounted, has no geometry yet
   * (streaming tilesets), or is a map-layer (basemap) — callers should
   * fall back to `flyToProjectView` for those cases.
   */
  flyToLayer(layerId: string): boolean {
    const record = this.layers.get(layerId);
    if (!record?.entity) return false;
    if (record.descriptor.attachTo === 'map-layer') return false;
    // Point clouds may not have traversable geometry children yet; prefer
    // the dedicated bounding-box API (getBoundingBox / getBoundingBoxWorld)
    // that COPC/Potree entities expose.
    const isPcKind = record.descriptor.kind === 'copc-pointcloud'
      || record.descriptor.kind === 'potree-pointcloud'
      || record.descriptor.kind === 'pointcloud-tiles3d';
    if (isPcKind) {
      const pcBox = readPointCloudBoundingBox(record.entity);
      if (pcBox && !pcBox.isEmpty()) {
        return this.animateCameraToBounds(pcBox);
      }
    }
    const bounds = this.computeEntityWorldBounds(record.entity);
    if (!bounds) {
      console.warn(`[Giro3DAdapter] flyToLayer skipped: ${layerId} has no rendered geometry yet`);
      return false;
    }
    return this.animateCameraToBounds(bounds);
  }

  /**
   * Return the Three.js Object3D that hosts a registered 3D Tiles / BIM
   * layer's scene graph (its tileset root). Returns `null` for map-layer
   * entries (basemaps, terrain ColorLayers) and for layers that have not
   * yet mounted any geometry.
   *
   * Used by the RFC-108-G alternate-group crossfade (Phase 2) to traverse
   * a layer's materials without coupling that module to Giro3DAdapter
   * internals.
   */
  getLayerObject3D(layerId: string): THREE.Object3D | null {
    const record = this.layers.get(layerId);
    if (!record?.entity) return null;
    if (record.descriptor.attachTo === 'map-layer') return null;
    return this.resolveEntityObject3DRoots(record.entity)[0] ?? null;
  }

  /**
   * Tell the renderer the scene changed. Public wrapper around
   * `this.instance.notifyChange()` for external animation modules (RFC-108-G
   * crossfade) that mutate materials and need a redraw to honour them.
   */
  notifyChange(): void {
    (this.instance as { notifyChange?: () => void } | null)?.notifyChange?.();
  }

  /**
   * Scene elevation (EPSG:3857 Z-up) for fly-to — never assume z=0 in PLATEAU scenes.
   */
  private resolveSceneElevationForFlyTo(): number {
    if (this.modelSphere?.center && Number.isFinite(this.modelSphere.center.z)) {
      return this.modelSphere.center.z;
    }
    // Spike (gap G20): a true-3D ElevationLayer raises the terrain surface to
    // the DEM's real elevation (often hundreds of metres). Treating the ground
    // as z=0 would place the fly-to target — and frequently the camera itself —
    // beneath the surface, leaving the user buried (the "flash then disappear").
    // Use the mounted DEM's min elevation as the ground floor instead.
    const terrainFloor = this.resolveTerrainElevationFloor();
    if (terrainFloor !== null) {
      return terrainFloor;
    }
    const targetZ = this.controls?.target?.z;
    if (typeof targetZ === 'number' && Number.isFinite(targetZ)) {
      return targetZ;
    }
    if (this.projectEcefAnchor?.height != null && Number.isFinite(this.projectEcefAnchor.height)) {
      return this.projectEcefAnchor.height;
    }
    return 0;
  }

  /**
   * Spike (gap G20): when the elevation-mesh flag is on and a `terrain-source`
   * `ElevationLayer` is mounted, return its `minmax.min` (the DEM's real ground
   * floor in EPSG:3857 metres). Returns `null` when the flag is off or no
   * ElevationLayer with a known range is mounted, so the flat-terrain camera
   * behaviour is unchanged.
   */
  private resolveTerrainElevationFloor(): number | null {
    return selectTerrainElevationFloor(this.layers.values(), TERRAIN_ELEVATION_MESH);
  }

  /**
   * G20 — mount a hypsometric ColorLayer draped over a terrain `ElevationLayer`.
   *
   * Reuses the existing `terrain-color-source` builder (TiTiler `colormap_name=
   * terrain` PNG), rescaled to the ElevationLayer's TRUE data min/max so the
   * colour ramp spans the real relief and aligns with the mesh. Mounted after
   * the elevation entity so it draws on top of the OSM basemap within the DEM
   * footprint. Failures are non-fatal: the terrain still renders (just without
   * the colour drape) so a colormap hiccup never breaks the 3D view.
   *
   * No-op unless the elevation-mesh flag is ON (only the flag-ON path produces a
   * `terrain-source`), so the flat ColorLayer path is unaffected.
   */
  private async mountTerrainColorDrape(
    layer: Layer,
    descriptor: Giro3DLayerEntityDescriptor,
    elevationEntity: unknown,
  ): Promise<void> {
    if (!TERRAIN_ELEVATION_MESH || !this.modules || !this.map) return;
    if (this.terrainColorDrapes.has(layer.id)) return;

    // Honour a previously chosen colour ramp across re-mounts; 'none' means the
    // operator removed the drape, so don't re-create it.
    const storedRamp = this.terrainColorRamp.get(layer.id) ?? 'hypsometric';
    if (storedRamp === 'none') return;
    const colormapName = rampToTitilerColormap(storedRamp) ?? 'terrain';

    const minmax = (elevationEntity as { minmax?: { min?: number; max?: number } } | undefined)?.minmax;
    const colorDescriptor = buildTerrainColorDrapeDescriptor(descriptor, minmax, colormapName);

    try {
      const drape = await createGiro3DEntity(colorDescriptor, this.modules);
      if (!drape) return;
      await this.map.addLayer(drape);
      this.terrainColorDrapes.set(layer.id, drape);
      this.terrainColorRamp.set(layer.id, storedRamp);
      // G20: re-apply any operator-set opacity so a re-mount / re-stream keeps
      // the slider value on the visible color surface.
      const storedOpacity = this.layerOpacity.get(layer.id);
      if (typeof storedOpacity === 'number' && Number.isFinite(storedOpacity)) {
        this.applyMapLayerEntityOpacity(drape, storedOpacity);
      }
      // G20: re-apply the eye-toggle so a hidden terrain stays hidden when its
      // drape re-mounts (the drape is a separate Map layer).
      const storedVisible = this.layerVisible.get(layer.id);
      if (storedVisible === false) {
        this.applyMapLayerEntityVisibility(drape, false, layer.id);
      }
      (this.instance as { notifyChange?: () => void } | null)?.notifyChange?.();
      console.log(
        `[Giro3DAdapter] terrain hypsometric color drape mounted for ${layer.id}`,
        colorDescriptor.colorRescaleRange
          ? `rescale=[${colorDescriptor.colorRescaleRange[0]},${colorDescriptor.colorRescaleRange[1]}]`
          : '(percentile rescale)',
      );
    } catch (err) {
      console.warn(`[Giro3DAdapter] terrain color drape FAILED for ${layer.id} (terrain still renders):`, err);
    }
  }

  /**
   * G20 — swap the colour ramp of a terrain layer's hypsometric drape (flag ON).
   *
   * The visible colour of a true-3D terrain is the TiTiler hypsometric ColorLayer
   * *drape* (`colormap_name=terrain`), NOT the height-only ElevationLayer's
   * client-side `colorMap`. So the colour-ramp selector can't just call
   * `applyColorMapMode` on the ElevationLayer (invisible under the opaque drape);
   * it must REBUILD the drape with the corresponding TiTiler `colormap_name`,
   * preserving the rescale to the true elevation min/max.
   *
   * Mapping (see `rampToTitilerColormap`): hypsometric→terrain, plasma→plasma,
   * rdylgn→rdylgn, gray→gray. `none` removes the drape (bare mesh / basemap).
   *
   * Idempotent: a no-op when the ramp is unchanged, so it's safe to call on every
   * terrain tool-card push (e.g. opacity drags) without churning the drape.
   * No-op when the flag is OFF (no drape exists), so the flat-path ramp behaviour
   * is untouched.
   */
  async setTerrainColorRamp(layerId: string, ramp: ColorRampPreset): Promise<void> {
    if (!TERRAIN_ELEVATION_MESH || !this.modules || !this.map) return;
    const record = this.layers.get(layerId);
    if (!record || record.layer.kind !== 'terrain') return;

    const current = this.terrainColorRamp.get(layerId) ?? 'hypsometric';
    const hadDrape = this.terrainColorDrapes.has(layerId);
    // Skip a rebuild when nothing changed (avoids drape churn on slider drags).
    if (current === ramp && (ramp === 'none' ? !hadDrape : hadDrape)) return;

    this.terrainColorRamp.set(layerId, ramp);

    // Tear down the existing drape (if any).
    const old = this.terrainColorDrapes.get(layerId);
    if (old) {
      try {
        this.map.removeLayer?.(old, { disposeLayer: true });
      } catch (err) {
        console.warn(`[Giro3DAdapter] failed to remove old terrain drape for ${layerId}:`, err);
      }
      this.terrainColorDrapes.delete(layerId);
    }

    if (ramp === 'none') {
      (this.instance as { notifyChange?: () => void } | null)?.notifyChange?.();
      console.log(`[Giro3DAdapter] terrain color ramp → none (drape removed) for ${layerId}`);
      return;
    }

    const colormapName = rampToTitilerColormap(ramp) ?? 'terrain';
    const minmax = (record.entity as { minmax?: { min?: number; max?: number } } | undefined)?.minmax;
    const colorDescriptor = buildTerrainColorDrapeDescriptor(record.descriptor, minmax, colormapName);

    try {
      const drape = await createGiro3DEntity(colorDescriptor, this.modules);
      if (!drape) return;
      await this.map.addLayer(drape);
      this.terrainColorDrapes.set(layerId, drape);
      // Preserve operator-set opacity + visibility on the rebuilt drape.
      const storedOpacity = this.layerOpacity.get(layerId);
      if (typeof storedOpacity === 'number' && Number.isFinite(storedOpacity)) {
        this.applyMapLayerEntityOpacity(drape, storedOpacity);
      }
      if (this.layerVisible.get(layerId) === false) {
        this.applyMapLayerEntityVisibility(drape, false, layerId);
      }
      (this.instance as { notifyChange?: () => void } | null)?.notifyChange?.();
      console.log(`[Giro3DAdapter] terrain color ramp → ${ramp} (colormap_name=${colormapName}) for ${layerId}`);
    } catch (err) {
      console.warn(`[Giro3DAdapter] terrain color ramp rebuild FAILED for ${layerId}:`, err);
    }
  }

  /**
   * Spike (gap G20): the initial fly-to runs before the DEM mounts, so the saved
   * pose is framed against a z=0 ground plane and the camera lands below the
   * elevated surface. Once the ElevationLayer is mounted, re-frame to an OBLIQUE
   * pose over the DEM so the relief and vertical exaggeration read immediately —
   * a top-down view hides the very Z stretch the feature exists to show. Falls
   * back to re-applying the saved pose (`resolveSceneElevationForFlyTo()` now
   * reports the real surface height) when no terrain bbox is available.
   */
  private reframeForTerrainElevation(): void {
    if (!TERRAIN_ELEVATION_MESH) return;
    if (this.frameTerrainOblique()) return;
    if (!this.camera) return;
    this.applyViewerCamera(this.camera);
  }

  /**
   * G20 — frame the mounted terrain DEM from an oblique angle (~-40° pitch).
   *
   * Converts the terrain layer's lon/lat bbox to the EPSG:3857 render frame,
   * uses the DEM floor (`selectTerrainElevationFloor`, the ElevationLayer's real
   * min) as the look-at height, and applies the pure
   * {@link computeTerrainObliquePose} result to the live camera + orbit target.
   * Returns `false` (so the caller can fall back) when the frame is not Web
   * Mercator, no terrain bbox exists, or the pose is degenerate.
   */
  private frameTerrainOblique(): boolean {
    if (!TERRAIN_ELEVATION_MESH) return false;
    if (this.activeFrameCrs !== 'EPSG:3857') return false;
    const inst = this.instance as any;
    const cam = inst?.view?.camera ?? inst?.camera?.camera3D;
    if (!cam || !this.controls?.target) return false;

    let terrainBbox: readonly number[] | undefined;
    for (const record of this.layers.values()) {
      if (record.descriptor.kind === 'terrain-source') {
        const bbox = (record.layer as { bbox?: readonly number[] }).bbox;
        if (Array.isArray(bbox) && bbox.length >= 4 && bbox.every((v) => Number.isFinite(v))) {
          terrainBbox = bbox;
          break;
        }
      }
    }
    if (!terrainBbox) return false;

    const [minLon, minLat, maxLon, maxLat] = terrainBbox;
    if (maxLon <= minLon || maxLat <= minLat) return false;
    const toMercatorX = (lon: number) => (lon / 180) * 20037508.342789244;
    const toMercatorY = (lat: number) => {
      const rad = (lat * Math.PI) / 180;
      return (Math.log(Math.tan(Math.PI / 4 + rad / 2)) / Math.PI) * 20037508.342789244;
    };
    const cx = (toMercatorX(minLon) + toMercatorX(maxLon)) / 2;
    const cy = (toMercatorY(minLat) + toMercatorY(maxLat)) / 2;
    const widthM = toMercatorX(maxLon) - toMercatorX(minLon);
    const heightM = toMercatorY(maxLat) - toMercatorY(minLat);
    const diagonal = Math.hypot(widthM, heightM);
    const floorZ = this.resolveSceneElevationForFlyTo();

    const pose = computeTerrainObliquePose(cx, cy, floorZ, diagonal, TERRAIN_OBLIQUE_PITCH_DEG);
    if (!pose) return false;

    cam.position.set(pose.position.x, pose.position.y, pose.position.z);
    cam.up?.set(0, 0, 1);
    cam.near = 0.1;
    cam.far = 10_000_000;
    cam.lookAt(pose.target.x, pose.target.y, pose.target.z);
    cam.updateProjectionMatrix?.();
    this.controls.target.set(pose.target.x, pose.target.y, pose.target.z);
    this.controls.update?.();
    inst?.notifyChange?.();
    console.log(
      `[Giro3DAdapter] terrain oblique framing: pitch=${pose.pitchDeg}° `
      + `pos=(${pose.position.x.toFixed(1)}, ${pose.position.y.toFixed(1)}, ${pose.position.z.toFixed(1)}) `
      + `target=(${pose.target.x.toFixed(1)}, ${pose.target.y.toFixed(1)}, ${pose.target.z.toFixed(1)})`,
    );
    return true;
  }

  /** Fly back to the project anchor / model bounds (used for global-bbox basemaps). */
  flyToProjectView(): boolean {
    if (this.modelSphere && Number.isFinite(this.modelSphere.radius) && this.modelSphere.radius > 0) {
      const c = this.modelSphere.center;
      this.positionCameraAt(c.x, c.y, c.z, this.modelSphere.radius * 2);
      return true;
    }
    const inst = this.instance as any;
    const camera = inst?.view?.camera ?? inst?.camera?.camera3D;
    if (camera && this.controls && this.projectEcefAnchor) {
      const { lat, lon, height = 0 } = this.projectEcefAnchor;
      const mx = (lon / 180) * 20037508.342789244;
      const latRad = (lat * Math.PI) / 180;
      const my = (Math.log(Math.tan(Math.PI / 4 + latRad / 2)) / Math.PI) * 20037508.342789244;
      this.positionCameraAt(mx, my, height, 5000);
      return true;
    }
    return false;
  }

  /**
   * Wave-1 A3 — animate the camera to look at a bbox.
   *
   * `crs` is the CRS the bbox values are expressed in. Only `EPSG:4326`
   * (lon/lat degrees) and `EPSG:3857` (Web Mercator metres) are accepted
   * directly. Anything else (e.g. `EPSG:4978` ECEF, project-local frames)
   * cannot be flown to without a transform — the function returns false
   * so the caller can fall back to `flyToProjectView` instead of
   * sending the camera into deep space.
   *
   * When `crs` is omitted, a heuristic infers degrees vs mercator from
   * the magnitude of the values. This is a best effort and may return
   * false rather than fly to a wrong location.
   */
  flyToBbox(bbox: readonly number[], crs?: string): boolean {
    if (!Array.isArray(bbox) || bbox.length < 4) return false;
    const [minX, minY, maxX, maxY] = bbox;
    if (![minX, minY, maxX, maxY].every((v) => Number.isFinite(v))) return false;
    if (maxX <= minX || maxY <= minY) return false;
    const lonSpan = maxX - minX;
    const latSpan = maxY - minY;
    if (lonSpan >= 359 && latSpan >= 169) {
      console.warn('[Giro3DAdapter] flyToBbox skipped global bbox', bbox);
      return this.flyToProjectView();
    }
    const inst = this.instance as any;
    const camera = inst?.view?.camera ?? inst?.camera?.camera3D;
    if (!camera || !this.controls) return false;
    const normalizedCrs = (crs ?? '').toUpperCase();
    const explicitDegrees = normalizedCrs === 'EPSG:4326' || normalizedCrs === 'OGC:CRS84';
    const explicitMercator = normalizedCrs === 'EPSG:3857' || normalizedCrs === 'EPSG:900913';
    const heuristicDegrees = !normalizedCrs
      && Math.abs(minX) <= 200 && Math.abs(maxX) <= 200
      && Math.abs(minY) <= 100 && Math.abs(maxY) <= 100;
    const heuristicMercator = !normalizedCrs
      && Math.max(Math.abs(minX), Math.abs(maxX)) <= 20100000
      && Math.max(Math.abs(minY), Math.abs(maxY)) <= 20100000
      && (Math.abs(minX) > 200 || Math.abs(maxX) > 200
        || Math.abs(minY) > 100 || Math.abs(maxY) > 100);
    const isDegrees = explicitDegrees || heuristicDegrees;
    const isMercator = explicitMercator || heuristicMercator;
    if (!isDegrees && !isMercator) {
      console.warn(
        `[Giro3DAdapter] flyToBbox skipped: bbox is in CRS '${normalizedCrs || 'unknown'}' which cannot be flown to without a transform — caller should use flyToLayer or flyToProjectView`,
        bbox,
      );
      return false;
    }
    const toMercatorX = (lon: number) => (lon / 180) * 20037508.342789244;
    const toMercatorY = (lat: number) => {
      const rad = (lat * Math.PI) / 180;
      const y = Math.log(Math.tan(Math.PI / 4 + rad / 2));
      return (y / Math.PI) * 20037508.342789244;
    };
    const mx = isDegrees ? (toMercatorX(minX) + toMercatorX(maxX)) / 2 : (minX + maxX) / 2;
    const my = isDegrees ? (toMercatorY(minY) + toMercatorY(maxY)) / 2 : (minY + maxY) / 2;
    const widthM = isDegrees
      ? toMercatorX(maxX) - toMercatorX(minX)
      : maxX - minX;
    const heightM = isDegrees
      ? toMercatorY(maxY) - toMercatorY(minY)
      : maxY - minY;
    const diagonal = Math.hypot(widthM, heightM);
    if (!Number.isFinite(diagonal) || diagonal <= 0) return false;
    const sceneZ = this.resolveSceneElevationForFlyTo();
    const distance = Math.max(diagonal * 0.45, 1200);
    const startTarget = this.controls.target.clone();
    const startPos = camera.position.clone();
    const endTarget = (this.controls.target.constructor)
      ? new (this.controls.target.constructor as any)(mx, my, sceneZ)
      : null;
    if (!endTarget) return false;
    const endPos = new (camera.position.constructor as any)(
      mx + distance * 0.6,
      my + distance * 0.6,
      sceneZ + distance * 0.6,
    );
    console.log('[Giro3DAdapter][flyToBbox] crs=%s isDegrees=%s isMercator=%s sceneZ=%s diagonal=%sm distance=%sm',
      normalizedCrs || '(none)', isDegrees, isMercator, sceneZ, Math.round(diagonal), Math.round(distance));
    console.log('[Giro3DAdapter][flyToBbox] target(merc)=', { mx, my, sceneZ }, ' endPos=', { x: endPos.x, y: endPos.y, z: endPos.z });
    const start = performance.now();
    const duration = 600;
    const ease = (t: number) => 0.5 - 0.5 * Math.cos(Math.PI * t);
    const step = () => {
      const now = performance.now();
      const t = Math.min(1, (now - start) / duration);
      const k = ease(t);
      camera.position.lerpVectors(startPos, endPos, k);
      this.controls!.target.lerpVectors(startTarget, endTarget, k);
      this.controls!.update?.();
      camera.updateProjectionMatrix?.();
      (this.instance as any)?.notifyChange?.();
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        console.log('[Giro3DAdapter][flyToBbox] DONE camera.pos=', { x: camera.position.x, y: camera.position.y, z: camera.position.z },
          ' target=', { x: this.controls!.target.x, y: this.controls!.target.y, z: this.controls!.target.z },
          ' near=', camera.near, ' far=', camera.far);
        const viewerCam = this.readCurrentViewerCamera();
        if (viewerCam) {
          this.camera = viewerCam;
          this.host?.emit({ type: 'cameraChange', camera: viewerCam });
        }
      }
    };
    requestAnimationFrame(step);
    return true;
  }

  /**
   * RFC-114 Phase 2 — Microscope / Telescope camera animation.
   *
   * Animates the camera to `targetDistanceM` metres from the current orbit
   * target over `durationMs` milliseconds (default 500). The orbit target
   * stays fixed; only the camera position moves along the look direction.
   *
   * Uses the same ease-in-out-cosine curve as flyToBbox for visual consistency.
   */
  animateCameraDistance(targetDistanceM: number, durationMs = 500): void {
    const inst = this.instance as any;
    const camera = inst?.view?.camera ?? inst?.camera?.camera3D;
    if (!camera || !this.controls) return;

    const target = (this.controls as any).target;
    if (!target) return;

    const startPos = camera.position.clone();
    const targetClone = target.clone();

    // Direction from orbit target to camera (normalised).
    const dir = startPos.clone().sub(targetClone);
    const currentDist = dir.length();
    if (currentDist < 1e-6) return;
    dir.divideScalar(currentDist);

    // End position: same direction, new distance.
    const endPos = targetClone.clone().addScaledVector(dir, targetDistanceM);

    const start = performance.now();
    const ease = (t: number) => 0.5 - 0.5 * Math.cos(Math.PI * t);

    const step = () => {
      const now = performance.now();
      const t = Math.min(1, (now - start) / durationMs);
      const k = ease(t);
      camera.position.lerpVectors(startPos, endPos, k);
      (this.controls as any).update?.();
      camera.updateProjectionMatrix?.();
      inst?.notifyChange?.();
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /**
   * Apply terrain tool card state to the Giro3D Map/Instance.
   *
   * Called by the terrain tool card subscription whenever the user
   * toggles hillshade, slope, aspect, exaggeration, or contours.
   * Each toggle is mapped to the corresponding Giro3D API.
   */
  applyTerrainToolCardState(state: {
    hillshade: boolean;
    slopeOn: boolean;
    aspectOn: boolean;
    verticalExaggeration: number;
    contoursOn: boolean;
    contourInterval: number;
    opacity: number;
    colorRamp?: string;
  }): void {
    if (!this.map && !this.instance) return;

    if (state.hillshade) {
      applyHillshade(this.map);
    } else {
      removeHillshade(this.map);
    }

    for (const record of this.layers.values()) {
      if (record.layer.kind !== 'terrain') continue;
      const entity = record.entity;
      // Flag ON: the visible colour is the hypsometric ColorLayer *drape*, so the
      // ramp selector must rebuild that drape with the matching TiTiler colormap
      // (applyColorMapMode on the height-only ElevationLayer is invisible under
      // the opaque drape). setTerrainColorRamp is idempotent + async-safe.
      const hasDrape = TERRAIN_ELEVATION_MESH && this.terrainColorDrapes.has(record.layer.id);
      if (hasDrape) {
        if (state.colorRamp !== undefined) {
          void this.setTerrainColorRamp(record.layer.id, state.colorRamp as ColorRampPreset);
        }
      } else {
        // Flag OFF (flat ColorLayer / no drape): unchanged legacy colour-map
        // behaviour on the ElevationLayer's client-side colorMap.
        if (state.slopeOn) {
          applyColorMapMode(entity, ColorMapMode.Slope, { ramp: state.colorRamp as ColorRampPreset });
        } else if (state.aspectOn) {
          applyColorMapMode(entity, ColorMapMode.Aspect, { ramp: state.colorRamp as ColorRampPreset });
        } else if (state.colorRamp && state.colorRamp !== 'none') {
          applyColorMapMode(entity, ColorMapMode.Elevation, { ramp: state.colorRamp as ColorRampPreset });
        } else {
          applyColorMapMode(entity, ColorMapMode.None);
        }
      }
      // Apply the terrain tool-card opacity so the opacity slider actually
      // changes on-screen opacity. Uses the same setLayerOpacity path that
      // the scene-card slider uses (layerOpacity map + ColorLayer.opacity),
      // which also fades the paired drape.
      this.setLayerOpacity?.(record.layer.id, state.opacity);
    }

    setContourLines(this.map, state.contoursOn, state.contourInterval);
    // G20 — anchor the vertical exaggeration about the DEM's true base elevation
    // (the ElevationLayer min), not world z=0, so the relief stretches in place
    // instead of skewing. Falls back to 0 (identity) on the flat / flag-OFF path.
    const exaggerationBase = this.resolveTerrainElevationFloor() ?? 0;
    setVerticalExaggeration(this.instance, this.map, state.verticalExaggeration, exaggerationBase);

    (this.instance as { notifyChange?: () => void } | null)?.notifyChange?.();
  }

  _layerStats(): Record<string, number> {
    const stats: Record<string, number> = {
      raster: 0,
      terrain: 0,
      '3dtiles': 0,
      bim: 0,
      pointcloud: 0,
    };
    for (const record of this.layers.values()) {
      stats[record.layer.kind] = (stats[record.layer.kind] ?? 0) + 1;
    }
    return stats;
  }

  _lastCamera(): ViewerCamera | null {
    return this.camera;
  }

  _layerDescriptors(): Giro3DLayerEntityDescriptor[] {
    return [...this.layers.values()].map((record) => record.descriptor);
  }

  /** Expose the raw Giro3D Instance for native tools (DrawTool, Inspector, picking). */
  getInstance(): Giro3DInstance | null {
    return this.instance;
  }

  /** Expose scene surfaces for 3D measurement raycasting (satisfies PointCloudCommandAdapter.getMeasureSurfaces). */
  getMeasureSurfaces(): import('@/engines/giro3d/measureController').MeasureSurfaces | null {
    const inst = this.instance as any;
    if (!inst?.domElement) return null;
    return {
      scene: inst.scene,
      camera: inst.view?.camera,
      domEl: inst.domElement,
      notifyChange: () => inst.notifyChange?.(),
    };
  }

  /** Expose section-plane/clipping-box control (satisfies PointCloudCommandAdapter.getSectionAndClipAdapter). */
  getSectionAndClipAdapter(): import('@/components/three-d/SectionAndClipToolCard').SectionAndClipCommandAdapter {
    return this;
  }

  /**
   * Apply the active LoD override to a freshly mounted 3D Tiles entity so a
   * forced 'high'/'low' mode persists across newly added tilesets.
   */
  private applyTilesetLodModeTo(entity: unknown, descriptor: Giro3DLayerEntityDescriptor): void {
    if (this.tilesetLodMode === 'auto') return;
    const ent = entity as any;
    const tr = ent?.tiles ?? ent?.tilesRenderer ?? ent?.renderer;
    if (!tr) return;
    const defaultTarget = descriptor.errorTarget ?? 8;
    tr.errorTarget =
      this.tilesetLodMode === 'high' ? 0.1
      : this.tilesetLodMode === 'low' ? 100000
      : defaultTarget;
  }

  /**
   * Runtime LoD override for 3D Tiles layers (operator visualization control).
   *
   * 3D Tiles normally choose tiles by screen-space error (SSE) + memory budget:
   * far away or under memory pressure the engine falls back to the coarse tier
   * automatically. This lets the operator FORCE a mode regardless of distance,
   * which is useful for presentation / inspection:
   *   - 'auto' → default SSE-driven streaming (engine decides; coarse far, fine near)
   *   - 'high' → force the finest tier (textured lod2) at any distance
   *   - 'low'  → force the coarse tier (lod1 blocks) — lightweight overview
   *
   * Implemented by overriding the 3d-tiles-renderer `errorTarget`: ~0 forces
   * maximum refinement, a very large value suppresses refinement. Applies to
   * every mounted 3D Tiles layer.
   */
  setTilesetLodMode(mode: 'auto' | 'high' | 'low', targetLayerId?: string | null): boolean {
    this.tilesetLodMode = mode;
    let applied = false;
    for (const [id, record] of this.layers.entries()) {
      if (record.descriptor.kind !== '3dtiles') continue;
      if (targetLayerId && id !== targetLayerId) continue;
      const ent = record.entity as any;
      const tr = ent?.tiles ?? ent?.tilesRenderer ?? ent?.renderer;
      if (!tr) continue;
      const defaultTarget = record.descriptor.errorTarget ?? 8;
      tr.errorTarget =
        mode === 'high' ? 0.1
        : mode === 'low' ? 100000
        : defaultTarget;
      applied = true;
    }
    (this.instance as any)?.notifyChange?.();
    console.log(`[Giro3DAdapter] Tileset LoD mode → ${mode} (applied to ${applied ? 'tilesets' : 'none yet'}${targetLayerId ? ` [layer=${targetLayerId}]` : ''})`);
    return applied;
  }

  getTilesetLodMode(): 'auto' | 'high' | 'low' {
    return this.tilesetLodMode;
  }

  /** True when at least one 3D Tiles layer is mounted (drives LoD control visibility). */
  hasTilesetLayers(): boolean {
    for (const record of this.layers.values()) {
      if (record.descriptor.kind === '3dtiles') return true;
    }
    return false;
  }

  /** Return this adapter as a ThreeDModelCommandAdapter for the 3D model tool card. */
  getThreeDModelAdapter(): import('@/components/three-d/ThreeDModelToolCard').ThreeDModelCommandAdapter {
    return this;
  }

  /** Extract the IFC model tree from glTF scene extras if available. */
  getModelTree(): import('@/components/three-d/ThreeDModelToolCard').ModelTreeNode[] {
    const inst = this.instance as any;
    if (!inst?.scene) return [];
    const tree: import('@/components/three-d/ThreeDModelToolCard').ModelTreeNode[] = [];
    inst.scene.traverse((child: any) => {
      const extras = child.userData?.geosoft ?? child.userData?.extras?.geosoft;
      if (extras?.modelTree && Array.isArray(extras.modelTree)) {
        for (const group of extras.modelTree) {
          if (group && typeof group === 'object' && group.name) {
            tree.push({
              id: group.id ?? group.name,
              name: `${group.name} (${group.children?.length ?? 0})`,
              visible: !this.hiddenModelNodeIds.has(
                normalizeNodeToken(group.featureId ?? group.id ?? group.name) ?? '',
              ),
              featureId: group.featureId,
              count: group.children?.length,
              children: (group.children ?? []).map((c: any) => ({
                id: c.id ?? c.featureId ?? c.name,
                name: c.name ?? c.id,
                visible: !this.hiddenModelNodeIds.has(
                  normalizeNodeToken(c.featureId ?? c.id ?? c.name) ?? '',
                ),
                featureId: c.featureId,
              })),
            });
          }
        }
      }
    });
    return tree;
  }

  /**
   * Returns the glTF node name of the last picked object — used by the
   * geometry-binding pick resolver (INIT-2026-Q3-SCENE-HIERARCHY) to locate
   * the corresponding TreeNode in the object-index via native_id matching.
   * Returns null when no pick has occurred or the last pick had no name.
   */
  getLastPickedObjectName(): string | null {
    return this.lastPickedObjectName;
  }

  /**
   * Reverse-resolve a tile batch id (e.g. "570" or "feature:570") back to the
   * CityGML gml:id, using the batch table prefetched at tileset load. Lets the
   * 3D→tree path find the object-index node by its nativeId (gml:id) even when
   * the object-index featureId drifted from geometry batch order.
   */
  getGmlIdForBatchId(batchId: string | null | undefined): string | null {
    if (batchId == null) return null;
    let raw = String(batchId);
    const colon = raw.lastIndexOf(':');
    if (colon >= 0) raw = raw.slice(colon + 1);
    return this.gmlIdByBatchIndex.get(raw) ?? null;
  }

  /**
   * Return the authoritative building attributes for a CityGML gml:id, read
   * from the b3dm batch table at load time. Used by the property panel so it
   * shows real attributes (height, usage, storeys, …) that match the geometry,
   * even when the object-index metadata is stale or empty.
   */
  getAttributesForGmlId(gmlId: string | null | undefined): Record<string, unknown> | null {
    if (!gmlId) return null;
    return this.attrsByGmlId.get(String(gmlId)) ?? null;
  }

  /**
   * Tree → engine highlight (INIT-2026-Q3-SCENE-HIERARCHY Block 2, Task E).
   *
   * Finds the scene object whose name matches the glTF node name (the
   * pick-binding key: OBJ/FBX group name == glTF node name == TreeNode
   * standardRef.nativeId) and applies the same translucent face-fill
   * highlight used by double-click picking, via {@link selectObject}.
   *
   * Returns true when a matching mesh was found and highlighted. Oversized
   * batched tiles (faces beyond the highlight cap) select without a fill
   * (graceful, mirrors the dblclick path), and unmatched names are a no-op.
   */
  highlightByObjectName(name: string): boolean {
    if (!name) return false;
    const scene = (this.instance as any)?.scene as THREE.Object3D | undefined;
    if (!scene) return false;

    let match: THREE.Object3D | null = null;
    scene.traverse((child: THREE.Object3D) => {
      if (match) return;
      if (child.name === name) match = child;
    });
    if (!match) return false;

    // Resolve a mesh: the named node itself, or its first descendant mesh.
    let mesh: THREE.Mesh | null = (match as THREE.Mesh).isMesh
      ? (match as THREE.Mesh)
      : null;
    if (!mesh) {
      (match as THREE.Object3D).traverse((c: THREE.Object3D) => {
        if (mesh) return;
        if ((c as THREE.Mesh).isMesh && (c as THREE.Mesh).geometry) {
          mesh = c as THREE.Mesh;
        }
      });
    }
    if (!mesh || !mesh.geometry) return false;

    // Highlight the WHOLE named mesh by building its full face-index set.
    // Only build it when within the highlight cap; otherwise pass null so
    // selectObject records the selection without an oversized fill mesh.
    const geom = mesh.geometry;
    const indexCount = geom.index
      ? geom.index.count
      : (geom.attributes.position?.count ?? 0);
    const faceCount = Math.floor(indexCount / 3);
    let faces: Set<number> | null = null;
    if (faceCount > 0 && faceCount <= MAX_SELECTION_HIGHLIGHT_FACES) {
      faces = new Set<number>();
      for (let f = 0; f < faceCount; f++) faces.add(f);
    }

    this.lastPickedObjectName = name;
    this.selectObject(mesh, false, faces);
    (this.instance as any)?.notifyChange?.();
    return true;
  }

  /**
   * Resolve a numeric batch index from a CityGML ``gml:id`` via the tile
   * batch table (``gml_id`` column). Falls back when object-index featureId
   * drifted from geometry batch order on older conversions.
   */
  private resolveBatchIndexFromGmlId(gmlId: string): string | null {
    if (!gmlId) return null;
    const cached = this.batchIndexByGmlId.get(gmlId);
    if (cached != null) return cached;

    const inst = this.instance as any;
    const scene = (inst?.scene ?? inst?.threeObjects ?? inst?.view?.scene) as THREE.Object3D | undefined;
    if (!scene) return null;

    let resolved: string | null = null;
    scene.traverse((child: THREE.Object3D) => {
      if (resolved) return;
      let current: THREE.Object3D | null = child;
      while (current) {
        const batchTable = current.userData?.batchTable;
        if (batchTable && typeof batchTable === 'object') {
          const gmlIds = (batchTable as Record<string, unknown>).gml_id;
          if (Array.isArray(gmlIds)) {
            const idx = gmlIds.findIndex(
              (entry) => entry != null && String(entry) === gmlId,
            );
            if (idx >= 0) {
              resolved = String(idx);
              return;
            }
          }
        }
        current = current.parent;
      }
    });
    return resolved;
  }

  /**
   * Animate the camera to the world-space bounds of the current selection
   * highlight (used after tree → 3D building selection).
   */
  flyToHighlightedSelection(): boolean {
    if (!this.selectionHighlights.length || !this.controls) return false;
    const inst = this.instance as any;
    const camera = inst?.view?.camera ?? inst?.camera?.camera3D;
    if (!camera) return false;

    const box = new THREE.Box3();
    for (const hl of this.selectionHighlights) {
      hl.geometry?.computeBoundingBox();
      const localBox = hl.geometry?.boundingBox;
      if (!localBox) continue;
      const worldBox = localBox.clone().applyMatrix4(hl.matrixWorld);
      box.union(worldBox);
    }
    if (box.isEmpty()) return false;

    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const diagonal = Math.max(size.length(), 40);
    const distance = Math.max(diagonal * 1.8, 80);
    const startTarget = this.controls.target.clone();
    const startPos = camera.position.clone();
    const dir = new THREE.Vector3(0.35, 0.35, 0.85).normalize();
    const endTarget = center.clone();
    const endPos = center.clone().addScaledVector(dir, distance);

    const duration = 650;
    const start = performance.now();
    const ease = (t: number) => 0.5 - 0.5 * Math.cos(Math.PI * t);
    const step = () => {
      const t = ease(Math.min(1, (performance.now() - start) / duration));
      camera.position.lerpVectors(startPos, endPos, t);
      this.controls.target.lerpVectors(startTarget, endTarget, t);
      camera.updateProjectionMatrix?.();
      this.controls.update?.();
      inst?.notifyChange?.();
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    return true;
  }

  /**
   * Highlight by batch ID / feature ID (INIT-2026-Q3-SCENE-HIERARCHY Block 2, Task C3).
   *
   * When ``gmlId`` is supplied, the batch table ``gml_id`` column is consulted
   * first so tree selection stays aligned with b3dm geometry even when the
   * object-index ``featureId`` is stale.
   */
  highlightByBatchId(layerId: string, batchId: string, gmlId?: string | null): boolean {
    const fromGml = gmlId ? this.resolveBatchIndexFromGmlId(gmlId) : null;
    const resolvedBatchId = fromGml ?? batchId;
    if (!resolvedBatchId) return false;
    const inst = this.instance as any;
    const scene = (inst?.scene ?? inst?.threeObjects ?? inst?.view?.scene) as THREE.Object3D | undefined;
     
    console.log("[highlightByBatchId] called", {
      layerId,
      batchId,
      gmlId,
      resolvedBatchId,
      hasScene: !!scene,
    });
    if (!scene) return false;

    const numericBatchId = Number(resolvedBatchId);
    const matches: Array<{ mesh: THREE.Mesh; faces: Set<number> }> = [];
    let meshCount = 0;
    let attrNamesSeen: string[] = [];
    // When a gmlId is supplied, prefer tile-local resolution: each mesh carries
    // its tile's gml_id column (stamped at load-model). `_BATCHID` is only unique
    // WITHIN a tile, so resolving the batch id per mesh via gml_id ensures we
    // highlight exactly the one building whose gml_id matches — not "building N"
    // in every block. Falls back to the global numeric match only when no mesh
    // carries a gml_id column (e.g. non-CityGML tiles).
    scene.traverse((child: THREE.Object3D) => {
      if (!(child as THREE.Mesh).isMesh || !(child as THREE.Mesh).geometry) return;

      const mesh = child as THREE.Mesh;
      const geom = mesh.geometry;
      meshCount++;
      if (attrNamesSeen.length === 0) attrNamesSeen = Object.keys(geom.attributes);
      const batchAttr =
        geom.attributes._batchid ??
        geom.attributes['_batchid'] ??
        geom.attributes._BATCHID ??
        geom.attributes['_BATCHID'] ??
        geom.attributes._BATCH_ID ??
        geom.attributes['_BATCH_ID'] ??
        geom.attributes.batchId ??
        geom.attributes['batchId'];

      if (!batchAttr) return;

      // Resolve the batch id to match for THIS mesh.
      const meshGmlIds = (mesh.userData as Record<string, unknown>).__gmlIds as
        | ArrayLike<unknown>
        | undefined;
      let targetBatch = numericBatchId;
        if (gmlId && meshGmlIds && meshGmlIds.length) {
        let local = -1;
        for (let i = 0; i < meshGmlIds.length; i++) {
          if (String(meshGmlIds[i]) === gmlId) {
            local = i;
            break;
          }
        }
        // This tile does not contain the target building → skip it entirely.
        if (local < 0) return;
        targetBatch = local;
      }

      const arr = batchAttr.array;
      const indexArray = geom.index?.array;
      const faceCount = indexArray
        ? Math.floor(indexArray.length / 3)
        : Math.floor((geom.attributes.position?.count ?? 0) / 3);

      const faces = new Set<number>();
      for (let fi = 0; fi < faceCount; fi++) {
        const vi = indexArray ? indexArray[fi * 3] : fi * 3;
        if (arr[vi] === targetBatch) {
          faces.add(fi);
        }
      }

      if (faces.size > 0) {
        matches.push({ mesh, faces });
      }
    });

    // Always clear the previous selection — even when the new building's tile
    // isn't loaded yet (meshHits === 0). Without this, the old yellow highlight
    // stays visible when the camera flies away and unloads the target's tile.
    this.clearSelection();

    if (matches.length === 0) return false;

    this.lastPickedFeatureId = resolvedBatchId;
    this.selectedFeatureIds.add(resolvedBatchId);

    let highlighted = false;
    for (const { mesh, faces } of matches) {
      this.selectedObjects.add(mesh);
      if (this.attachFaceHighlight(mesh, faces)) {
        highlighted = true;
      }
    }

    if (this.outlinePassManager) {
      this.outlinePassManager.setSelectedObjects([]);
    }
    if (highlighted) {
      this.notifySelectionRender();
      // Tree-driven selections frequently target a building outside the current
      // view. Fly the camera to the highlighted geometry so the selection is
      // visible. Uses world-space bounds of the highlight meshes (no CRS guess).
      this.flyToHighlightedSelection();
    }
    return highlighted;
  }

  /** Last rich selection snapshot captured from double-click picking. */
  getLastPickedFeatureSelection():
    | import('@/components/three-d/ThreeDModelToolCard').ThreeDFeatureSelection
    | null {
    if (!this.lastPickedFeatureSelection) return null;
    return {
      ...this.lastPickedFeatureSelection,
      properties: this.lastPickedFeatureSelection.properties
        ? [...this.lastPickedFeatureSelection.properties]
        : undefined,
    };
  }

  /** Get selection data for property panel with full PLATEAU batch table properties.
   * Returns data whenever an object is selected, even when no batch-table feature ID
   * is present (e.g. non-PLATEAU 3D tiles with no encoded IDs). */
  getSelectionData(): { featureId: string; properties: Record<string, unknown> } | null {
    if (!this.selectedObjects.size) return null;
    // Use the resolved feature ID when available; fall back to the mesh name.
    const firstMesh = [...this.selectedObjects][0];
    const featureId =
      this.lastPickedFeatureId ??
      firstMesh?.name ??
      `selected-${Date.now()}`;
    return {
      featureId,
      properties: this.lastPickedBatchTableProperties || {},
    };
  }

  /** Get count of selected features for multi-selection display. */
  getSelectionCount(): number {
    // Use selected objects count when no explicit feature IDs are tracked
    return this.selectedFeatureIds.size || this.selectedObjects.size;
  }

  /** Check if any objects are currently selected. */
  hasSelection(): boolean {
    return this.selectedObjects.size > 0;
  }

  /** Expose the Three.js controls for camera preset manipulation. */
  getControls(): any {
    return this.controls;
  }

  /** Get model bounds — from geometry traversal OR pre-fetched tileset bounding volume.
   *
   * Camera presets (Top / Front / Side / Iso / Fit) and section-plane
   * controls are model-centric tools, so by default this method excludes
   * point-cloud / COPC / Potree records — otherwise a multi-billion-point
   * cloud spanning a wide AOI would dominate the bounds and dwarf the
   * 3D model the operator wants to inspect. Pass
   * `{ includePointClouds: true }` to get the union bounds for the
   * legacy "fit to all visible content" behaviour. */
  getModelBounds(options?: { includePointClouds?: boolean }): ModelBounds | null {
    const includePointClouds = options?.includePointClouds ?? false;
    // If we have pre-fetched bounds from tileset.json, use those as fallback
    const fallback = this.knownBounds;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    let hasGeom = false;

    for (const record of this.layers.values()) {
      if (
        !includePointClouds
        && (record.descriptor.kind === 'copc-pointcloud'
          || record.descriptor.kind === 'potree-pointcloud')
      ) {
        continue;
      }
      const obj = record.entity as any;
      if (!obj || typeof obj.traverse !== 'function') continue;
      obj.traverse((child: any) => {
        if (child.geometry) {
          child.geometry.computeBoundingBox?.();
          const bb = child.geometry.boundingBox;
          if (bb) {
            child.updateWorldMatrix(true, false);
            const m = child.matrixWorld;
            const corners = [
              [bb.min.x, bb.min.y, bb.min.z], [bb.max.x, bb.min.y, bb.min.z],
              [bb.min.x, bb.max.y, bb.min.z], [bb.max.x, bb.max.y, bb.min.z],
              [bb.min.x, bb.min.y, bb.max.z], [bb.max.x, bb.min.y, bb.max.z],
              [bb.min.x, bb.max.y, bb.max.z], [bb.max.x, bb.max.y, bb.max.z],
            ];
            for (const [cx, cy, cz] of corners) {
              const wx = m.elements[0]*cx + m.elements[4]*cy + m.elements[8]*cz + m.elements[12];
              const wy = m.elements[1]*cx + m.elements[5]*cy + m.elements[9]*cz + m.elements[13];
              const wz = m.elements[2]*cx + m.elements[6]*cy + m.elements[10]*cz + m.elements[14];
              if (!isFiniteVec3(wx, wy, wz)) continue;
              minX = Math.min(minX, wx); maxX = Math.max(maxX, wx);
              minY = Math.min(minY, wy); maxY = Math.max(maxY, wy);
              minZ = Math.min(minZ, wz); maxZ = Math.max(maxZ, wz);
            }
            hasGeom = true;
          }
        }
      });
    }

    if (!hasGeom) return fallback;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const cz = (minZ + maxZ) / 2;
    const dx = maxX - minX;
    const dy = maxY - minY;
    const dz = maxZ - minZ;
    const diagonal = Math.sqrt(dx*dx + dy*dy + dz*dz);
    if (!isFiniteVec3(cx, cy, cz) || !isFiniteVec3(dx, dy, dz) || !Number.isFinite(diagonal) || diagonal <= 0) {
      return fallback;
    }
    return {
      center: [cx, cy, cz],
      size: [dx, dy, dz],
      diagonal,
    };
  }

  /** Get the loaded entities for model tree traversal. */
  getEntities(): Map<string, { layer: Layer; entity: unknown }> {
    const result = new Map<string, { layer: Layer; entity: unknown }>();
    for (const [id, record] of this.layers) {
      if (record.entity) {
        result.set(id, { layer: record.layer, entity: record.entity });
      }
    }
    // For Tiles3D, also check the Giro3D instance's scene for loaded tile meshes
    if (result.size === 0 || [...result.values()].every(r => {
      const e = r.entity as any;
      return !e || typeof e.traverse !== 'function';
    })) {
      const inst = this.instance as any;
      if (inst?.scene) {
        // Wrap the entire scene as a pseudo-entity so tree traversal works
        for (const [id, record] of this.layers) {
          result.set(id, { layer: record.layer, entity: inst.scene });
        }
      }
    }
    return result;
  }

  private applyModelNodeVisibility(
    visibilityFor: (candidate: string | null) => boolean,
  ): void {
    const inst = this.instance as any;
    if (!inst?.scene) return;
    inst.scene.traverse((child: any) => {
      if (!child?.isMesh) return;
      const userData = asUnknownRecord(child.userData);
      const extras = asUnknownRecord(userData?.extras);
      const geosoft = asUnknownRecord(userData?.geosoft) ?? asUnknownRecord(extras?.geosoft);
      const candidate =
        readFeatureIdFromObject(child) ??
        readStringField(userData, ['featureId', 'nodeId', 'indexNodeId', 'modelNodeId', 'name']) ??
        readStringField(geosoft, ['featureId', 'nodeId', 'indexNodeId', 'modelNodeId', 'name']) ??
        readStringField(extras, ['featureId', 'nodeId', 'indexNodeId', 'modelNodeId', 'name']) ??
        child.name ??
        null;
      child.visible = visibilityFor(candidate);
    });
    inst.notifyChange?.();
  }
}

function boundsFromBox(box: THREE.Box3): { center: [number, number, number]; size: [number, number, number]; diagonal: number } {
  const center = new THREE.Vector3();
  const size = new THREE.Vector3();
  box.getCenter(center);
  box.getSize(size);
  const diagonal = size.length();
  return {
    center: [
      Number.isFinite(center.x) ? center.x : 0,
      Number.isFinite(center.y) ? center.y : 0,
      Number.isFinite(center.z) ? center.z : 0,
    ],
    size: [
      Number.isFinite(size.x) ? size.x : 1,
      Number.isFinite(size.y) ? size.y : 1,
      Number.isFinite(size.z) ? size.z : 1,
    ],
    diagonal: Number.isFinite(diagonal) && diagonal > 0 ? diagonal : 1,
  };
}

function isFiniteVec3(x: number, y: number, z: number): boolean {
  return Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z);
}

function buildPotreeClipBox(box: ClippingBoxSpec, bounds: ModelBounds): {
  box: THREE.Box3;
  inverse: THREE.Matrix4;
  matrix: THREE.Matrix4;
  position: THREE.Vector3;
} {
  const [minX, minY, minZ] = [
    bounds.center[0] - bounds.size[0] / 2,
    bounds.center[1] - bounds.size[1] / 2,
    bounds.center[2] - bounds.size[2] / 2,
  ];
  const size = new THREE.Vector3(
    Math.max((box.xMax - box.xMin) * bounds.size[0], 0.001),
    Math.max((box.yMax - box.yMin) * bounds.size[1], 0.001),
    Math.max((box.zMax - box.zMin) * bounds.size[2], 0.001),
  );
  const position = new THREE.Vector3(
    minX + ((box.xMin + box.xMax) / 2) * bounds.size[0],
    minY + ((box.yMin + box.yMax) / 2) * bounds.size[1],
    minZ + ((box.zMin + box.zMax) / 2) * bounds.size[2],
  );
  const matrix = new THREE.Matrix4().compose(
    position,
    new THREE.Quaternion(),
    size,
  );
  const inverse = matrix.clone().invert();
  const unit = new THREE.Box3(
    new THREE.Vector3(-0.5, -0.5, -0.5),
    new THREE.Vector3(0.5, 0.5, 0.5),
  );
  return {
    box: unit.clone().applyMatrix4(matrix),
    inverse,
    matrix,
    position,
  };
}

function readPointCloudBoundingBox(entity: unknown): THREE.Box3 | null {
  const pointcloud = entity as {
    getBoundingBox?: () => THREE.Box3 | null;
    getBoundingBoxWorld?: () => THREE.Box3 | null;
    boundingBox?: THREE.Box3;
  };
  // Potree v2 octree boxes are CUBIFIED (max = min + cube edge in every axis),
  // so for slender scans (e.g. a 9x12x28 m platform tower) the cube centre can
  // sit metres outside the actual structure and the camera frames empty space.
  // The metadata 'position' attribute range carries the TRUE data extent in
  // absolute coordinates — prefer it when available.
  const tight = readPotreeTightWorldBox(entity);
  if (tight) return tight;
  try {
    const direct = pointcloud.getBoundingBox?.();
    if (direct && !direct.isEmpty()) return direct;
  } catch {
    // Continue to other known point-cloud shapes.
  }
  try {
    const world = pointcloud.getBoundingBoxWorld?.();
    if (world && !world.isEmpty()) return world;
  } catch {
    // Continue to static property fallback.
  }
  const box = pointcloud.boundingBox;
  return box && !box.isEmpty() ? box : null;
}

/**
 * Read the true (non-cubified) world-space data extent of a potree-core v2
 * octree from the metadata `position` attribute range.
 *
 * potree-core translates the octree so `pcoGeometry.offset` is carried on the
 * entity `position`, while the position attribute min/max stay in absolute
 * dataset coordinates — so the attribute range IS the world box for
 * identity-scaled entities (our instance-object mounting path).
 */
function readPotreeTightWorldBox(entity: unknown): THREE.Box3 | null {
  const pointcloud = entity as {
    pcoGeometry?: {
      pointAttributes?: {
        attributes?: Array<{
          name?: string;
          range?: [number[] | number, number[] | number];
        }>;
      };
    };
  };
  const attrs = pointcloud.pcoGeometry?.pointAttributes?.attributes;
  if (!Array.isArray(attrs)) return null;
  const position = attrs.find((a) => a?.name === 'position');
  const range = position?.range;
  if (!Array.isArray(range) || range.length !== 2) return null;
  const [min, max] = range;
  if (!Array.isArray(min) || !Array.isArray(max) || min.length < 3 || max.length < 3) return null;
  const nums = [...min.slice(0, 3), ...max.slice(0, 3)].map(Number);
  if (nums.some((v) => !Number.isFinite(v))) return null;
  const box = new THREE.Box3(
    new THREE.Vector3(nums[0], nums[1], nums[2]),
    new THREE.Vector3(nums[3], nums[4], nums[5]),
  );
  return box.isEmpty() ? null : box;
}

/**
 * Read the recommended interactive point budget from a SceneItem.
 *
 * Wave-4 (A5) flat shape: `extensions.pointcloud.recommendedPointBudget`
 * (the new contract). Legacy fallback:
 * `extensions.pointcloud.pointBudget.recommendedInteractiveBudget`
 * (the pre-A5 nested shape; retained so older scene packages still load).
 */
function readRecommendedPointBudget(layer: Layer): number | null {
  const pointcloud = layer.style?.pointcloud;
  if (typeof pointcloud !== 'object' || pointcloud === null) return null;
  const ext = pointcloud as { recommendedPointBudget?: unknown; pointBudget?: unknown };
  if (typeof ext.recommendedPointBudget === 'number'
    && Number.isFinite(ext.recommendedPointBudget)
    && ext.recommendedPointBudget > 0
  ) {
    return ext.recommendedPointBudget;
  }
  const budget = ext.pointBudget;
  if (typeof budget !== 'object' || budget === null) return null;
  const recommended = (budget as { recommendedInteractiveBudget?: unknown }).recommendedInteractiveBudget;
  return typeof recommended === 'number' && Number.isFinite(recommended) && recommended > 0
    ? recommended
    : null;
}

/**
 * Read the Wave-4 (A5) extension fields the adapter cares about from a
 * `Layer`. Reads `extensions.pointcloud.*` (already normalized onto
 * `layer.style.pointcloud` by `app/scenePackage.ts`) and falls back to
 * `layer.style.coordinateMode` and `layer.crs` for `coordinateMode`.
 */
function readCopcExtensions(layer: Layer): {
  sourceGroupId: string | null;
  coordinateMode: CopcCoordinateMode;
  recommendedPointBudget: number | null;
} {
  const style = (layer.style ?? undefined) as
    | { pointcloud?: unknown; coordinateMode?: unknown }
    | undefined;
  const pointcloud = style?.pointcloud;
  const ext = (typeof pointcloud === 'object' && pointcloud !== null
    ? (pointcloud as Record<string, unknown>)
    : {});

  const groupRaw = ext['sourceGroupId'];
  const sourceGroupId = typeof groupRaw === 'string' && groupRaw.length > 0 ? groupRaw : null;

  const extMode = typeof ext['coordinateMode'] === 'string' ? (ext['coordinateMode'] as string) : undefined;
  const styleMode = typeof style?.coordinateMode === 'string' ? (style.coordinateMode as string) : undefined;
  const coordinateMode: CopcCoordinateMode =
    extMode === 'georeferenced' || styleMode === 'georeferenced' ? 'georeferenced'
    : extMode === 'local-engineering' || styleMode === 'local-engineering' ? 'local-engineering'
    : extMode === 'unknown-local' || styleMode === 'unknown-local' ? 'unknown-local'
    : inferCoordinateModeFromCrs(layer);

  return {
    sourceGroupId,
    coordinateMode,
    recommendedPointBudget: readRecommendedPointBudget(layer),
  };
}

function inferCoordinateModeFromCrs(layer: Layer): CopcCoordinateMode {
  const crs = (layer.crs ?? '').trim().toLowerCase();
  if (crs === '' || crs === 'local' || crs === 'epsg:0') return 'local-engineering';
  return 'georeferenced';
}

/**
 * Stable grouping key for a COPC SceneItem. Returns the declared
 * `sourceGroupId` when present, otherwise the layer id so that ungrouped
 * SceneItems form their own singleton group.
 */
function copcGroupKey(layer: Layer): string {
  const ext = readCopcExtensions(layer);
  return ext.sourceGroupId ?? `layer:${layer.id}`;
}

function asUnknownRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function readStringField(
  record: Record<string, unknown> | null | undefined,
  keys: readonly string[],
): string | undefined {
  if (!record) return undefined;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) return trimmed;
    }
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

function normalizeFeatureProperties(
  source: unknown,
): import('@/components/three-d/ThreeDModelToolCard').ThreeDFeatureProperty[] {
  const out: import('@/components/three-d/ThreeDModelToolCard').ThreeDFeatureProperty[] = [];
  const seen = new Set<string>();
  const push = (key: string, value: unknown): void => {
    if (out.length >= 12) return;
    const normalizedKey = key.trim();
    if (normalizedKey.length === 0) return;
    const normalizedValue = stringifyPropertyValue(value);
    if (normalizedValue === null) return;
    const dedupeKey = `${normalizedKey.toLowerCase()}:${normalizedValue}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    out.push({ key: normalizedKey, value: normalizedValue });
  };
  const visit = (value: unknown, prefix?: string): void => {
    if (out.length >= 12 || value === undefined || value === null) return;
    if (Array.isArray(value)) {
      for (const entry of value) {
        if (out.length >= 12) break;
        const item = asUnknownRecord(entry);
        if (item) {
          const key = readStringField(item, ['key', 'name', 'label']);
          const rawValue = item.value ?? item.val ?? item.content;
          if (key && rawValue !== undefined && rawValue !== null) {
            push(prefix ? `${prefix}.${key}` : key, rawValue);
            continue;
          }
        }
        visit(entry, prefix);
      }
      return;
    }
    const record = asUnknownRecord(value);
    if (!record) {
      if (prefix) push(prefix, value);
      return;
    }
    const nestedCollection =
      record['properties'] ??
      record['attributes'] ??
      record['props'] ??
      record['metadata'];
    if (nestedCollection !== undefined) {
      visit(nestedCollection, prefix);
    }
    const entries = Object.entries(record).sort(([a], [b]) => a.localeCompare(b));
    for (const [key, entryValue] of entries) {
      if (['properties', 'attributes', 'props', 'metadata'].includes(key)) continue;
      const nextKey = prefix ? `${prefix}.${key}` : key;
      if (entryValue !== null && typeof entryValue === 'object') {
        const nestedRecord = asUnknownRecord(entryValue);
        if (nestedRecord) {
          for (const [nestedKey, nestedValue] of Object.entries(nestedRecord).sort(([a], [b]) => a.localeCompare(b))) {
            push(`${nextKey}.${nestedKey}`, nestedValue);
            if (out.length >= 12) break;
          }
        } else {
          push(nextKey, entryValue);
        }
      } else {
        push(nextKey, entryValue);
      }
      if (out.length >= 12) break;
    }
  };
  visit(source);
  return out;
}

function stringifyPropertyValue(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return value
      .map((entry) => stringifyPropertyValue(entry))
      .filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
      .join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function collectModelNodeTargets(
  node: import('@/components/three-d/ThreeDModelToolCard').ModelTreeNode,
): Set<string> {
  const out = new Set<string>();
  const visit = (
    current: import('@/components/three-d/ThreeDModelToolCard').ModelTreeNode,
  ) => {
    for (const token of [
      current.featureId,
      current.id,
      current.name,
    ]) {
      const normalized = normalizeNodeToken(token);
      if (normalized) out.add(normalized);
    }
    for (const child of current.children ?? []) visit(child);
  };
  visit(node);
  return out;
}

function normalizeNodeToken(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const base = value.replace(/\s*\(\d+\)\s*$/, '').trim().toLowerCase();
  return base.length > 0 ? base : null;
}
